// OneAmerica AssetCare Application Form
// Form ICC18 I-31139

import { FundingPolicyType } from '@shared';

export const sharedOneAmericaQuestions: Record<string, PolicyFormQuestion> = {
  /* Section 1 - First Proposed Insured */
  firstProposedInsuredFirstName: {
    id: 'first-proposed-insured-first-name',
    questionRef: 'firstProposedInsuredFirstName',
    title: 'First Name',
    type: 'text',
    required: true,
  },
  firstProposedInsuredMiddleInitial: {
    id: 'first-proposed-insured-middle-initial',
    questionRef: 'firstProposedInsuredMiddleInitial',
    title: 'Middle Initial',
    type: 'text',
    required: false,
  },
  firstProposedInsuredLastName: {
    id: 'first-proposed-insured-last-name',
    questionRef: 'firstProposedInsuredLastName',
    title: 'Last Name',
    type: 'text',
    required: true,
  },
  firstProposedInsuredStreetAddress: {
    id: 'first-proposed-insured-street-address',
    questionRef: 'firstProposedInsuredStreetAddress',
    title: 'Street Address',
    type: 'text',
    required: true,
    validationType: 'streetAddress',
  },
  firstProposedInsuredCityStateZip: {
    id: 'first-proposed-insured-city-state-zip',
    questionRef: 'firstProposedInsuredCityStateZip',
    title: 'City, State, Zip',
    placeholder: 'Los Angeles, CA, 90001',
    type: 'text',
    required: true,
    validationType: 'cityStateZipcode',
  },
  firstProposedInsuredBirthDate: {
    id: 'first-proposed-insured-birth-date',
    questionRef: 'firstProposedInsuredBirthDate',
    title: 'Birth Date',
    type: 'date',
    required: true,
  },
  firstProposedInsuredSsn: {
    id: 'first-proposed-insured-ssn',
    questionRef: 'firstProposedInsuredSsn',
    title: 'Social Security Number',
    type: 'text',
    required: true,
    validationType: 'ssn',
  },
  firstProposedInsuredPlaceOfBirth: {
    id: 'first-proposed-insured-place-of-birth',
    questionRef: 'firstProposedInsuredPlaceOfBirth',
    title: 'Place of Birth (State/Country)',
    type: 'text',
    required: true,
  },
  firstProposedInsuredGender: {
    id: 'first-proposed-insured-gender',
    questionRef: 'firstProposedInsuredGender',
    title: 'Gender',
    type: 'radio',
    required: true,
    options: [
      { id: 'male', value: 'Male' },
      { id: 'female', value: 'Female' },
    ],
  },
  firstProposedInsuredMarriedStatus: {
    id: 'first-proposed-insured-married-status',
    questionRef: 'firstProposedInsuredMarriedStatus',
    title:
      'Is the Proposed Insured married or in a legally recognized civil union or domestic partnership?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  firstProposedInsuredUsCitizen: {
    id: 'first-proposed-insured-us-citizen',
    questionRef: 'firstProposedInsuredUsCitizen',
    title: 'U.S. Citizen?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  firstProposedNotUsCitizenDetails: {
    id: 'first-proposed-not-us-citizen-details',
    questionRef: 'firstProposedNotUsCitizenDetails',
    title:
      'Please provide details about your citizenship as well as your visa or permanent resident status. You may include other relevant details as you see fit. Please also attach a copy of your visa or permanent resident card with your application.',
    type: 'textarea',
    required: true,
    conditional: {
      visible: {
        field: 'firstProposedInsuredUsCitizen',
        operator: 'equals',
        value: 'no',
      },
    },
  },
  firstProposedInsuredPhoneNumber: {
    id: 'first-proposed-insured-phone-number',
    questionRef: 'firstProposedInsuredPhoneNumber',
    title: 'Phone Number',
    type: 'text',
    required: true,
    validationType: 'phone',
  },
  firstProposedInsuredEmailAddress: {
    id: 'first-proposed-insured-email-address',
    questionRef: 'firstProposedInsuredEmailAddress',
    title: 'E-mail Address',
    type: 'text',
    required: true,
    validationType: 'email',
  },
  firstProposedInsuredOccupation: {
    id: 'first-proposed-insured-occupation',
    questionRef: 'firstProposedInsuredOccupation',
    title: 'Occupation',
    type: 'text',
    required: true,
  },
  firstProposedInsuredEmployerName: {
    id: 'first-proposed-insured-employer-name',
    questionRef: 'firstProposedInsuredEmployerName',
    title: 'Employer Name',
    type: 'text',
    required: true,
  },

  /* Section 2 - Second Proposed Insured */
  secondProposedInsuredFirstName: {
    id: 'second-proposed-insured-first-name',
    questionRef: 'secondProposedInsuredFirstName',
    title: 'First Name',
    type: 'text',
    required: true,
  },
  secondProposedInsuredMiddleInitial: {
    id: 'second-proposed-insured-middle-initial',
    questionRef: 'secondProposedInsuredMiddleInitial',
    title: 'Middle Initial',
    type: 'text',
    required: false,
  },
  secondProposedInsuredLastName: {
    id: 'second-proposed-insured-last-name',
    questionRef: 'secondProposedInsuredLastName',
    title: 'Last Name',
    type: 'text',
    required: true,
  },
  secondProposedInsuredRelationship: {
    id: 'second-proposed-insured-relationship',
    questionRef: 'secondProposedInsuredRelationship',
    title: 'Relationship to Proposed Insured',
    type: 'text',
    required: false,
  },
  secondProposedInsuredStreetAddress: {
    id: 'second-proposed-insured-street-address',
    questionRef: 'secondProposedInsuredStreetAddress',
    title: 'Street Address',
    type: 'text',
    required: true,
    validationType: 'streetAddress',
  },
  secondProposedInsuredCityStateZip: {
    id: 'second-proposed-insured-city-state-zip',
    questionRef: 'secondProposedInsuredCityStateZip',
    title: 'City, State, Zip',
    type: 'text',
    required: true,
    validationType: 'cityStateZipcode',
  },
  secondProposedInsuredBirthDate: {
    id: 'second-proposed-insured-birth-date',
    questionRef: 'secondProposedInsuredBirthDate',
    title: 'Birth Date',
    type: 'date',
    required: true,
  },
  secondProposedInsuredSsn: {
    id: 'second-proposed-insured-ssn',
    questionRef: 'secondProposedInsuredSsn',
    title: 'Social Security Number',
    type: 'text',
    required: true,
    validationType: 'ssn',
  },
  secondProposedInsuredPlaceOfBirth: {
    id: 'second-proposed-insured-place-of-birth',
    questionRef: 'secondProposedInsuredPlaceOfBirth',
    title: 'Place of Birth (State/Country)',
    type: 'text',
    required: true,
  },
  secondProposedInsuredGender: {
    id: 'second-proposed-insured-gender',
    questionRef: 'secondProposedInsuredGender',
    title: 'Gender',
    type: 'radio',
    required: true,
    options: [
      { id: 'male', value: 'Male' },
      { id: 'female', value: 'Female' },
    ],
  },
  secondProposedInsuredMarriedStatus: {
    id: 'second-proposed-insured-married-status',
    questionRef: 'secondProposedInsuredMarriedStatus',
    title:
      'Is the Second Proposed Insured married or in a legally recognized civil union or domestic partnership?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  secondProposedInsuredUsCitizen: {
    id: 'second-proposed-insured-us-citizen',
    questionRef: 'secondProposedInsuredUsCitizen',
    title: 'U.S. Citizen?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  secondProposedNotUsCitizenDetails: {
    id: 'second-proposed-not-us-citizen-details',
    questionRef: 'secondProposedNotUsCitizenDetails',
    title:
      'Please provide details about your citizenship as well as your visa or permanent resident status. You may include other relevant details as you see fit. Please also attach a copy of your visa or permanent resident card with your application.',
    type: 'textarea',
    required: true,
    conditional: {
      visible: {
        field: 'secondProposedInsuredUsCitizen',
        operator: 'equals',
        value: 'no',
      },
    },
  },
  secondProposedInsuredPhoneNumber: {
    id: 'second-proposed-insured-phone-number',
    questionRef: 'secondProposedInsuredPhoneNumber',
    title: 'Phone Number',
    type: 'text',
    required: true,
    validationType: 'phone',
  },
  secondProposedInsuredEmailAddress: {
    id: 'second-proposed-insured-email-address',
    questionRef: 'secondProposedInsuredEmailAddress',
    title: 'E-mail Address',
    type: 'text',
    required: true,
    validationType: 'email',
  },
  secondProposedInsuredOccupation: {
    id: 'second-proposed-insured-occupation',
    questionRef: 'secondProposedInsuredOccupation',
    title: 'Occupation',
    type: 'text',
    required: true,
  },
  secondProposedInsuredEmployerName: {
    id: 'second-proposed-insured-employer-name',
    questionRef: 'secondProposedInsuredEmployerName',
    title: 'Employer Name',
    type: 'text',
    required: true,
  },

  /* Section 3 - Owner */
  ownerIndicateMultipleOwners: {
    id: 'owner-indicate-multiple-owners',
    questionRef: 'ownerIndicateMultipleOwners',
    title:
      'Please indicate if there are to be multiple owners. If more than one individual is named as Owner, they will own the policy as joint tenants with rights of survivorship unless otherwise stated.',
    type: 'multiselect',
    required: false,
    options: [{ id: 'yes', value: 'Yes' }],
  },
  ownerOtherFirstName: {
    id: 'owner-other-first-name',
    questionRef: 'ownerOtherFirstName',
    title: 'First Name',
    type: 'text',
    required: true,
  },
  ownerOtherMiddleInitial: {
    id: 'owner-other-middle-initial',
    questionRef: 'ownerOtherMiddleInitial',
    title: 'Middle Initial',
    type: 'text',
    required: false,
  },
  ownerOtherLastName: {
    id: 'owner-other-last-name',
    questionRef: 'ownerOtherLastName',
    title: 'Last Name',
    type: 'text',
    required: true,
  },
  ownerOtherFullName: {
    id: 'owner-other-full-name',
    questionRef: 'ownerOtherFullName',
    title: 'Full Name',
    type: 'text',
    hidden: true,
    disabled: true,
    composite: {
      questionRefs: ['ownerOtherFirstName', 'ownerOtherMiddleInitial', 'ownerOtherLastName'],
      delimiter: ' ',
    },
  },
  ownerRelationshipToInsured: {
    id: 'owner-relationship-to-insured',
    questionRef: 'ownerRelationshipToInsured',
    title: 'Relationship to Proposed Insured',
    type: 'text',
    required: false,
  },
  ownerGender: {
    id: 'owner-gender',
    questionRef: 'ownerGender',
    title: 'Gender',
    type: 'radio',
    required: false,
    options: [
      { id: 'male', value: 'Male' },
      { id: 'female', value: 'Female' },
    ],
  },
  ownerUsCitizen: {
    id: 'owner-us-citizen',
    questionRef: 'ownerUsCitizen',
    title: 'U.S. Citizen?',
    type: 'radio',
    required: false,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  ownerOtherEmail: {
    id: 'owner-other-email',
    questionRef: 'ownerOtherEmail',
    title: 'E-mail Address',
    type: 'text',
    required: false,
    validationType: 'email',
  },
  ownerCorpTrustName: {
    id: 'owner-corp-trust-name',
    questionRef: 'ownerCorpTrustName',
    title: 'Name of Corporation, Trust or Qualified Retirement Plan',
    type: 'text',
    required: true,
  },
  ownerCorpOfficerTrustee: {
    id: 'owner-corp-officer-trustee',
    questionRef: 'ownerCorpOfficerTrustee',
    title: 'Full Name of Corporate Officer or Trustee, Title and State of Incorporation',
    type: 'text',
    required: true,
  },
  ownerEntityType: {
    id: 'owner-entity-type',
    questionRef: 'ownerEntityType',
    title: 'Entity Type',
    type: 'radio',
    required: true,
    options: [
      { id: 'corporation', value: 'Corporation' },
      { id: 'trust', value: 'Trust' },
      { id: 'qualified-retirement-plan', value: 'Qualified Retirement Plan' },
      { id: 'other', value: 'Other' },
    ],
  },
  ownerOtherEntitySpecify: {
    id: 'owner-other-entity-specify',
    questionRef: 'ownerOtherEntitySpecify',
    title: 'Other Entity Type',
    type: 'text',
    required: true,
    conditional: {
      visible: {
        field: 'ownerEntityType',
        value: 'other',
        operator: 'contains',
      },
    },
  },
  ownerSsnOrTaxId: {
    id: 'owner-ssn-or-tax-id',
    questionRef: 'ownerSsnOrTaxId',
    title: 'Social Security Number or Tax ID Number',
    type: 'text',
    required: true,
    validationType: 'ssn',
  },
  ownerStreetAddress: {
    id: 'owner-street-address',
    questionRef: 'ownerStreetAddress',
    title: 'Street Address',
    type: 'text',
    required: true,
    validationType: 'streetAddress',
  },
  ownerCityStateZip: {
    id: 'owner-city-state-zip',
    questionRef: 'ownerCityStateZip',
    title: 'City, State, Zip',
    type: 'text',
    required: true,
    validationType: 'cityStateZipcode',
  },

  /* Section 4 - Joint Owner */
  jointOwnerFirstName: {
    id: 'joint-owner-first-name',
    questionRef: 'jointOwnerFirstName',
    title: 'First Name',
    type: 'text',
    required: true,
    placeholder: 'First Name',
    composite: {
      questionRefs: ['secondProposedInsuredFirstName', 'ownerJointOtherFirstName'],
      delimiter: ' ',
      allowAppendText: true,
      conditional: {
        operator: 'or',
        conditions: [
          {
            field: 'policyOwners',
            value: 'owner_joint',
            operator: 'equals',
          },
          {
            field: 'policyOwners',
            value: 'owner_joint_other',
            operator: 'equals',
          },
        ],
      },
    },
  },
  jointOwnerMiddleInitial: {
    id: 'joint-owner-middle-initial',
    questionRef: 'jointOwnerMiddleInitial',
    title: 'Middle Initial',
    type: 'text',
    required: false,
    composite: {
      questionRefs: ['secondProposedInsuredMiddleInitial'],
      delimiter: ' ',
      allowAppendText: true,
      conditional: {
        field: 'policyOwners',
        value: 'owner_joint',
        operator: 'equals',
      },
    },
  },
  jointOwnerLastName: {
    id: 'joint-owner-last-name',
    questionRef: 'jointOwnerLastName',
    title: 'Last Name',
    type: 'text',
    required: true,
    placeholder: 'Last Name',
    composite: {
      questionRefs: ['secondProposedInsuredLastName', 'ownerJointOtherLastName'],
      delimiter: ' ',
      allowAppendText: true,
      conditional: {
        operator: 'or',
        conditions: [
          {
            field: 'policyOwners',
            value: 'owner_joint',
            operator: 'equals',
          },
          {
            field: 'policyOwners',
            value: 'owner_joint_other',
            operator: 'equals',
          },
        ],
      },
    },
  },
  jointOwnerFullName: {
    id: 'joint-owner-full-name',
    questionRef: 'jointOwnerFullName',
    title: 'Full Name',
    type: 'text',
    hidden: true,
    disabled: true,
    composite: {
      questionRefs: ['jointOwnerFirstName', 'jointOwnerMiddleInitial', 'jointOwnerLastName'],
      delimiter: ' ',
    },
  },
  jointOwnerStreetAddress: {
    id: 'joint-owner-street-address',
    questionRef: 'jointOwnerStreetAddress',
    title: 'Street Address',
    type: 'text',
    required: true,
    validationType: 'streetAddress',
    composite: {
      questionRefs: ['secondProposedInsuredStreetAddress'],
      delimiter: ' ',
      allowAppendText: true,
      conditional: {
        field: 'policyOwners',
        operator: 'equals',
        value: 'owner_joint',
      },
    },
  },
  jointOwnerCityStateZip: {
    id: 'joint-owner-city-state-zip',
    questionRef: 'jointOwnerCityStateZip',
    title: 'City, State, Zip',
    type: 'text',
    required: true,
    validationType: 'cityStateZipcode',
    composite: {
      questionRefs: ['secondProposedInsuredCityStateZip'],
      delimiter: ' ',
      allowAppendText: true,
      conditional: {
        field: 'policyOwners',
        operator: 'equals',
        value: 'owner_joint',
      },
    },
  },
  jointOwnerBirthDate: {
    id: 'joint-owner-birth-date',
    questionRef: 'jointOwnerBirthDate',
    title: 'Birth Date',
    type: 'date',
    required: true,
    composite: {
      questionRefs: ['secondProposedInsuredBirthDate'],
      delimiter: ' ',
      allowAppendText: true,
      conditional: {
        field: 'policyOwners',
        operator: 'equals',
        value: 'owner_joint',
      },
    },
  },
  jointOwnerSsn: {
    id: 'joint-owner-ssn',
    questionRef: 'jointOwnerSsn',
    title: 'Social Security Number',
    type: 'text',
    required: true,
    validationType: 'ssn',
    composite: {
      questionRefs: ['secondProposedInsuredSsn'],
      delimiter: ' ',
      allowAppendText: true,
      conditional: {
        field: 'policyOwners',
        operator: 'equals',
        value: 'owner_joint',
      },
    },
  },
  jointOwnerPlaceOfBirth: {
    id: 'joint-owner-place-of-birth',
    questionRef: 'jointOwnerPlaceOfBirth',
    title: 'Place of Birth (State/Country)',
    type: 'text',
    required: true,
    composite: {
      questionRefs: ['secondProposedInsuredPlaceOfBirth'],
      delimiter: ' ',
      allowAppendText: true,
      conditional: {
        field: 'policyOwners',
        operator: 'equals',
        value: 'owner_joint',
      },
    },
  },
  jointOwnerGender: {
    id: 'joint-owner-gender',
    questionRef: 'jointOwnerGender',
    title: 'Gender',
    type: 'radio',
    required: true,
    options: [
      { id: 'male', value: 'Male' },
      { id: 'female', value: 'Female' },
    ],
    composite: {
      questionRefs: ['secondProposedInsuredGender'],
      delimiter: ' ',
      allowAppendText: true,
      conditional: {
        field: 'policyOwners',
        operator: 'equals',
        value: 'owner_joint',
      },
    },
  },
  jointOwnerRelationshipToInsured: {
    id: 'joint-owner-relationship-to-insured',
    questionRef: 'jointOwnerRelationshipToInsured',
    title: 'Relationship to Proposed Insured',
    type: 'text',
    required: false,
    composite: {
      questionRefs: ['secondProposedInsuredRelationship'],
      delimiter: ' ',
      allowAppendText: true,
      conditional: {
        field: 'policyOwners',
        operator: 'equals',
        value: 'owner_joint',
      },
    },
  },
  jointOwnerPhoneNumber: {
    id: 'joint-owner-phone-number',
    questionRef: 'jointOwnerPhoneNumber',
    title: 'Phone Number',
    type: 'text',
    required: true,
    validationType: 'phone',
    composite: {
      questionRefs: ['secondProposedInsuredPhoneNumber'],
      delimiter: ' ',
      allowAppendText: true,
      conditional: {
        field: 'policyOwners',
        operator: 'equals',
        value: 'owner_joint',
      },
    },
  },
  jointOwnerEmailAddress: {
    id: 'joint-owner-email-address',
    questionRef: 'jointOwnerEmailAddress',
    title: 'E-mail Address',
    type: 'text',
    placeholder: 'Email Address',
    required: true,
    validationType: 'email',
    composite: {
      questionRefs: ['secondProposedInsuredEmailAddress', 'ownerJointOtherEmail'],
      delimiter: ' ',
      allowAppendText: true,
      conditional: {
        operator: 'or',
        conditions: [
          {
            field: 'policyOwners',
            value: 'owner_joint',
            operator: 'equals',
          },
          {
            field: 'policyOwners',
            value: 'owner_joint_other',
            operator: 'equals',
          },
        ],
      },
    },
  },

  /* Section 5 - Beneficiary */
  beneficiaryDistributionMethod: {
    id: 'beneficiary-distribution-method',
    questionRef: 'beneficiaryDistributionMethod',
    title:
      "If more than one primary beneficiary is named but not all of the named primary beneficiaries survive the insured, select the manner in which the deceased beneficiary's portion should be paid:",
    type: 'multiselect',
    required: false,
    options: [
      { id: 'owner', value: 'Owner (if other than Proposed Insured)' },
      { id: 'spouse', value: "Deceased Beneficiary's Spouse" },
      { id: 'children', value: "Deceased Beneficiary's Child(ren)" },
      { id: 'heirs', value: "Deceased Beneficiary's Surviving Heirs" },
    ],
    conditional: {
      showConditionalText: {
        field: 'fundingPolicyType',
        operator: 'equals',
        value: FundingPolicyType.jointAssetCare,
      },
    },
    helperText:
      'If no selection above is made, the insurance proceeds shall be divided equally among all persons who are named as primary beneficiary and who survive the insured.',
    conditionalHelperText:
      'If no selection above is made, the insurance proceeds shall be divided equally among all persons who are named as primary beneficiary and who survive the insured.\n\n**If this is a joint application, any proposed insured cannot be named a beneficiary.**',
  },
  primaryBeneficiary: {
    id: 'primary-beneficiary',
    questionRef: 'primaryBeneficiary',
    title: 'Primary Beneficiary',
    type: 'table',
    required: false,
    minRows: 0,
    maxRows: 2,
    columns: [
      {
        id: 'primary-beneficiary-full-name',
        title: 'Full Name or Name of Corporation /Trust',
        type: 'text',
        required: false,
      },
      {
        id: 'primary-beneficiary-relationship',
        title: 'Relationship to Proposed Insured',
        type: 'text',
        required: false,
      },
      {
        id: 'primary-beneficiary-gender',
        title: 'Gender',
        type: 'radio',
        required: false,
        options: [
          { id: 'male', value: 'Male' },
          { id: 'female', value: 'Female' },
        ],
      },
      {
        id: 'primary-beneficiary-dob-or-trust-date',
        title: 'DOB or Date of Trust',
        type: 'date',
        required: false,
      },
      {
        id: 'primary-beneficiary-email',
        title: 'E-mail Address',
        type: 'text',
        required: false,
      },
      {
        id: 'primary-beneficiary-percent-of-proceeds',
        title: '% of Proceeds',
        type: 'number',
        required: false,
      },
      {
        id: 'primary-beneficiary-ssn-or-tax-id',
        title: 'Social Security or Tax Identification Number',
        type: 'text',
        required: false,
      },
      {
        id: 'primary-beneficiary-address',
        title: 'Address',
        type: 'text',
        required: false,
      },
    ],
  },
  primaryBeneficiaryCorporateOfficer: {
    id: 'primary-beneficiary-corporate-officer',
    questionRef: 'primaryBeneficiaryCorporateOfficer',
    title: 'Full Name of Corporate Officer or Trustee and Title',
    type: 'text',
    required: false,
  },
  primaryBeneficiaryStateOfIncorporation: {
    id: 'primary-beneficiary-state-of-incorporation',
    questionRef: 'primaryBeneficiaryStateOfIncorporation',
    title: 'State of Incorporation',
    type: 'text',
    required: false,
  },
  secondaryBeneficiary: {
    id: 'secondary-beneficiary',
    questionRef: 'secondaryBeneficiary',
    title: 'Secondary Beneficiary',
    type: 'table',
    required: false,
    minRows: 0,
    maxRows: 2,
    columns: [
      {
        id: 'secondary-beneficiary-full-name',
        title: 'Full Name or Name of Corporation /Trust',
        type: 'text',
        required: false,
      },
      {
        id: 'secondary-beneficiary-relationship',
        title: 'Relationship to Proposed Insured',
        type: 'text',
        required: false,
      },
      {
        id: 'secondary-beneficiary-gender',
        title: 'Gender',
        type: 'radio',
        required: false,
        options: [
          { id: 'male', value: 'Male' },
          { id: 'female', value: 'Female' },
        ],
      },
      {
        id: 'secondary-beneficiary-dob-or-trust-date',
        title: 'DOB or Date of Trust',
        type: 'date',
        required: false,
      },
      {
        id: 'secondary-beneficiary-email',
        title: 'E-mail Address',
        type: 'text',
        required: false,
      },
      {
        id: 'secondary-beneficiary-percent-of-proceeds',
        title: '% of Proceeds',
        type: 'number',
        required: false,
      },
      {
        id: 'secondary-beneficiary-ssn-or-tax-id',
        title: 'Social Security or Tax Identification Number',
        type: 'text',
        required: false,
      },
      {
        id: 'secondary-beneficiary-address',
        title: 'Address',
        type: 'text',
        required: false,
      },
    ],
  },
  secondaryBeneficiaryCorporateOfficer: {
    id: 'secondary-beneficiary-corporate-officer',
    questionRef: 'secondaryBeneficiaryCorporateOfficer',
    title: 'Full Name of Corporate Officer or Trustee and Title',
    type: 'text',
    required: false,
  },
  secondaryBeneficiaryStateOfIncorporation: {
    id: 'secondary-beneficiary-state-of-incorporation',
    questionRef: 'secondaryBeneficiaryStateOfIncorporation',
    title: 'State of Incorporation',
    type: 'text',
    required: false,
  },

  /* Section 6 - Policy Selections */
  policyFaceAmount: {
    id: 'policy-face-amount',
    questionRef: 'policyFaceAmount',
    title: 'Face Amount',
    type: 'text',
    required: true,
    placeholder: '$',
    validationType: 'currency',
  },
  policyProductFundingSelection: {
    id: 'policy-product-funding-selection',
    questionRef: 'policyProductFundingSelection',
    title: 'Product/Funding Selection',
    type: 'select',
    required: true,
    options: [
      {
        id: 'single-premium-no-return',
        value: 'Single Premium, no Return of Premium (if selected, proceed to #3)',
      },
      {
        id: 'single-premium-with-return',
        value:
          'Single Premium with Return of Premium* (if selected, choose COB, then proceed to #5)',
      },
      {
        id: 'recurring-premium',
        value: 'Recurring Premium Whole Life (if selected, proceed to #3)',
      },
      {
        id: 'annuity-funding',
        value:
          'Annuity Funding Whole Life (application ICC18 I-31157 must also be completed) (if selected, proceed to #3)',
      },
    ],
  },
  policySingleReturnCob: {
    id: 'policy-single-return-cob',
    questionRef: 'policySingleReturnCob',
    title: 'COB Selection',
    type: 'radio',
    required: false,
    options: [
      { id: '4-years', value: '4 Years' },
      { id: '2-years', value: '2 Years' },
      { id: 'none', value: 'None' },
    ],
    conditional: {
      visible: {
        field: 'policyProductFundingSelection',
        value: 'single-premium-no-return',
        operator: 'equals',
      },
      required: {
        field: 'policyProductFundingSelection',
        value: 'single-premium-no-return',
        operator: 'equals',
      },
    },
  },
  policyRecurringPayPeriod: {
    id: 'policy-recurring-pay-period',
    questionRef: 'policyRecurringPayPeriod',
    title: 'Pay Period',
    type: 'radio',
    required: false,
    options: [
      { id: '5-pay', value: '5-Pay' },
      { id: '10-pay', value: '10-Pay' },
      { id: '20-pay', value: '20-Pay' },
      { id: 'pay-to-95', value: 'Pay to 95' },
    ],
    conditional: {
      visible: {
        field: 'policyProductFundingSelection',
        value: 'recurring-premium',
        operator: 'equals',
      },
      required: {
        field: 'policyProductFundingSelection',
        value: 'recurring-premium',
        operator: 'equals',
      },
    },
  },
  policyRecurringBillingMode: {
    id: 'policy-recurring-billing-mode',
    questionRef: 'policyRecurringBillingMode',
    title: 'Billing Mode',
    type: 'radio',
    required: false,
    options: [
      { id: 'annual', value: 'Annual' },
      { id: 'semi-annual', value: 'Semi-Annual' },
      { id: 'quarterly', value: 'Quarterly' },
      { id: 'monthly-bank-draft', value: 'Monthly Bank Draft' },
    ],
    conditional: {
      visible: {
        field: 'policyProductFundingSelection',
        value: 'recurring-premium',
        operator: 'equals',
      },
      required: {
        field: 'policyProductFundingSelection',
        value: 'recurring-premium',
        operator: 'equals',
      },
    },
  },
  policyMinBenefitDurationAob: {
    id: 'policy-min-benefit-duration-aob',
    questionRef: 'policyMinBenefitDurationAob',
    title: 'Minimum Benefit Duration for Long-Term Care',
    type: 'radio',
    required: true,
    options: [{ id: 'aob', value: 'AOB 2 Years' }],
  },
  policyMinBenefitDurationCob: {
    id: 'policy-min-benefit-duration-cob',
    questionRef: 'policyMinBenefitDurationCob',
    title: 'COB Selection (select one)',
    type: 'select',
    required: true,
    options: [
      { id: 'lifetime', value: 'Lifetime' },
      { id: '6-years', value: '6 Years' },
      { id: '4-years', value: '4 Years' },
      { id: '2-years', value: '2 Years' },
      { id: 'none', value: 'None' },
    ],
  },
  policyCobPaymentPeriod: {
    id: 'policy-cob-payment-period',
    questionRef: 'policyCobPaymentPeriod',
    title: 'COB Payment Period',
    type: 'select',
    required: false,
    options: [
      { id: 'single-pay', value: 'Single-Pay' },
      { id: '5-pay', value: '5-Pay' },
      { id: '10-pay', value: '10-Pay' },
      { id: '20-pay', value: '20-Pay' },
      { id: 'pay-to-95', value: 'Pay to 95' },
    ],
  },
  policyCobRecurringBilling: {
    id: 'policy-cob-recurring-billing',
    questionRef: 'policyCobRecurringBilling',
    title: 'Recurring Premium Billing Mode',
    type: 'select',
    required: false,
    options: [
      { id: 'annual', value: 'Annual' },
      { id: 'semi-annual', value: 'Semi-Annual' },
      { id: 'quarterly', value: 'Quarterly' },
      { id: 'monthly-bank-draft', value: 'Monthly Bank Draft' },
    ],
    conditional: {
      visible: {
        field: 'policyCobPaymentPeriod',
        value: 'single-pay',
        operator: 'notEquals',
      },
      required: {
        field: 'policyProductFundingSelection',
        value: 'single-pay',
        operator: 'notEquals',
      },
    },
  },
  policyInflationProtection: {
    id: 'policy-inflation-protection',
    questionRef: 'policyInflationProtection',
    title: 'Inflation Protection AOB/COB Options',
    type: 'select',
    required: false,
    options: [
      { id: '0-2', value: '0%, 2%' },
      { id: '0-3', value: '0%, 3%' },
      { id: '0-5', value: '0%, 5%' },
      { id: '2-2', value: '2%, 2%' },
      { id: '3-3', value: '3%, 3%' },
      { id: '5-5', value: '5%, 5%' },
      { id: '2-no-cob', value: '2%, no COB' },
      { id: '3-no-cob', value: '3%, no COB' },
      { id: '5-no-cob', value: '5%, no COB' },
      { id: 'none', value: 'None' },
    ],
  },
  policyInflationDuration: {
    id: 'policy-inflation-duration',
    questionRef: 'policyInflationDuration',
    title: 'Duration',
    type: 'radio',
    required: false,
    options: [
      { id: 'na', value: 'N/A (if None is selected)' },
      { id: '20-years', value: '20 Years' },
      { id: 'lifetime', value: 'Lifetime' },
    ],
    conditional: {
      visible: {
        field: 'policyInflationProtection',
        value: 'none',
        operator: 'notEquals',
      },
    },
  },
  policySinglePremiumDropIn: {
    id: 'policy-single-premium-drop-in',
    questionRef: 'policySinglePremiumDropIn',
    title:
      'Single Premium Drop-In Rider (only available if the product selected is "Recurring Premium Whole Life")',
    type: 'radio',
    required: false,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
    conditional: {
      visible: {
        field: 'policyProductFundingSelection',
        value: 'recurring-premium',
        operator: 'equals',
      },
    },
  },
  policyDropInPaymentMethod: {
    id: 'policy-drop-in-payment-method',
    questionRef: 'policyDropInPaymentMethod',
    title: 'Drop-In Payment Method',
    type: 'select',
    required: false,
    options: [
      {
        id: 'client-sends-direct',
        value: 'Client will send funds directly to The State Life Insurance Company.',
      },
      {
        id: 'send-after-underwriting',
        value: 'Send the funds after underwriting has been completed.',
      },
      { id: 'send-after-issue', value: 'Send the funds after the Policy has been issued.' },
      { id: 'company-requests', value: 'The State Life Insurance Company will request the funds' },
    ],
    conditional: {
      visible: {
        field: 'policySinglePremiumDropIn',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  policyTotalPremiumDropIn: {
    id: 'policy-total-premium-drop-in',
    questionRef: 'policyTotalPremiumDropIn',
    title: 'Total Premium Drop-In',
    type: 'text',
    required: false,
    placeholder: '$',
    validationType: 'currency',
    conditional: {
      visible: {
        field: 'policySinglePremiumDropIn',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  policyTotalPremiumSelected: {
    id: 'policy-total-premium-selected',
    questionRef: 'policyTotalPremiumSelected',
    title: 'Total Premium for All Selected',
    type: 'text',
    required: true,
    placeholder: '$',
    validationType: 'currency',
  },
  policyPaymentOptions: {
    id: 'policy-payment-options',
    questionRef: 'policyPaymentOptions',
    title: 'Premium Payment Options',
    type: 'multiselect',
    required: false,
    options: [
      { id: '1035-exchange', value: '1035 Exchange' },
      { id: 'ira-transfer', value: 'IRA Transfer' },
      { id: 'cd-transfer', value: 'CD Transfer' },
      { id: 'cash', value: 'Cash' },
    ],
  },
  policyMoneySubmitted: {
    id: 'policy-money-submitted',
    questionRef: 'policyMoneySubmitted',
    title: 'Is money submitted with this application?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },

  /* Section 7 - Annual Income of Proposed Insured */
  incomeEarned: {
    id: 'income-earned',
    questionRef: 'incomeEarned',
    title: 'Earned',
    type: 'text',
    required: true,
    placeholder: '$',
    validationType: 'currency',
  },
  incomeUnearned: {
    id: 'income-unearned',
    questionRef: 'incomeUnearned',
    title: 'Unearned',
    type: 'text',
    required: true,
    placeholder: '$',
    validationType: 'currency',
  },
  incomeNetWorth: {
    id: 'income-net-worth',
    questionRef: 'incomeNetWorth',
    title: 'Net Worth',
    type: 'text',
    required: true,
    placeholder: '$',
    validationType: 'currency',
  },

  /* Section 8 - Protection Against Unintentional Lapse */
  lapseNoticeDesignation: {
    id: 'lapse-notice-designation',
    questionRef: 'lapseNoticeDesignation',
    title:
      'I understand that I have the right to designate at least one person other than myself to receive notice of lapse or termination of this insurance policy and long-term care riders for non-payment of premium. I understand that notice to the other person will not be given until 30 days after a premium is due and unpaid.',
    type: 'radio',
    required: true,
    options: [
      { id: 'designate', value: 'I elect to designate a person to receive such notice.' },
      { id: 'not-designate', value: 'I elect not to designate a person to receive such notice.' },
    ],
  },
  lapseNoticeRecipientName: {
    id: 'lapse-notice-recipient-name',
    questionRef: 'lapseNoticeRecipientName',
    title: 'Name',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'lapseNoticeDesignation',
        value: 'designate',
        operator: 'equals',
      },
      required: {
        field: 'lapseNoticeDesignation',
        value: 'designate',
        operator: 'equals',
      },
    },
  },
  lapseNoticeRecipientAddress: {
    id: 'lapse-notice-recipient-address',
    questionRef: 'lapseNoticeRecipientAddress',
    title: 'Address',
    type: 'text',
    required: false,
    validationType: 'address',
    conditional: {
      visible: {
        field: 'lapseNoticeDesignation',
        value: 'designate',
        operator: 'equals',
      },
      required: {
        field: 'lapseNoticeDesignation',
        value: 'designate',
        operator: 'equals',
      },
    },
  },

  /* Section 9 - Information Regarding other Coverage */
  coverageHasOtherLtc: {
    id: 'coverage-has-other-ltc',
    questionRef: 'coverageHasOtherLtc',
    title:
      'Does any Proposed Insured have another long-term care insurance policy or certificate in force (including health care service contract or health maintenance organization contract)?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  coverageHadLtcLastYear: {
    id: 'coverage-had-ltc-last-year',
    questionRef: 'coverageHadLtcLastYear',
    title:
      'Has any Proposed Insured had another long-term care policy or certificate in force during the last twelve (12) months?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  coverageLtcCompanyName: {
    id: 'coverage-ltc-company-name',
    questionRef: 'coverageLtcCompanyName',
    title: 'If so, with which company?',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'coverageHadLtcLastYear',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  coverageLtcLapseDate: {
    id: 'coverage-ltc-lapse-date',
    questionRef: 'coverageLtcLapseDate',
    title: 'If that policy lapsed, when did it lapse?',
    type: 'date',
    required: false,
    conditional: {
      visible: {
        field: 'coverageHadLtcLastYear',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  coverageHasExistingInsurance: {
    id: 'coverage-has-existing-insurance',
    questionRef: 'coverageHasExistingInsurance',
    title:
      'Does any Proposed Insured have existing life insurance or annuity(ies) with this or any other company?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  coverageExistingInsuranceDetails: {
    id: 'coverage-existing-insurance-details',
    questionRef: 'coverageExistingInsuranceDetails',
    title: 'If yes to a, b or c above, list all insurance or annuities below:',
    type: 'table',
    required: false,
    minRows: 0,
    maxRows: 6,
    gridColumns: 3,
    columns: [
      {
        id: 'amount',
        title: 'Amount',
        type: 'number',
        required: false,
        placeholder: '$',
      },
      {
        id: 'issueYear',
        title: 'Issue Year',
        type: 'number',
        required: false,
      },
      {
        id: 'type',
        title: 'Type',
        type: 'text',
        required: false,
      },
      {
        id: 'companyPolicyInsured',
        title: 'Company / Policy Number / Insured',
        type: 'text',
        required: false,
      },
      {
        id: 'replacement',
        title: 'Replacement?',
        type: 'radio',
        required: false,
        options: [
          { id: 'yes', value: 'Yes' },
          { id: 'no', value: 'No' },
        ],
      },
      {
        id: 'exchange',
        title: 'Exchange?',
        type: 'radio',
        required: false,
        options: [
          { id: 'yes', value: 'Yes' },
          { id: 'no', value: 'No' },
        ],
      },
    ],
  },
  coverageEligibleMedicaid: {
    id: 'coverage-eligible-medicaid',
    questionRef: 'coverageEligibleMedicaid',
    title:
      'Is any Proposed Insured currently eligible for benefits under or covered by Medicaid (not Medicare)?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  coverageReplacingPolicy: {
    id: 'coverage-replacing-policy',
    questionRef: 'coverageReplacingPolicy',
    title:
      'Will this policy be replacing or changing any existing life insurance or annuity with this or any other company?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  coverageApplicationPending: {
    id: 'coverage-application-pending',
    questionRef: 'coverageApplicationPending',
    title:
      'Is an application for life, health insurance or annuity pending with this or any other company?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  coveragePendingCompany: {
    id: 'coverage-pending-company',
    questionRef: 'coveragePendingCompany',
    title: 'Company Name',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'coverageApplicationPending',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  coveragePendingAmount: {
    id: 'coverage-pending-amount',
    questionRef: 'coveragePendingAmount',
    title: 'Amount',
    type: 'text',
    required: false,
    placeholder: '$',
    validationType: 'currency',
    conditional: {
      visible: {
        field: 'coverageApplicationPending',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  coverageSoldLifeSettlement: {
    id: 'coverage-sold-life-settlement',
    questionRef: 'coverageSoldLifeSettlement',
    title:
      'Has any Proposed Insured sold a policy to a life settlement, viatical or other secondary market product provider, are you in the process of selling a policy, or are you planning a future sale?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  coverageSettlementCompany: {
    id: 'coverage-settlement-company',
    questionRef: 'coverageSettlementCompany',
    title: 'Company Name',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'coverageSoldLifeSettlement',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  coverageSettlementAmount: {
    id: 'coverage-settlement-amount',
    questionRef: 'coverageSettlementAmount',
    title: 'Amount',
    type: 'text',
    required: false,
    placeholder: '$',
    validationType: 'currency',
    conditional: {
      visible: {
        field: 'coverageSoldLifeSettlement',
        value: 'yes',
        operator: 'equals',
      },
    },
  },

  /* Section 10 - Special Request / Additional Information */
  specialAdditionalInfo: {
    id: 'special-additional-info',
    questionRef: 'specialAdditionalInfo',
    title: 'Additional Information',
    type: 'textarea',
    required: false,
    placeholder: 'Enter any additional information here',
    composite: {
      questionRefs: ['firstProposedNotUsCitizenDetails', 'secondProposedNotUsCitizenDetails'],
      delimiter: ', ',
      includeMetadata: false,
      allowAppendText: true,
    },
  },

  /* Acknowledgement Section */
  acknowledgementPremiumDeposit: {
    id: 'acknowledgement-premium-deposit',
    questionRef: 'acknowledgementPremiumDeposit',
    title: 'Acknowledgement Option',
    type: 'radio',
    required: true,
    format: 'vertical',
    options: [
      {
        id: 'no-deposit',
        value:
          'I have NOT made a premium deposit with this application nor have I received the Temporary Insurance Agreement.',
      },
      {
        id: 'with-deposit',
        value:
          'I have made a premium deposit in the amount of $ in connection with the application for life insurance. I have received and have read the Temporary Insurance Agreement (TIA). It has been explained to me, and I understand and agree to all of the conditions and limitations.',
      },
    ],
  },
  acknowledgementDepositAmount: {
    id: 'acknowledgement-deposit-amount',
    questionRef: 'acknowledgementDepositAmount',
    title: 'Premium Deposit Amount',
    type: 'text',
    required: false,
    placeholder: '$',
    validationType: 'currency',
    conditional: {
      visible: {
        field: 'acknowledgementPremiumDeposit',
        value: 'with-deposit',
        operator: 'equals',
      },
      required: {
        field: 'acknowledgementPremiumDeposit',
        value: 'with-deposit',
        operator: 'equals',
      },
    },
  },

  /* Substitute W-9 Certification */
  substituteW9BackupWithholding: {
    id: 'substitute-w9-backup-withholding',
    questionRef: 'substituteW9BackupWithholding',
    title: '',
    type: 'multiselect',
    required: false,
    options: [
      {
        id: 'notified-by-irs-subject-to-withholding',
        value:
          'Check this box if you have been notified by the IRS that you are currently subject to withholding because of under reporting interest or dividends on your tax return.',
      },
    ],
  },

  /* Representative's Statement */
  representativeReplacementKnowledge: {
    id: 'representative-replacement-knowledge',
    questionRef: 'representativeReplacementKnowledge',
    title:
      'Do you have any knowledge or reason to believe that replacement of existing insurance or annuity coverage may be involved?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  representativeWitnessedSignatures: {
    id: 'representative-witnessed-signatures',
    questionRef: 'representativeWitnessedSignatures',
    title: 'Did you witness the completion of signatures on this application?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  representativeIdentificationMethod: {
    id: 'representative-identification-method',
    questionRef: 'representativeIdentificationMethod',
    title: 'How did you identify the proposed insured?',
    type: 'radio',
    required: true,
    options: [
      { id: 'photo-id', value: 'Photo ID' },
      { id: 'related', value: 'Related' },
      { id: 'well-known', value: 'Well known to you' },
    ],
  },
  representativeRelationshipExplanation: {
    id: 'representative-relationship-explanation',
    questionRef: 'representativeRelationshipExplanation',
    title: 'If related or well-known to you, please explain:',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'representativeIdentificationMethod',
        value: ['related', 'well-known'],
        operator: 'in',
      },
    },
  },
  representativeFormerNames: {
    id: 'representative-former-names',
    questionRef: 'representativeFormerNames',
    title: 'List any former names of the proposed insured(s):',
    type: 'text',
    required: false,
  },
  representativeEvaluateWithOthers: {
    id: 'representative-evaluate-with-others',
    questionRef: 'representativeEvaluateWithOthers',
    title: 'Should this application be evaluated with any other applications?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  representativeOtherApplicantName: {
    id: 'representative-other-applicant-name',
    questionRef: 'representativeOtherApplicantName',
    title: 'Name',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'representativeEvaluateWithOthers',
        value: 'yes',
        operator: 'equals',
      },
      required: {
        field: 'representativeEvaluateWithOthers',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  representativeOtherApplicantRelationship: {
    id: 'representative-other-applicant-relationship',
    questionRef: 'representativeOtherApplicantRelationship',
    title: 'Relationship to Insured',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'representativeEvaluateWithOthers',
        value: 'yes',
        operator: 'equals',
      },
      required: {
        field: 'representativeEvaluateWithOthers',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  representativeContactName: {
    id: 'representative-contact-name',
    questionRef: 'representativeContactName',
    title:
      'If the Company has questions concerning this application, who should we call at your office? Name:',
    type: 'text',
    required: true,
  },
  representativeContactPhone: {
    id: 'representative-contact-phone',
    questionRef: 'representativeContactPhone',
    title: 'Phone Number',
    type: 'text',
    required: true,
    validationType: 'phone',
  },
  representativeContactEmail: {
    id: 'representative-contact-email',
    questionRef: 'representativeContactEmail',
    title: 'E-mail Address',
    type: 'text',
    required: true,
  },

  /* Asset Care Application Health Pre Questions */
  healthPreQuestion: {
    id: 'health-pre-question',
    questionRef: 'healthPreQuestion',
    title:
      'Do you elect to answer the medical questions in Part 2 with this form, or would you prefer to complete a tele-interview with LTCG instead covering the same information?',
    type: 'radio',
    format: 'vertical',
    required: true,
    options: [
      {
        id: 'answer-medical-questions',
        value: 'I would like to answer the medical questions in Part 2 with this form',
      },
      {
        id: 'complete-tele-interview',
        value:
          'I would like to complete the medical questions via a tele-interview with LTCG scheduled at my convenience',
      },
    ],
  },

  /* Asset Care Application Checklist */

  // Licensing Information
  agentsContractingAndTrainingConfirmation: {
    id: 'agents-contracting-and-training-confirmation',
    questionRef: 'agentsContractingAndTrainingConfirmation',
    title: '',
    type: 'multiselect',
    required: false,
    options: [{ id: 'yes', value: "Confirm all agent's contracting and training are completed." }],
  },

  // Underwriting Information
  presentAssetCareIneligibleImpairments: {
    id: 'present-asset-care-ineligible-impairments',
    questionRef: 'presentAssetCareIneligibleImpairments',
    title: '',
    type: 'multiselect',
    required: false,
    options: [
      { id: 'yes', value: 'Present the Asset Care Ineligible Impairments to applicant(s)' },
    ],
  },
  provideApplicationPreInterviewWorksheetI30110: {
    id: 'provide-application-pre-interview-worksheet-i-30110',
    questionRef: 'provideApplicationPreInterviewWorksheetI30110',
    title: '',
    type: 'multiselect',
    required: false,
    options: [
      {
        id: 'yes',
        value:
          'Provide applicant(s) the Pre-Interview Worksheet (I-30110). This form is only necessary when the agent elects to have the teleinterview completed by LTCG, it should not be submitted to the Home Office.',
      },
    ],
  },

  // Part A – Required Forms – Fax to OneAmerica 1-317-285-5235
  partARequiredForms: {
    id: 'part-a-required-forms',
    questionRef: 'partARequiredForms',
    title: 'Required Forms:',
    type: 'multiselect',
    format: 'vertical',
    required: true,
    options: [
      { id: 'ACA', value: 'Asset Care Application (I-31139 (ICC) or state variation)' },
      { id: 'MQP', value: 'Medical Questionnaire Part 2 (I-31140 (ICC) or state variation)' },
      { id: 'SQ', value: 'Sales Quote' },
      { id: 'PAN', value: 'Patriot Act Notice and Customer Identification Verification (7-17078)' },
      { id: 'LTCPW', value: 'Long-Term Care Personal Worksheet (I-32315 (ICC))' },
    ],
  },

  // Part B – Supplemental Forms – Fax to OneAmerica 1-317-285-5235

  partBSupplementalForms: {
    id: 'part-b-supplemental-forms',
    questionRef: 'partBSupplementalForms',
    title: 'Supplemental Forms:',
    type: 'multiselect',
    required: false,
    options: [
      {
        id: 'CSDDQ',
        value:
          'Consumer Suitability and Due Diligence Questionnaire (I-22733 or state variation), only complete when utilizing the annuity funding whole life option',
      },
      {
        id: 'AFA',
        value:
          'Application for Annuities (I-31157 (ICC) or state variation), complete when utilizing the annuity funding whole life option',
      },
      {
        id: 'LARN',
        value: 'Life/Annuity Replacement Notice (7-17258 or 7-17256 or state variation)',
      },
      { id: 'LTCRF', value: 'Long-Term Care Replacement Form (I-32317 (ICC) or state variation)' },
      {
        id: 'ROF',
        value:
          'Request of Funds via 1035 Exchange, IRA Transfer (I-18748 (Full) or (I-18743 (Partial))',
      },
      { id: 'ADD', value: "Any other additional forms required by the insured's state" },
    ],
  },

  // Part C – Applicant Forms – To be left with applicant(s)

  partCApplicantForms: {
    id: 'part-c-applicant-forms',
    questionRef: 'partCApplicantForms',
    title: 'Applicant Forms:',
    type: 'multiselect',
    format: 'vertical',
    required: false,
    options: [
      { id: 'PPN', value: 'Privacy Practices Notice (C-24925)' },
      {
        id: 'OOC',
        value: 'Outlines of Coverage (I-31346 (ICC) and I-31347 (ICC) or state variation)',
      },
      { id: 'NIP', value: 'Notice of Information Practices (I-19080 or state variation)' },
      {
        id: 'KBLTCI',
        value: 'Things You Should Know Before You Buy Long-Term Care Insurance (I-32316 (ICC))',
      },
      { id: 'ACAFWLDF', value: 'Asset Care Annuity Funding Whole Life Disclosure Form (I-32669)' },
      {
        id: 'MBG',
        value:
          "Memo: Buyer's Guide to Fixed Annuities (I-18655), Producer must read and give to applicant(s) at time of application for AssetCare Annuity Funding Whole Life in AZ, NH, UT, & WI",
      },
      {
        id: 'NAICAB',
        value:
          "NAIC Annuity Buyer's Guide for Deferred Annuities (ANB-LP), Producer must give to applicant(s) at time of application for Asset Care Annuity Funding Whole Life in AZ, NH, UT, & WI",
      },
      {
        id: 'NAICLIBG',
        value: "NAIC Life Insurance Buyer's Guide (I-18672), all Asset Care products",
      },
      {
        id: 'NAICSG',
        value:
          "NAIC A Shopper's Guide to LTC Insurance, Shopper's Guide LTC, all Asset Care products",
      },
    ],
  },

  /* Medical Questionnaire Part 2 - Section 1: Health Questions */
  medicalFirstProposedInsuredHeightFT: {
    id: 'medical-first-proposed-insured-height-ft',
    questionRef: 'medicalFirstProposedInsuredHeightFT',
    title: 'Proposed Insured Height (ft)',
    type: 'number',
    required: true,
    placeholder: 'ft.',
  },
  medicalFirstProposedInsuredHeightIN: {
    id: 'medical-first-proposed-insured-height-in',
    questionRef: 'medicalFirstProposedInsuredHeightIN',
    title: 'Proposed Insured Height (in)',
    type: 'number',
    required: true,
    placeholder: 'in.',
  },
  medicalFirstProposedInsuredWeight: {
    id: 'medical-first-proposed-insured-weight',
    questionRef: 'medicalFirstProposedInsuredWeight',
    title: 'Proposed Insured Weight',
    type: 'number',
    required: true,
    placeholder: 'lbs.',
    validationType: 'weight',
  },
  medicalFirstProposedInsuredWeightChange: {
    id: 'medical-first-proposed-insured-weight-change',
    questionRef: 'medicalFirstProposedInsuredWeightChange',
    title: 'Weight Change in Past Year',
    type: 'radio',
    required: false,
    options: [
      { id: 'gained', value: 'Gained' },
      { id: 'lost', value: 'Lost' },
    ],
  },
  medicalFirstProposedInsuredWeightChangeAmount: {
    id: 'medical-first-proposed-insured-weight-change-amount',
    questionRef: 'medicalFirstProposedInsuredWeightChangeAmount',
    title: 'lbs.',
    type: 'number',
    required: true,
    conditional: {
      visible: {
        field: 'medicalFirstProposedInsuredWeightChange',
        value: ['gained', 'lost'],
        operator: 'in',
      },
    },
  },
  medicalSecondProposedInsuredHeightFT: {
    id: 'medical-second-proposed-insured-height-ft',
    questionRef: 'medicalSecondProposedInsuredHeightFT',
    title: 'Second Proposed Insured Height (ft)',
    type: 'number',
    required: true,
    placeholder: 'ft.',
  },
  medicalSecondProposedInsuredHeightIN: {
    id: 'medical-second-proposed-insured-height-in',
    questionRef: 'medicalSecondProposedInsuredHeightIN',
    title: 'Second Proposed Insured Height (in)',
    type: 'number',
    required: true,
    placeholder: 'in.',
  },
  medicalSecondProposedInsuredWeight: {
    id: 'medical-second-proposed-insured-weight',
    questionRef: 'medicalSecondProposedInsuredWeight',
    title: 'Second Proposed Insured Weight',
    type: 'number',
    required: true,
    placeholder: 'lbs.',
    validationType: 'weight',
  },
  medicalSecondProposedInsuredWeightChange: {
    id: 'medical-second-proposed-insured-weight-change',
    questionRef: 'medicalSecondProposedInsuredWeightChange',
    title: 'Weight Change in Past Year',
    type: 'radio',
    required: false,
    options: [
      { id: 'gained', value: 'Gained' },
      { id: 'lost', value: 'Lost' },
    ],
  },
  medicalSecondProposedInsuredWeightChangeAmount: {
    id: 'medical-second-proposed-insured-weight-change-amount',
    questionRef: 'medicalSecondProposedInsuredWeightChangeAmount',
    title: 'lbs.',
    type: 'number',
    required: true,
    conditional: {
      visible: {
        field: 'medicalSecondProposedInsuredWeightChange',
        value: ['gained', 'lost'],
        operator: 'in',
      },
    },
  },

  /* Medical Questions A - During past 10 years */
  medicalHeartConditionFirstInsured: {
    id: 'medical-heart-condition-first-insured',
    questionRef: 'medicalHeartConditionFirstInsured',
    title:
      'Heart attack, high blood pressure, stroke, transient ischemic attack (TIA), congestive heart failure, irregular heartbeat, aneurysm, chest pain, carotid artery disease, cardiomyopathy, or any other disorder of the heart, blood vessels, or circulatory system?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalHeartConditionSecondInsured: {
    id: 'medical-heart-condition-second-insured',
    questionRef: 'medicalHeartConditionSecondInsured',
    title:
      'Heart attack, high blood pressure, stroke, transient ischemic attack (TIA), congestive heart failure, irregular heartbeat, aneurysm, chest pain, carotid artery disease, cardiomyopathy, or any other disorder of the heart, blood vessels, or circulatory system?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalCancerFirstInsured: {
    id: 'medical-cancer-first-insured',
    questionRef: 'medicalCancerFirstInsured',
    title: 'Cancer, tumor, lymph gland/node disorder, leukemia, or any other blood abnormalities?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalCancerSecondInsured: {
    id: 'medical-cancer-second-insured',
    questionRef: 'medicalCancerSecondInsured',
    title: 'Cancer, tumor, lymph gland/node disorder, leukemia, or any other blood abnormalities?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalDiabetesFirstInsured: {
    id: 'medical-diabetes-first-insured',
    questionRef: 'medicalDiabetesFirstInsured',
    title: 'Diabetes or other endocrine disorder; disorder of the kidney, bladder, or prostate?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalDiabetesSecondInsured: {
    id: 'medical-diabetes-second-insured',
    questionRef: 'medicalDiabetesSecondInsured',
    title: 'Diabetes or other endocrine disorder; disorder of the kidney, bladder, or prostate?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalLungDisorderFirstInsured: {
    id: 'medical-lung-disorder-first-insured',
    questionRef: 'medicalLungDisorderFirstInsured',
    title:
      'Lung or chronic respiratory disorder, chronic obstructive pulmonary disease (COPD), asthma, sleep apnea, emphysema, pneumonia, tuberculosis, or any other disorder of the respiratory system?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalLungDisorderSecondInsured: {
    id: 'medical-lung-disorder-second-insured',
    questionRef: 'medicalLungDisorderSecondInsured',
    title:
      'Lung or chronic respiratory disorder, chronic obstructive pulmonary disease (COPD), asthma, sleep apnea, emphysema, pneumonia, tuberculosis, or any other disorder of the respiratory system?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalIntestinalFirstInsured: {
    id: 'medical-intestinal-first-insured',
    questionRef: 'medicalIntestinalFirstInsured',
    title:
      'Intestinal bleeding, ulcer, hepatitis, inflammatory bowel disease, or any other disorder of the stomach, liver, intestine, gall bladder, or pancreas?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalIntestinalSecondInsured: {
    id: 'medical-intestinal-second-insured',
    questionRef: 'medicalIntestinalSecondInsured',
    title:
      'Intestinal bleeding, ulcer, hepatitis, inflammatory bowel disease, or any other disorder of the stomach, liver, intestine, gall bladder, or pancreas?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalBrainNervousFirstInsured: {
    id: 'medical-brain-nervous-first-insured',
    questionRef: 'medicalBrainNervousFirstInsured',
    title:
      "Brain, mental or nervous disorder, fainting, convulsions or epilepsy, paralysis, chronic fatigue, chronic pain, fibromyalgia, depression, anxiety, suicide attempt, dizziness or vertigo, frequently recurring headaches, Alzheimer's disease, dementia, memory loss, tremor, or any other disorder of the nervous system?",
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalBrainNervousSecondInsured: {
    id: 'medical-brain-nervous-second-insured',
    questionRef: 'medicalBrainNervousSecondInsured',
    title:
      "Brain, mental or nervous disorder, fainting, convulsions or epilepsy, paralysis, chronic fatigue, chronic pain, fibromyalgia, depression, anxiety, suicide attempt, dizziness or vertigo, frequently recurring headaches, Alzheimer's disease, dementia, memory loss, tremor, or any other disorder of the nervous system?",
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalArthritisFirstInsured: {
    id: 'medical-arthritis-first-insured',
    questionRef: 'medicalArthritisFirstInsured',
    title:
      "Rheumatoid or any other form of arthritis, loss of limb or deformity, disorder of bone, joint, muscle, back, spine or neck, osteoporosis, systemic lupus, scleroderma, Parkinson's disease, multiple sclerosis, ALS (amyotrophic lateral sclerosis), skin disorder, or any other disorder of the skeletal system?",
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalArthritisSecondInsured: {
    id: 'medical-arthritis-second-insured',
    questionRef: 'medicalArthritisSecondInsured',
    title:
      "Rheumatoid or any other form of arthritis, loss of limb or deformity, disorder of bone, joint, muscle, back, spine or neck, osteoporosis, systemic lupus, scleroderma, Parkinson's disease, multiple sclerosis, ALS (amyotrophic lateral sclerosis), skin disorder, or any other disorder of the skeletal system?",
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalEyesEarsFirstInsured: {
    id: 'medical-eyes-ears-first-insured',
    questionRef: 'medicalEyesEarsFirstInsured',
    title: 'Disease or disorder of the eyes, ears, nose, or throat?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalEyesEarsSecondInsured: {
    id: 'medical-eyes-ears-second-insured',
    questionRef: 'medicalEyesEarsSecondInsured',
    title: 'Disease or disorder of the eyes, ears, nose, or throat?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalSectionADetailsFirstInsured: {
    id: 'medical-section-a-details-first-insured',
    questionRef: 'medicalSectionADetailsFirstInsured',
    title: 'PROVIDE DETAILS TO YES ANSWERS ABOVE FOR "{clientFullName}".',
    type: 'textarea',
    required: false,
    placeholder: 'Enter details for any YES answers in section A',
    conditional: {
      visible: {
        operator: 'or',
        conditions: [
          {
            field: 'medicalHeartConditionFirstInsured',
            value: 'yes',
            operator: 'equals',
          },
          {
            field: 'medicalCancerFirstInsured',
            value: 'yes',
            operator: 'equals',
          },
          {
            field: 'medicalDiabetesFirstInsured',
            value: 'yes',
            operator: 'equals',
          },
          {
            field: 'medicalLungDisorderFirstInsured',
            value: 'yes',
            operator: 'equals',
          },
          {
            field: 'medicalIntestinalFirstInsured',
            value: 'yes',
            operator: 'equals',
          },
          {
            field: 'medicalBrainNervousFirstInsured',
            value: 'yes',
            operator: 'equals',
          },
          {
            field: 'medicalArthritisFirstInsured',
            value: 'yes',
            operator: 'equals',
          },
          {
            field: 'medicalEyesEarsFirstInsured',
            value: 'yes',
            operator: 'equals',
          },
          {
            field: 'medicalAidsFirstInsured',
            value: 'yes',
            operator: 'equals',
          },
          {
            field: 'medicalSectionADetailsFirstInsured',
            value: 'yes',
            operator: 'equals',
          },
        ],
      },
    },
  },

  medicalSectionADetailsSecondInsured: {
    id: 'medical-section-a-details-second-insured',
    questionRef: 'medicalSectionADetailsSecondInsured',
    title: 'PROVIDE DETAILS TO YES ANSWERS ABOVE FOR "{clientPartnerFullName}".',
    type: 'textarea',
    required: false,
    placeholder: 'Enter details for any YES answers in section A',
    conditional: {
      visible: {
        operator: 'or',
        conditions: [
          {
            field: 'medicalHeartConditionSecondInsured',
            value: 'yes',
            operator: 'equals',
          },
          {
            field: 'medicalCancerSecondInsured',
            value: 'yes',
            operator: 'equals',
          },
          {
            field: 'medicalDiabetesSecondInsured',
            value: 'yes',
            operator: 'equals',
          },
          {
            field: 'medicalLungDisorderSecondInsured',
            value: 'yes',
            operator: 'equals',
          },
          {
            field: 'medicalIntestinalSecondInsured',
            value: 'yes',
            operator: 'equals',
          },
          {
            field: 'medicalBrainNervousSecondInsured',
            value: 'yes',
            operator: 'equals',
          },
          {
            field: 'medicalArthritisSecondInsured',
            value: 'yes',
            operator: 'equals',
          },
          {
            field: 'medicalEyesEarsSecondInsured',
            value: 'yes',
            operator: 'equals',
          },
          {
            field: 'medicalAidsSecondInsured',
            value: 'yes',
            operator: 'equals',
          },
          {
            field: 'medicalSectionADetailsSecondInsured',
            value: 'yes',
            operator: 'equals',
          },
        ],
      },
    },
  },

  /* Medical Questions B - During past 5 years */
  medicalHospitalPatientFirstInsured: {
    id: 'medical-hospital-patient-first-insured',
    questionRef: 'medicalHospitalPatientFirstInsured',
    title: 'Been a patient at a hospital, an emergency room, clinic, or other medical facility?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalHospitalPatientSecondInsured: {
    id: 'medical-hospital-patient-second-insured',
    questionRef: 'medicalHospitalPatientSecondInsured',
    title: 'Been a patient at a hospital, an emergency room, clinic, or other medical facility?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalNursingHomeFirstInsured: {
    id: 'medical-nursing-home-first-insured',
    questionRef: 'medicalNursingHomeFirstInsured',
    title:
      'Been admitted to a nursing home or has a doctor discussed nursing home or assisted living facility admission?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalNursingHomeSecondInsured: {
    id: 'medical-nursing-home-second-insured',
    questionRef: 'medicalNursingHomeSecondInsured',
    title:
      'Been admitted to a nursing home or has a doctor discussed nursing home or assisted living facility admission?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalDailyAssistanceFirstInsured: {
    id: 'medical-daily-assistance-first-insured',
    questionRef: 'medicalDailyAssistanceFirstInsured',
    title:
      'Required assistance for a period longer than 2 weeks to perform the following daily activities: bathing, dressing, walking, eating, using toilet, getting up and down, taking medication, shopping, or cooking?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalDailyAssistanceSecondInsured: {
    id: 'medical-daily-assistance-second-insured',
    questionRef: 'medicalDailyAssistanceSecondInsured',
    title:
      'Required assistance for a period longer than 2 weeks to perform the following daily activities: bathing, dressing, walking, eating, using toilet, getting up and down, taking medication, shopping, or cooking?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalClaimBenefitsFirstInsured: {
    id: 'medical-claim-benefits-first-insured',
    questionRef: 'medicalClaimBenefitsFirstInsured',
    title:
      'Made a claim for or received benefits, compensation, or pension for any injury, sickness, disability, or impaired condition and/or been unable to work, attend school, or perform the normal activities of like age?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalClaimBenefitsSecondInsured: {
    id: 'medical-claim-benefits-second-insured',
    questionRef: 'medicalClaimBenefitsSecondInsured',
    title:
      'Made a claim for or received benefits, compensation, or pension for any injury, sickness, disability, or impaired condition and/or been unable to work, attend school, or perform the normal activities of like age?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalFallingInjuryFirstInsured: {
    id: 'medical-falling-injury-first-insured',
    questionRef: 'medicalFallingInjuryFirstInsured',
    title:
      'Been treated, examined, or advised by a member of the medical profession for an injury caused by falling?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalFallingInjurySecondInsured: {
    id: 'medical-falling-injury-second-insured',
    questionRef: 'medicalFallingInjurySecondInsured',
    title:
      'Been treated, examined, or advised by a member of the medical profession for an injury caused by falling?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },

  /* Medical Questions C - Current Status */
  medicalPhysicalTherapyFirstInsured: {
    id: 'medical-physical-therapy-first-insured',
    questionRef: 'medicalPhysicalTherapyFirstInsured',
    title:
      'Receiving physical therapy, occupation therapy, speech therapy, or respiratory therapy?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalPhysicalTherapySecondInsured: {
    id: 'medical-physical-therapy-second-insured',
    questionRef: 'medicalPhysicalTherapySecondInsured',
    title:
      'Receiving physical therapy, occupation therapy, speech therapy, or respiratory therapy?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalAssistiveDevicesFirstInsured: {
    id: 'medical-assistive-devices-first-insured',
    questionRef: 'medicalAssistiveDevicesFirstInsured',
    title:
      'Using a cane, crutch, walker, wheelchair, motorized scooter, chair or stair lift, or handicapped parking plate/placard/sticker?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalAssistiveDevicesSecondInsured: {
    id: 'medical-assistive-devices-second-insured',
    questionRef: 'medicalAssistiveDevicesSecondInsured',
    title:
      'Using a cane, crutch, walker, wheelchair, motorized scooter, chair or stair lift, or handicapped parking plate/placard/sticker?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalMedicalApplianceFirstInsured: {
    id: 'medical-medical-appliance-first-insured',
    questionRef: 'medicalMedicalApplianceFirstInsured',
    title:
      'Using any medical appliance such as catheter, oxygen equipment, respirator, CPAP or Bi-PAP, defibrillator, pacemaker, or dialysis machine?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalMedicalApplianceSecondInsured: {
    id: 'medical-medical-appliance-second-insured',
    questionRef: 'medicalMedicalApplianceSecondInsured',
    title:
      'Using any medical appliance such as catheter, oxygen equipment, respirator, CPAP or Bi-PAP, defibrillator, pacemaker, or dialysis machine?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalScheduledDoctorFirstInsured: {
    id: 'medical-scheduled-doctor-first-insured',
    questionRef: 'medicalScheduledDoctorFirstInsured',
    title: 'Scheduled for a doctor visit or any medical care within the next 3 months?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalScheduledDoctorSecondInsured: {
    id: 'medical-scheduled-doctor-second-insured',
    questionRef: 'medicalScheduledDoctorSecondInsured',
    title: 'Scheduled for a doctor visit or any medical care within the next 3 months?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalPregnantFirstInsured: {
    id: 'medical-pregnant-first-insured',
    questionRef: 'medicalPregnantFirstInsured',
    title: 'Currently pregnant?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalPregnantSecondInsured: {
    id: 'medical-pregnant-second-insured',
    questionRef: 'medicalPregnantSecondInsured',
    title: 'Currently pregnant?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalOtherTreatmentFirstInsured: {
    id: 'medical-other-treatment-first-insured',
    questionRef: 'medicalOtherTreatmentFirstInsured',
    title:
      'Being treated, examined, or advised by a member of the medical profession for any illness, disease, or injury not mentioned above?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalOtherTreatmentSecondInsured: {
    id: 'medical-other-treatment-second-insured',
    questionRef: 'medicalOtherTreatmentSecondInsured',
    title:
      'Being treated, examined, or advised by a member of the medical profession for any illness, disease, or injury not mentioned above?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalPrimaryCarePhysicianFirstInsuredName: {
    id: 'medical-primary-care-physician-first-insured-name',
    questionRef: 'medicalPrimaryCarePhysicianFirstInsuredName',
    title: 'List the name of the primary care physician for the Proposed Insured',
    type: 'text',
    required: true,
  },
  medicalPrimaryCarePhysicianFirstInsuredAddress: {
    id: 'medical-primary-care-physician-first-insured-address',
    questionRef: 'medicalPrimaryCarePhysicianFirstInsuredAddress',
    title: 'List address of current or most recent primary care physician for the Proposed Insured',
    type: 'text',
    required: true,
    validationType: 'address',
  },
  medicalPrimaryCarePhysicianSecondInsuredName: {
    id: 'medical-primary-care-physician-second-insured-name',
    questionRef: 'medicalPrimaryCarePhysicianSecondInsuredName',
    title:
      'List the name of the current or most recent primary care physician for the Second Proposed Insured',
    type: 'text',
    required: true,
  },
  medicalPrimaryCarePhysicianSecondInsuredAddress: {
    id: 'medical-primary-care-physician-second-insured-address',
    questionRef: 'medicalPrimaryCarePhysicianSecondInsuredAddress',
    title:
      'List address of current or most recent primary care physician for the Second Proposed Insured',
    type: 'text',
    required: true,
    validationType: 'address',
  },
  medicalMedicationsFirstInsured: {
    id: 'medical-medications-first-insured',
    questionRef: 'medicalMedicationsFirstInsured',
    title: 'List all medications currently being taken - Proposed Insured',
    type: 'table',
    required: true,
    minRows: 0,
    maxRows: 3,
    gridColumns: 1,
    columns: [
      {
        id: 'medicalMedicationsItem',
        title: 'Medication',
        type: 'text',
        required: false,
      },
    ],
  },
  medicalMedicationsSecondInsured: {
    id: 'medical-medications-second-insured',
    questionRef: 'medicalMedicationsSecondInsured',
    title: 'List all medications currently being taken - Second Proposed Insured',
    type: 'table',
    required: true,
    minRows: 0,
    maxRows: 3,
    gridColumns: 1,
    columns: [
      {
        id: 'medicalMedicationsItem',
        title: 'Medication',
        type: 'text',
        required: false,
      },
    ],
  },
  medicalSectionBCDetailsFirstInsured: {
    id: 'medical-section-bc-details-first-insured',
    questionRef: 'medicalSectionBCDetailsFirstInsured',
    title: 'PROVIDE DETAILS TO YES ANSWERS ABOVE FOR "{clientFullName}".',
    type: 'textarea',
    required: false,
    placeholder: 'Enter details for any YES answers in C',
    conditional: {
      visible: {
        operator: 'or',
        conditions: [
          {
            field: 'medicalPhysicalTherapyFirstInsured',
            value: 'yes',
            operator: 'equals',
          },
          {
            field: 'medicalAssistiveDevicesFirstInsured',
            value: 'yes',
            operator: 'equals',
          },
          {
            field: 'medicalMedicalApplianceFirstInsured',
            value: 'yes',
            operator: 'equals',
          },
          {
            field: 'medicalScheduledDoctorFirstInsured',
            value: 'yes',
            operator: 'equals',
          },
          {
            field: 'medicalPregnantFirstInsured',
            value: 'yes',
            operator: 'equals',
          },
          {
            field: 'medicalOtherTreatmentFirstInsured',
            value: 'yes',
            operator: 'equals',
          },
        ],
      },
    },
  },
  medicalSectionBCDetailsSecondInsured: {
    id: 'medical-section-bc-details-second-insured',
    questionRef: 'medicalSectionBCDetailsSecondInsured',
    title: 'PROVIDE DETAILS TO YES ANSWERS ABOVE FOR "{clientPartnerFullName}".',
    type: 'textarea',
    required: false,
    placeholder: 'Enter details for any YES answers in C',
    conditional: {
      visible: {
        operator: 'or',
        conditions: [
          {
            field: 'medicalPhysicalTherapySecondInsured',
            value: 'yes',
            operator: 'equals',
          },
          {
            field: 'medicalAssistiveDevicesSecondInsured',
            value: 'yes',
            operator: 'equals',
          },
          {
            field: 'medicalMedicalApplianceSecondInsured',
            value: 'yes',
            operator: 'equals',
          },
          {
            field: 'medicalScheduledDoctorSecondInsured',
            value: 'yes',
            operator: 'equals',
          },
          {
            field: 'medicalPregnantSecondInsured',
            value: 'yes',
            operator: 'equals',
          },
          {
            field: 'medicalOtherTreatmentSecondInsured',
            value: 'yes',
            operator: 'equals',
          },
        ],
      },
    },
  },

  /* Medical Questions D - Driving and Legal */
  medicalDrivingViolationFirstInsured: {
    id: 'medical-driving-violation-first-insured',
    questionRef: 'medicalDrivingViolationFirstInsured',
    title:
      "Been convicted of a driving violation, driving under the influence of alcohol or drugs, or had his/hers driver's license suspended or revoked?",
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalDrivingViolationSecondInsured: {
    id: 'medical-driving-violation-second-insured',
    questionRef: 'medicalDrivingViolationSecondInsured',
    title:
      "Been convicted of a driving violation, driving under the influence of alcohol or drugs, or had his/hers driver's license suspended or revoked?",
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalCriminalConvictionFirstInsured: {
    id: 'medical-criminal-conviction-first-insured',
    questionRef: 'medicalCriminalConvictionFirstInsured',
    title:
      'Plead guilty to or been convicted of a felony or misdemeanor or have such charge currently pending?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalCriminalConvictionSecondInsured: {
    id: 'medical-criminal-conviction-second-insured',
    questionRef: 'medicalCriminalConvictionSecondInsured',
    title:
      'Plead guilty to or been convicted of a felony or misdemeanor or have such charge currently pending?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalSectionDDetailsFirstInsured: {
    id: 'medical-section-d-details-first-insured',
    questionRef: 'medicalSectionDDetailsFirstInsured',
    title: 'PROVIDE DETAILS TO YES ANSWERS ABOVE FOR "{clientFullName}".',
    type: 'textarea',
    required: false,
    placeholder: 'Enter details for any YES answers in section D',
    conditional: {
      visible: {
        operator: 'or',
        conditions: [
          {
            field: 'medicalDrivingViolationFirstInsured',
            value: 'yes',
            operator: 'equals',
          },
          {
            field: 'medicalCriminalConvictionFirstInsured',
            value: 'yes',
            operator: 'equals',
          },
        ],
      },
    },
  },
  medicalSectionDDetailsSecondInsured: {
    id: 'medical-section-d-details-second-insured',
    questionRef: 'medicalSectionDDetailsSecondInsured',
    title: 'PROVIDE DETAILS TO YES ANSWERS ABOVE FOR "{clientPartnerFullName}".',
    type: 'textarea',
    required: false,
    placeholder: 'Enter details for any YES answers in section D',
    conditional: {
      visible: {
        operator: 'or',
        conditions: [
          {
            field: 'medicalDrivingViolationSecondInsured',
            value: 'yes',
            operator: 'equals',
          },
          {
            field: 'medicalCriminalConvictionSecondInsured',
            value: 'yes',
            operator: 'equals',
          },
        ],
      },
    },
  },

  /* Medical Questions E - Insurance History */
  medicalInsuranceDeclinedFirstInsured: {
    id: 'medical-insurance-declined-first-insured',
    questionRef: 'medicalInsuranceDeclinedFirstInsured',
    title:
      'Has any person proposed for insurance ever been declined, postponed, or had an insurance application modified as to plan, amount, or rate?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalInsuranceDeclinedSecondInsured: {
    id: 'medical-insurance-declined-second-insured',
    questionRef: 'medicalInsuranceDeclinedSecondInsured',
    title:
      'Has any person proposed for insurance ever been declined, postponed, or had an insurance application modified as to plan, amount, or rate?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalSectionEDetailsFirstInsured: {
    id: 'medical-section-e-details-first-insured',
    questionRef: 'medicalSectionEDetailsFirstInsured',
    title: 'PROVIDE DETAILS TO YES ANSWERS ABOVE FOR "{clientFullName}".',
    type: 'textarea',
    required: false,
    placeholder: 'Enter details for any YES answers in section E',
    conditional: {
      visible: {
        field: 'medicalInsuranceDeclinedFirstInsured',
        operator: 'equals',
        value: 'yes',
      },
    },
  },
  medicalSectionEDetailsSecondInsured: {
    id: 'medical-section-e-details-second-insured',
    questionRef: 'medicalSectionEDetailsSecondInsured',
    title: 'PROVIDE DETAILS TO YES ANSWERS ABOVE FOR "{clientPartnerFullName}".',
    type: 'textarea',
    required: false,
    placeholder: 'Enter details for any YES answers in section E',
    conditional: {
      visible: {
        field: 'medicalInsuranceDeclinedSecondInsured',
        operator: 'equals',
        value: 'yes',
      },
    },
  },

  /* Medical Questions F - Substance Use */
  medicalDrugUseFirstInsured: {
    id: 'medical-drug-use-first-insured',
    questionRef: 'medicalDrugUseFirstInsured',
    title:
      'Used narcotics, barbiturates, amphetamines, hallucinogens, heroin, cocaine, marijuana, or other habit forming drugs, except as prescribed by a physician?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalDrugUseSecondInsured: {
    id: 'medical-drug-use-second-insured',
    questionRef: 'medicalDrugUseSecondInsured',
    title:
      'Used narcotics, barbiturates, amphetamines, hallucinogens, heroin, cocaine, marijuana, or other habit forming drugs, except as prescribed by a physician?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalAlcoholTreatmentFirstInsured: {
    id: 'medical-alcohol-treatment-first-insured',
    questionRef: 'medicalAlcoholTreatmentFirstInsured',
    title:
      'Received medical treatment or counseling for, or been advised by a physician to discontinue the use of alcohol, prescribed medication, or any other non-prescribed drugs?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalAlcoholTreatmentSecondInsured: {
    id: 'medical-alcohol-treatment-second-insured',
    questionRef: 'medicalAlcoholTreatmentSecondInsured',
    title:
      'Received medical treatment or counseling for, or been advised by a physician to discontinue the use of alcohol, prescribed medication, or any other non-prescribed drugs?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalSelfHelpGroupFirstInsured: {
    id: 'medical-self-help-group-first-insured',
    questionRef: 'medicalSelfHelpGroupFirstInsured',
    title:
      'Been a member of any self-help group such as Alcoholics Anonymous or Narcotics Anonymous?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalSelfHelpGroupSecondInsured: {
    id: 'medical-self-help-group-second-insured',
    questionRef: 'medicalSelfHelpGroupSecondInsured',
    title:
      'Been a member of any self-help group such as Alcoholics Anonymous or Narcotics Anonymous?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },

  /* Medical Questions G-K - Activities and Tobacco */
  medicalHazardousActivitiesFirstInsured: {
    id: 'medical-hazardous-activities-first-insured',
    questionRef: 'medicalHazardousActivitiesFirstInsured',
    title:
      'Has any person proposed for insurance participated in any vehicle racing, parachuting, hang gliding, scuba diving, ballooning, rock or mountain climbing, or spelunking (cave exploring) within the past 2 years or is any such activity contemplated within the next 2 years?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalHazardousActivitiesSecondInsured: {
    id: 'medical-hazardous-activities-second-insured',
    questionRef: 'medicalHazardousActivitiesSecondInsured',
    title:
      'Has any person proposed for insurance participated in any vehicle racing, parachuting, hang gliding, scuba diving, ballooning, rock or mountain climbing, or spelunking (cave exploring) within the past 2 years or is any such activity contemplated within the next 2 years?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalAviationFirstInsured: {
    id: 'medical-aviation-first-insured',
    questionRef: 'medicalAviationFirstInsured',
    title:
      'Excluding commercial aviation, has any person proposed for insurance flown within the past 2 years as a pilot, student pilot, crew member, or had any flying duties, or is any such activity contemplated within the next 2 years?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalAviationSecondInsured: {
    id: 'medical-aviation-second-insured',
    questionRef: 'medicalAviationSecondInsured',
    title:
      'Excluding commercial aviation, has any person proposed for insurance flown within the past 2 years as a pilot, student pilot, crew member, or had any flying duties, or is any such activity contemplated within the next 2 years?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalForeignTravelFirstInsured: {
    id: 'medical-foreign-travel-first-insured',
    questionRef: 'medicalForeignTravelFirstInsured',
    title:
      'Does any person proposed for insurance contemplate travel or residence in a foreign country within the next 2 years?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalForeignTravelSecondInsured: {
    id: 'medical-foreign-travel-second-insured',
    questionRef: 'medicalForeignTravelSecondInsured',
    title:
      'Does any person proposed for insurance contemplate travel or residence in a foreign country within the next 2 years?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalArmedForcesFirstInsured: {
    id: 'medical-armed-forces-first-insured',
    questionRef: 'medicalArmedForcesFirstInsured',
    title:
      'Is any person proposed for insurance currently a member of the Armed Forces or has any person proposed for insurance entered into a written agreement to become a member?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalArmedForcesSecondInsured: {
    id: 'medical-armed-forces-second-insured',
    questionRef: 'medicalArmedForcesSecondInsured',
    title:
      'Is any person proposed for insurance currently a member of the Armed Forces or has any person proposed for insurance entered into a written agreement to become a member?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalTobaccoUseFirstInsured: {
    id: 'medical-tobacco-use-first-insured',
    questionRef: 'medicalTobaccoUseFirstInsured',
    title:
      'Has any person proposed for insurance ever used any nicotine and/or tobacco products such as smoking cigarettes, pipes or cigars, using snuff, chewing tobacco, or a nicotine delivery device (patch, gum, vaping, e-cigarettes, hookah, etc.)?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalTobaccoUseSecondInsured: {
    id: 'medical-tobacco-use-second-insured',
    questionRef: 'medicalTobaccoUseSecondInsured',
    title:
      'Has any person proposed for insurance ever used any nicotine and/or tobacco products such as smoking cigarettes, pipes or cigars, using snuff, chewing tobacco, or a nicotine delivery device (patch, gum, vaping, e-cigarettes, hookah, etc.)?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalTobaccoDetailsFirstInsured: {
    id: 'medical-tobacco-details-first-insured',
    questionRef: 'medicalTobaccoDetailsFirstInsured',
    title: 'Tobacco Use Details - Proposed Insured',
    type: 'table',
    required: false,
    minRows: 1,
    maxRows: 1,
    gridColumns: 1,
    columns: [
      {
        id: 'presentOrFormer',
        title: 'a. Present or Former',
        type: 'select',
        required: false,
        options: [
          { id: 'present', value: 'Present' },
          { id: 'former', value: 'Former' },
        ],
      },
      {
        id: 'typeAndAmount',
        title: 'b. Type and amount of nicotine or tobacco used',
        type: 'text',
        required: false,
      },
      {
        id: 'whenStopped',
        title:
          'c. When stopped using all forms of nicotine (including substitutes listed above) or tobacco?',
        type: 'date',
        required: false,
      },
    ],
    conditional: {
      visible: {
        field: 'medicalTobaccoUseFirstInsured',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  medicalTobaccoDetailsSecondInsured: {
    id: 'medical-tobacco-details-second-insured',
    questionRef: 'medicalTobaccoDetailsSecondInsured',
    title: 'Tobacco Use Details - Second Proposed Insured',
    type: 'table',
    required: false,
    minRows: 1,
    maxRows: 1,
    gridColumns: 1,
    columns: [
      {
        id: 'presentOrFormer',
        title: 'a. Present or Former',
        type: 'select',
        required: false,
        options: [
          { id: 'present', value: 'Present' },
          { id: 'former', value: 'Former' },
        ],
      },
      {
        id: 'typeAndAmount',
        title: 'b. Type and amount of nicotine or tobacco used',
        type: 'text',
        required: false,
      },
      {
        id: 'whenStopped',
        title:
          'c. When stopped using all forms of nicotine (including substitutes listed above) or tobacco?',
        type: 'date',
        required: false,
      },
    ],
    conditional: {
      visible: {
        field: 'medicalTobaccoUseSecondInsured',
        value: 'yes',
        operator: 'equals',
      },
    },
  },

  /* US Patriot Act */
  /* Product Selection */
  patriotActProduct: {
    id: 'patriot-act-product',
    questionRef: 'patriotActProduct',
    title: 'Product',
    type: 'multiselect',
    required: true,
    options: [
      { id: 'life-insurance', value: 'Life Insurance' },
      { id: 'fixed-annuity', value: 'Fixed Annuity' },
      { id: 'life-insurance-annuity-ltc', value: 'Life Insurance or Annuity with LTC benefits' },
    ],
  },
  patriotActPolicyNumber: {
    id: 'patriot-act-policy-number',
    questionRef: 'patriotActPolicyNumber',
    title: 'Policy Number',
    type: 'text',
    required: false,
  },

  /* Section 1: Source of Funds Used for Purchase */
  patriotActSourceOfFunds: {
    id: 'patriot-act-source-of-funds',
    questionRef: 'patriotActSourceOfFunds',
    title: 'Source of Funds Used for Purchase',
    type: 'multiselect',
    required: true,
    options: [
      { id: 'cash-savings', value: 'Cash or Personal Savings' },
      { id: 'income-earnings', value: 'Income from Earnings' },
      { id: 'inheritance', value: 'Inheritance' },
      { id: 'insurance-proceeds', value: 'Insurance Proceeds' },
      { id: 'investment-proceeds', value: 'Investment Proceeds' },
      { id: 'pension-ira-retirement', value: 'Pension/IRA/Retirement Savings' },
      { id: 'legal-settlements', value: 'Legal Settlements' },
      { id: 'gift', value: 'Gift' },
      { id: 'transfer-exchange', value: 'Transfer/Exchange from Existing Annuity' },
      { id: 'sale-business', value: 'Sale of Business' },
      {
        id: 'transfer-exchange-insurance',
        value: 'Transfer/Exchange from Existing Life Insurance',
      },
      { id: 'other', value: 'Other' },
    ],
  },
  patriotActOtherSourceSpecify: {
    id: 'patriot-act-other-source-specify',
    questionRef: 'patriotActOtherSourceSpecify',
    title: 'Other Source (Please specify)',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'patriotActSourceOfFunds',
        value: 'other',
        operator: 'contains',
      },
      required: {
        field: 'patriotActSourceOfFunds',
        value: 'other',
        operator: 'contains',
      },
    },
  },
  patriotActPremiumPayerName: {
    id: 'patriot-act-premium-payer-name',
    questionRef: 'patriotActPremiumPayerName',
    title: 'Name of Premium Payer, if other than the Owner',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'policyPayor',
        operator: 'equals',
        value: 'payor_other',
      },
    },
  },
  patriotActPremiumPayerRelationship: {
    id: 'patriot-act-premium-payer-relationship',
    questionRef: 'patriotActPremiumPayerRelationship',
    title: 'Relationship to Owner',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'patriotActPremiumPayerName',
        operator: 'notEmpty',
      },
    },
  },

  /* Identification Verified - Owner/Trustee/Partner */
  patriotActOwnerType: {
    id: 'patriot-act-owner-type',
    questionRef: 'patriotActOwnerType',
    title: 'IDENTIFICATION VERIFIED (One for each Owner/Trustee/Partner.)',
    type: 'radio',
    required: true,
    options: [
      { id: 'owner', value: 'Owner' },
      { id: 'trustee', value: 'Trustee' },
      { id: 'partner', value: 'Partner' },
    ],
  },
  patriotActOwnerIdType: {
    id: 'patriot-act-owner-id-type',
    questionRef: 'patriotActOwnerIdType',
    title: 'Check one form of ID',
    type: 'select',
    required: true,
    options: [
      { id: 'drivers-license', value: "Driver's License" },
      { id: 'resident-alien-id', value: 'Resident Alien ID (Green Card)' },
      { id: 'passport', value: 'Passport' },
      { id: 'other', value: 'Other' },
    ],
  },
  patriotActOwnerIdOtherDesc: {
    id: 'patriot-act-owner-id-other-desc',
    questionRef: 'patriotActOwnerIdOtherDesc',
    title: 'Other (Describe)',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'patriotActOwnerIdType',
        value: 'other',
        operator: 'equals',
      },
      required: {
        field: 'patriotActOwnerIdType',
        value: 'other',
        operator: 'equals',
      },
    },
  },
  patriotActOwnerDriversLicenseIssuer: {
    id: 'patriot-act-owner-drivers-license-issuer',
    questionRef: 'patriotActOwnerDriversLicenseIssuer',
    title: 'Issuer',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'patriotActOwnerIdType',
        value: 'drivers-license',
        operator: 'equals',
      },
      required: {
        field: 'patriotActOwnerIdType',
        value: 'drivers-license',
        operator: 'equals',
      },
    },
  },
  patriotActOwnerAlienIdIssuer: {
    id: 'patriot-act-owner-alien-id-issuer',
    questionRef: 'patriotActOwnerAlienIdIssuer',
    title: 'Issuer',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'patriotActOwnerIdType',
        value: 'resident-alien-id',
        operator: 'equals',
      },
      required: {
        field: 'patriotActOwnerIdType',
        value: 'resident-alien-id',
        operator: 'equals',
      },
    },
  },
  patriotActOwnerPassportIssuer: {
    id: 'patriot-act-owner-passport-issuer',
    questionRef: 'patriotActOwnerPassportIssuer',
    title: 'Issuer',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'patriotActOwnerIdType',
        value: 'passport',
        operator: 'equals',
      },
      required: {
        field: 'patriotActOwnerIdType',
        value: 'passport',
        operator: 'equals',
      },
    },
  },
  patriotActOwnerName: {
    id: 'patriot-act-owner-name',
    questionRef: 'patriotActOwnerName',
    title: 'Name',
    type: 'text',
    required: true,
  },
  patriotActOwnerStreetAddress: {
    id: 'patriot-act-owner-street-address',
    questionRef: 'patriotActOwnerStreetAddress',
    title: 'Street Address (not P.O. Box)',
    type: 'text',
    required: true,
    validationType: 'streetAddress',
  },
  patriotActOwnerCityStateZip: {
    id: 'patriot-act-owner-city-state-zip',
    questionRef: 'patriotActOwnerCityStateZip',
    title: 'City, State, ZIP',
    type: 'text',
    required: true,
    validationType: 'cityStateZipcode',
  },
  patriotActOwnerIdNumber: {
    id: 'patriot-act-owner-id-number',
    questionRef: 'patriotActOwnerIdNumber',
    title: 'Number on ID',
    type: 'text',
    required: true,
  },
  patriotActOwnerIdStateCountry: {
    id: 'patriot-act-owner-id-state-country',
    questionRef: 'patriotActOwnerIdStateCountry',
    title: 'State or Country',
    type: 'text',
    required: true,
  },
  patriotActOwnerIdExpirationDate: {
    id: 'patriot-act-owner-id-expiration-date',
    questionRef: 'patriotActOwnerIdExpirationDate',
    title: 'Identification Expiration Date',
    type: 'date',
    required: true,
  },

  /* Joint Owner Identification */
  patriotActJointOwnerType: {
    id: 'patriot-act-joint-owner-type',
    questionRef: 'patriotActJointOwnerType',
    title: 'IDENTIFICATION VERIFIED (One for each Owner/Trustee/Partner.)',
    type: 'radio',
    required: false,
    options: [
      { id: 'joint-owner', value: 'Joint Owner' },
      { id: 'trustee', value: 'Trustee' },
      { id: 'partner', value: 'Partner' },
    ],
  },
  patriotActJointOwnerIdType: {
    id: 'patriot-act-joint-owner-id-type',
    questionRef: 'patriotActJointOwnerIdType',
    title: 'Check one form of ID',
    type: 'select',
    required: false,
    options: [
      { id: 'drivers-license', value: "Driver's License" },
      { id: 'resident-alien-id', value: 'Resident Alien ID (Green Card)' },
      { id: 'passport', value: 'Passport' },
      { id: 'other', value: 'Other' },
    ],
  },
  patriotActJointOwnerIdOtherDesc: {
    id: 'patriot-act-joint-owner-id-other-desc',
    questionRef: 'patriotActJointOwnerIdOtherDesc',
    title: 'Other (Describe)',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'patriotActJointOwnerIdType',
        value: 'other',
        operator: 'equals',
      },
      required: {
        field: 'patriotActJointOwnerIdType',
        value: 'other',
        operator: 'equals',
      },
    },
  },
  patriotActJointOwnerDriversLicenseIssuer: {
    id: 'patriot-act-joint-owner-drivers-license-issuer',
    questionRef: 'patriotActJointOwnerDriversLicenseIssuer',
    title: 'Issuer',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'patriotActJointOwnerIdType',
        value: 'drivers-license',
        operator: 'equals',
      },
      required: {
        field: 'patriotActJointOwnerIdType',
        value: 'drivers-license',
        operator: 'equals',
      },
    },
  },
  patriotActJointOwnerAlienIdIssuer: {
    id: 'patriot-act-joint-owner-alien-id-issuer',
    questionRef: 'patriotActJointOwnerAlienIdIssuer',
    title: 'Issuer',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'patriotActJointOwnerIdType',
        value: 'resident-alien-id',
        operator: 'equals',
      },
      required: {
        field: 'patriotActJointOwnerIdType',
        value: 'resident-alien-id',
        operator: 'equals',
      },
    },
  },
  patriotActJointOwnerPassportIssuer: {
    id: 'patriot-act-joint-owner-passport-issuer',
    questionRef: 'patriotActJointOwnerPassportIssuer',
    title: 'Issuer',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'patriotActJointOwnerIdType',
        value: 'passport',
        operator: 'equals',
      },
      required: {
        field: 'patriotActJointOwnerIdType',
        value: 'passport',
        operator: 'equals',
      },
    },
  },
  patriotActJointOwnerName: {
    id: 'patriot-act-joint-owner-name',
    questionRef: 'patriotActJointOwnerName',
    title: 'Name',
    type: 'text',
    required: false,
  },
  patriotActJointOwnerStreetAddress: {
    id: 'patriot-act-joint-owner-street-address',
    questionRef: 'patriotActJointOwnerStreetAddress',
    title: 'Street Address (not P.O. Box)',
    type: 'text',
    required: false,
    validationType: 'streetAddress',
  },
  patriotActJointOwnerCityStateZip: {
    id: 'patriot-act-joint-owner-city-state-zip',
    questionRef: 'patriotActJointOwnerCityStateZip',
    title: 'City, State, ZIP',
    type: 'text',
    required: false,
    validationType: 'cityStateZipcode',
  },
  patriotActJointOwnerIdNumber: {
    id: 'patriot-act-joint-owner-id-number',
    questionRef: 'patriotActJointOwnerIdNumber',
    title: 'Number on ID',
    type: 'text',
    required: false,
  },
  patriotActJointOwnerIdStateCountry: {
    id: 'patriot-act-joint-owner-id-state-country',
    questionRef: 'patriotActJointOwnerIdStateCountry',
    title: 'State or Country',
    type: 'text',
    required: false,
  },
  patriotActJointOwnerIdExpirationDate: {
    id: 'patriot-act-joint-owner-id-expiration-date',
    questionRef: 'patriotActJointOwnerIdExpirationDate',
    title: 'Identification Expiration Date',
    type: 'date',
    required: false,
  },

  /* Entity Verification */
  patriotActEntityDocuments: {
    id: 'patriot-act-entity-documents',
    questionRef: 'patriotActEntityDocuments',
    title: 'Entity Verification Documents',
    type: 'multiselect',
    required: false,
    options: [
      { id: 'articles-incorporation', value: 'Articles of Incorporation' },
      { id: 'llc-operating-agreement', value: 'LLC Operating Agreement' },
      { id: 'partnership-agreement', value: 'Partnership Agreement' },
      { id: 'organizing-documents', value: 'Organizing Documents' },
      { id: 'trust-documents', value: 'Trust Documents' },
      { id: 'other', value: 'Other' },
    ],
  },
  patriotActEntityOtherDesc: {
    id: 'patriot-act-entity-other-desc',
    questionRef: 'patriotActEntityOtherDesc',
    title: 'Other (Describe on back)',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'patriotActEntityDocuments',
        value: 'other',
        operator: 'contains',
      },
    },
  },

  /* Certifications */
  patriotActCertification: {
    id: 'patriot-act-certification',
    questionRef: 'patriotActCertification',
    title: 'Certification',
    type: 'radio',
    format: 'vertical',
    required: true,
    options: [
      {
        id: 'yes',
        value:
          'I certify that I personally met with the proposed Owner(s)/Trustee(s)/Partners and reviewed the above identification document. To the best of my knowledge it accurately reflects the identity of the proposed Owner(s)/Trustee(s)/Partners.',
      },
      {
        id: 'no',
        value:
          'I was unable to personally review the identification documents for the reason stated below. I certify that, to the best of my knowledge, the information provided by the Owner(s)/Trustee(s)/Partners is true and accurate.',
      },
    ],
  },
  patriotActReasonNoReview: {
    id: 'patriot-act-reason-no-review',
    questionRef: 'patriotActReasonNoReview',
    title: 'Reason',
    type: 'textarea',
    required: false,
    conditional: {
      visible: {
        field: 'patriotActCertification',
        value: 'no',
        operator: 'equals',
      },
      required: {
        field: 'patriotActCertification',
        value: 'no',
        operator: 'equals',
      },
    },
  },

  /* Producer Information */
  patriotActProducerName: {
    id: 'patriot-act-producer-name',
    questionRef: 'patriotActProducerName',
    title: 'Producer Name',
    type: 'text',
    required: true,
  },
  patriotActProducerNumber: {
    id: 'patriot-act-producer-number',
    questionRef: 'patriotActProducerNumber',
    title: 'Producer Number',
    type: 'text',
    required: true,
  },

  /* Authorization for Disclosure of Personal Information */

  /* Header Information */
  authDisclosureAttention: {
    id: 'auth-disclosure-attention',
    questionRef: 'authDisclosureAttention',
    title: 'Attn (who should receive this)',
    type: 'text',
    required: false,
  },
  authDisclosureApplicantPolicyOwners: {
    id: 'auth-disclosure-applicant-policy-owners',
    questionRef: 'authDisclosureApplicantPolicyOwners',
    title: 'Applicant(s)/Policy Owner(s)',
    type: 'text',
    required: true,
  },
  authDisclosureApplicantPolicyI: {
    id: 'auth-disclosure-applicant-policy-i',
    questionRef: 'authDisclosureApplicantPolicyI',
    title: 'I (Your full legal name)',
    type: 'text',
    required: true,
  },
  authDisclosureProposedInsuredAnnuitant: {
    id: 'auth-disclosure-proposed-insured-annuitant',
    questionRef: 'authDisclosureProposedInsuredAnnuitant',
    title: 'Proposed Insured/Annuitant or Insured/Annuitant',
    type: 'text',
    required: true,
  },
  authDisclosurePolicyNumber: {
    id: 'auth-disclosure-policy-number',
    questionRef: 'authDisclosurePolicyNumber',
    title: 'Policy No.',
    type: 'text',
    required: false,
  },

  /* Company Authorization */
  authDisclosureCompanyAuthorized: {
    id: 'auth-disclosure-company-authorized',
    questionRef: 'authDisclosureCompanyAuthorized',
    title: 'I hereby authorize',
    type: 'multiselect',
    required: true,
    options: [
      { id: 'aul', value: 'American United Life Insurance Company® (AUL)' },
      { id: 'state-life', value: 'The State Life Insurance Company (State Life)' },
      { id: 'pml', value: 'Pioneer Mutual Life Insurance Company (PML)' },
      { id: 'golden-rule', value: 'Golden Rule Insurance Company' },
    ],
  },

  /* Description of Information to be Disclosed */
  authDisclosureInformationType: {
    id: 'auth-disclosure-information-type',
    questionRef: 'authDisclosureInformationType',
    title: 'Description of Information to be Disclosed and Purpose',
    type: 'multiselect',
    required: true,
    options: [
      {
        id: 'medical-admin',
        value:
          'Medical information (including health information, but excluding psychotherapy notes) for the purpose of policy administration at my request.',
      },
      {
        id: 'medical-processing',
        value:
          'Medical information (including health and claims information, but excluding psychotherapy notes) for the purpose of processing my claim for benefits at my request.',
      },
      {
        id: 'medical-underwriting',
        value:
          'Medical information (including health information, but excluding psychotherapy notes) for the purpose of obtaining underwriting information at my request.',
      },
    ],
  },

  /* Person or Business Authorized to Receive Information */
  authDisclosureRecipientName: {
    id: 'auth-disclosure-recipient-name',
    questionRef: 'authDisclosureRecipientName',
    title: 'Name',
    type: 'text',
    required: true,
  },
  authDisclosureRecipientAddress: {
    id: 'auth-disclosure-recipient-address',
    questionRef: 'authDisclosureRecipientAddress',
    title: 'Address',
    type: 'text',
    required: true,
    validationType: 'streetAddress',
  },
  authDisclosureRecipientCity: {
    id: 'auth-disclosure-recipient-city',
    questionRef: 'authDisclosureRecipientCity',
    title: 'City',
    type: 'text',
    required: true,
    validationType: 'city',
  },
  authDisclosureRecipientState: {
    id: 'auth-disclosure-recipient-state',
    questionRef: 'authDisclosureRecipientState',
    title: 'State',
    type: 'text',
    required: true,
    validationType: 'state',
  },
  authDisclosureRecipientZip: {
    id: 'auth-disclosure-recipient-zip',
    questionRef: 'authDisclosureRecipientZip',
    title: 'Zip',
    type: 'text',
    required: true,
    validationType: 'zipcode',
  },

  /* Expiration of Authorization */
  authDisclosureSignatureDate: {
    id: 'auth-disclosure-signature-date',
    questionRef: 'authDisclosureSignatureDate',
    title: 'Date',
    type: 'date',
    required: true,
  },
  authDisclosureDaytimePhone: {
    id: 'auth-disclosure-daytime-phone',
    questionRef: 'authDisclosureDaytimePhone',
    title:
      'Daytime Phone Number of Claimant, Insured/Annuitant, Proposed Insured/Annuitant or Legal Representative',
    type: 'text',
    required: true,
    validationType: 'phone',
  },
  authDisclosureStateOfSigning: {
    id: 'auth-disclosure-state-of-signing',
    questionRef: 'authDisclosureStateOfSigning',
    title: 'State in which this authorization was signed',
    type: 'text',
    required: true,
  },

  /* Representative's Authority */
  authDisclosureRepresentativeAuthority: {
    id: 'auth-disclosure-representative-authority',
    questionRef: 'authDisclosureRepresentativeAuthority',
    title: "Description of Representative's Authority",
    type: 'text',
    required: false,
    placeholder: '(as Guardian, Person with Power of Attorney, etc.)',
  },

  /* Section 13 - Premium (Annual/Single) */
  ltcPremiumForPolicy: {
    id: 'ltc-premium-for-policy',
    questionRef: 'ltcPremiumForPolicy',
    title: 'Premium for the Policy',
    type: 'text',
    placeholder: '$',
    validationType: 'currency',
  },
  ltcPremiumForAobRider: {
    id: 'ltc-premium-for-aob-rider',
    questionRef: 'ltcPremiumForAobRider',
    title: 'Premium for the AOB Rider',
    type: 'text',
    placeholder: '$',
    validationType: 'currency',
  },
  ltcPremiumForInflationProtectionRider: {
    id: 'ltc-premium-for-inflation-protection-rider',
    questionRef: 'ltcPremiumForInflationProtectionRider',
    title: 'Premium for Inflation Protection Rider',
    type: 'text',
    placeholder: '$',
    validationType: 'currency',
  },

  /* Section 13 - Premium (Annual/Single) (Rider) */
  ltcPremiumForThisRider: {
    id: 'ltc-premium-for-this-rider',
    questionRef: 'ltcPremiumForThisRider',
    title: 'Premium for This Rider',
    type: 'text',
    placeholder: '$',
    validationType: 'currency',
  },
  ltcPremiumForInflationProtectionRider2: {
    id: 'ltc-premium-for-inflation-protection-rider-2',
    questionRef: 'ltcPremiumForInflationProtectionRider2',
    title: 'Premium for Inflation Protection Rider',
    type: 'text',
    placeholder: '$',
    validationType: 'currency',
  },

  /* Request of Funds, Full 1035 Exchange, IRA Transfer, Direct Rollover, CD Assignment, Mutual Fund Transfer */

  /* Part 1 - Current Custodian Information */
  rofCurrentCustodianCompany: {
    id: 'rof-current-custodian-company',
    questionRef: 'rofCurrentCustodianCompany',
    title: 'Current Custodian (company from which funds will be requested)',
    type: 'text',
    required: true,
  },
  rofCurrentCustodianTelephone: {
    id: 'rof-current-custodian-telephone',
    questionRef: 'rofCurrentCustodianTelephone',
    title: 'Telephone',
    type: 'text',
    required: true,
    validationType: 'phone',
  },
  rofCurrentPolicyAccountNumbers: {
    id: 'rof-current-policy-account-numbers',
    questionRef: 'rofCurrentPolicyAccountNumbers',
    title: 'Current Policy/Account Nos.',
    type: 'text',
    required: true,
  },
  rofCustodianAddress: {
    id: 'rof-custodian-address',
    questionRef: 'rofCustodianAddress',
    title: 'Address',
    type: 'text',
    required: true,
    validationType: 'streetAddress',
  },
  rofCustodianCity: {
    id: 'rof-custodian-city',
    questionRef: 'rofCustodianCity',
    title: 'City',
    type: 'text',
    required: true,
    validationType: 'city',
  },
  rofCustodianState: {
    id: 'rof-custodian-state',
    questionRef: 'rofCustodianState',
    title: 'State',
    type: 'text',
    required: true,
    validationType: 'city',
  },
  rofCustodianZip: {
    id: 'rof-custodian-zip',
    questionRef: 'rofCustodianZip',
    title: 'Zip',
    type: 'text',
    required: true,
    validationType: 'zipcode',
  },
  rofCustomerPolicyownerTaxpayer: {
    id: 'rof-customer-policyowner-taxpayer',
    questionRef: 'rofCustomerPolicyownerTaxpayer',
    title: 'Name of Customer/Policyowner/Taxpayer',
    type: 'text',
    required: true,
  },
  rofTaxpayerIdentificationNumber: {
    id: 'rof-taxpayer-identification-number',
    questionRef: 'rofTaxpayerIdentificationNumber',
    title: 'Taxpayer Identification No.',
    type: 'text',
    required: true,
  },
  rofEstimatedPolicyAccountValue: {
    id: 'rof-estimated-policy-account-value',
    questionRef: 'rofEstimatedPolicyAccountValue',
    title: 'Estimated Policy/Account Value',
    type: 'text',
    placeholder: '$',
    required: true,
    validationType: 'currency',
  },
  rofNameOfInsuredAnnuitant: {
    id: 'rof-name-of-insured-annuitant',
    questionRef: 'rofNameOfInsuredAnnuitant',
    title: 'Name of Insured/Annuitant (if any)',
    type: 'text',
    required: false,
  },
  rofFundsBeingTransferredInto: {
    id: 'rof-funds-being-transferred-into',
    questionRef: 'rofFundsBeingTransferredInto',
    title: 'Funds Are Being Transferred Into',
    type: 'radio',
    required: true,
    options: [
      { id: 'asset-care', value: 'Asset-Care®' },
      { id: 'immediate-annuity', value: 'Immediate Annuity' },
      { id: 'deferred-annuity', value: 'Deferred Annuity' },
      { id: 'other', value: 'Other' },
    ],
  },
  rofMustCheckOne: {
    id: 'rof-must-check-one',
    questionRef: 'rofMustCheckOne',
    title: 'Must Check One',
    type: 'radio',
    required: true,
    options: [
      { id: '1035-exchanges', value: 'Section 1035 Exchanges (must complete Part 2)' },
      {
        id: 'qualified-funds',
        value: 'Qualified Funds: Transfers and Direct Rollovers (must complete Part 3)',
      },
      { id: 'other-funds', value: 'Other Funds: CDs and Mutual Funds (must complete Part 4)' },
    ],
  },
  rofMustCheckOnePolicy: {
    id: 'rof-must-check-one-policy',
    questionRef: 'rofMustCheckOnePolicy',
    title: 'Must Check One',
    type: 'radio',
    required: true,
    format: 'vertical',
    options: [
      { id: 'policy-enclosed', value: 'The policy is enclosed.' },
      {
        id: 'policy-lost',
        value:
          'The original policy has been lost or destroyed and to the best of my/our knowledge is not in the possession of any other person or firm.',
      },
    ],
  },

  /* Part 2 - Section 1035 Exchanges */
  rofFundsTransferredFrom: {
    id: 'rof-funds-transferred-from',
    questionRef: 'rofFundsTransferredFrom',
    title: 'Funds are being transferred from',
    type: 'radio',
    required: false,
    options: [
      { id: 'annuity', value: 'Annuity' },
      { id: 'whole-life', value: 'Whole Life' },
      { id: 'universal-life', value: 'Universal Life' },
      { id: 'variable-universal-life', value: 'Variable Universal Life' },
    ],
    conditional: {
      visible: {
        field: 'rofMustCheckOne',
        value: '1035-exchanges',
        operator: 'equals',
      },
    },
  },

  /* Part 3 - Qualified Funds */
  rofQualifiedCurrentStatus: {
    id: 'rof-qualified-current-status',
    questionRef: 'rofQualifiedCurrentStatus',
    title: 'A. Current Status of Policy/Account',
    format: 'vertical',
    type: 'radio',
    required: false,
    options: [
      {
        id: 'qualified-retirement-plan',
        value: 'Qualified Retirement Plan (e.g., TSA, 401(k), Keogh, Profit Sharing, etc.)',
      },
      { id: 'traditional-ira-sep', value: 'Traditional IRA, SEP' },
      { id: 'other', value: 'Other' },
    ],
    conditional: {
      visible: {
        field: 'rofMustCheckOne',
        value: 'qualified-funds',
        operator: 'equals',
      },
    },
  },
  rofQualifiedOtherSpecify: {
    id: 'rof-qualified-other-specify',
    questionRef: 'rofQualifiedOtherSpecify',
    title: 'Other (specify)',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'rofQualifiedCurrentStatus',
        value: 'other',
        operator: 'equals',
      },
    },
  },
  rofQualifiedTransferType: {
    id: 'rof-qualified-transfer-type',
    questionRef: 'rofQualifiedTransferType',
    title: 'B. Type of Tax-Free Funds Transfer',
    format: 'vertical',
    type: 'radio',
    required: false,
    options: [
      { id: 'direct-rollover', value: 'Direct Rollover (From Qualified Retirement Plan)' },
      { id: 'nondirect-rollover', value: 'Nondirect Rollover (QRP, IRA, or SEP)' },
      { id: 'trustee-transfer', value: 'Trustee-to-Trustee Transfer (IRA or SEP)' },
    ],
    conditional: {
      visible: {
        field: 'rofMustCheckOne',
        value: 'qualified-funds',
        operator: 'equals',
      },
    },
  },
  rofQualifiedAmountOfFunds: {
    id: 'rof-qualified-amount-of-funds',
    questionRef: 'rofQualifiedAmountOfFunds',
    title: 'C. Amount of Funds',
    format: 'vertical',
    type: 'radio',
    required: false,
    options: [
      { id: 'all-value', value: 'Rollover/Transfer ALL of the policy/account value.' },
      {
        id: 'dollar-amount',
        value: 'Rollover/Transfer $ or % of the policy/account value.',
      },
    ],
    conditional: {
      visible: {
        field: 'rofMustCheckOne',
        value: 'qualified-funds',
        operator: 'equals',
      },
    },
  },
  rofQualifiedDollarAmount: {
    id: 'rof-qualified-dollar-amount',
    questionRef: 'rofQualifiedDollarAmount',
    title: 'Dollar Amount',
    type: 'text',
    required: false,
    placeholder: '$',
    validationType: 'currency',
    conditional: {
      visible: {
        field: 'rofQualifiedAmountOfFunds',
        value: 'dollar-amount',
        operator: 'equals',
      },
    },
  },
  rofQualifiedPercentage: {
    id: 'rof-qualified-percentage',
    questionRef: 'rofQualifiedPercentage',
    title: 'Percentage',
    type: 'number',
    required: false,
    placeholder: '%',
    conditional: {
      visible: {
        field: 'rofQualifiedAmountOfFunds',
        value: 'dollar-amount',
        operator: 'equals',
      },
    },
  },

  /* Part 4 - Other Funds */
  rofOtherFundsType: {
    id: 'rof-other-funds-type',
    questionRef: 'rofOtherFundsType',
    title: 'A. Type of Funds',
    format: 'vertical',
    type: 'radio',
    required: false,
    options: [
      {
        id: 'cd-requested',
        value:
          'Certificate of Deposit (Certificate will be requested prior to maturity) Maturity Date',
      },
      {
        id: 'mutual-fund',
        value: 'Mutual Fund (Please review prospectus - Signature Guarantee may be required)',
      },
    ],
    conditional: {
      visible: {
        field: 'rofMustCheckOne',
        value: 'other-funds',
        operator: 'equals',
      },
    },
  },
  rofOtherFundsCdMaturityDate: {
    id: 'rof-other-funds-cd-maturity-date',
    questionRef: 'rofOtherFundsCdMaturityDate',
    title: 'CD Maturity Date',
    type: 'date',
    required: false,
    conditional: {
      visible: {
        field: 'rofOtherFundsType',
        value: 'cd-requested',
        operator: 'equals',
      },
    },
  },
  rofOtherFundsAmount: {
    id: 'rof-other-funds-amount',
    questionRef: 'rofOtherFundsAmount',
    title: 'B. Amount of Funds',
    format: 'vertical',
    type: 'radio',
    required: false,
    options: [
      { id: 'all-funds', value: 'ALL of above stated funds.' },
      { id: 'specific-amount', value: '$ or % of the above stated funds.' },
    ],
    conditional: {
      visible: {
        field: 'rofMustCheckOne',
        value: 'other-funds',
        operator: 'equals',
      },
    },
  },
  rofOtherFundsDollarAmount: {
    id: 'rof-other-funds-dollar-amount',
    questionRef: 'rofOtherFundsDollarAmount',
    title: 'Dollar Amount',
    type: 'text',
    required: false,
    placeholder: '$',
    validationType: 'currency',
    conditional: {
      visible: {
        field: 'rofOtherFundsAmount',
        value: 'specific-amount',
        operator: 'equals',
      },
    },
  },
  rofOtherFundsPercentage: {
    id: 'rof-other-funds-percentage',
    questionRef: 'rofOtherFundsPercentage',
    title: 'Percentage',
    type: 'number',
    required: false,
    placeholder: '%',
    conditional: {
      visible: {
        field: 'rofOtherFundsAmount',
        value: 'specific-amount',
        operator: 'equals',
      },
    },
  },

  /* Electronic Premium Payment Authorization */

  /* Transaction Type */
  epaTransactionType: {
    id: 'epa-transaction-type',
    questionRef: 'epaTransactionType',
    title: 'PLEASE MARK THE ONE BOX THAT APPLIES',
    type: 'radio',
    required: true,
    options: [
      { id: 'new-policy', value: 'New Policy Application' },
      { id: 'lapsed-policy', value: 'Lapsed Policy Reinstatement' },
      { id: 'inforce-policy', value: 'InForce Policy' },
    ],
  },
  epaPolicyNumber: {
    id: 'epa-policy-number',
    questionRef: 'epaPolicyNumber',
    title: 'Policy Number',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        operator: 'or',
        conditions: [
          {
            field: 'epaTransactionType',
            value: 'lapsed-policy',
            operator: 'equals',
          },
          {
            field: 'epaTransactionType',
            value: 'inforce-policy',
            operator: 'equals',
          },
        ],
      },
      required: {
        operator: 'or',
        conditions: [
          {
            field: 'epaTransactionType',
            value: 'lapsed-policy',
            operator: 'equals',
          },
          {
            field: 'epaTransactionType',
            value: 'inforce-policy',
            operator: 'equals',
          },
        ],
      },
    },
  },

  /* One-Time Premium Payment */
  epaOneTimePremiumAuth: {
    id: 'epa-one-time-premium-auth',
    questionRef: 'epaOneTimePremiumAuth',
    title:
      'I authorize a one-time premium payment to be made by charging the BANK ACCOUNT listed below in the amount of',
    type: 'radio',
    required: false,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  epaOneTimePremiumAmount: {
    id: 'epa-one-time-premium-amount',
    questionRef: 'epaOneTimePremiumAmount',
    title: 'One-Time Premium Amount',
    type: 'text',
    required: false,
    placeholder: '$',
    validationType: 'currency',
    conditional: {
      visible: {
        field: 'epaOneTimePremiumAuth',
        value: 'yes',
        operator: 'contains',
      },
      required: {
        field: 'epaOneTimePremiumAuth',
        value: 'yes',
        operator: 'contains',
      },
    },
  },

  /* Recurring Premium Payments */
  recurringPremiumPayments: {
    id: 'recurring-premium-payments',
    questionRef: 'recurringPremiumPayments',
    title: 'Select Recurring Premium Payments',
    type: 'radio',
    format: 'vertical',
    required: false,
    options: [
      { id: 'add-existing-ach', value: 'Add to existing ACH draft from policy number' },
      {
        id: 'authorize-recurring-payment',
        value:
          'I authorize the deduction for recurring premium payments from the BANK ACCOUNT listed below',
      },
    ],
  },
  epaRecurringExistingPolicyNumber: {
    id: 'epa-recurring-existing-policy-number',
    questionRef: 'epaRecurringExistingPolicyNumber',
    title: 'Existing Policy Number',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'recurringPremiumPayments',
        value: 'add-existing-ach',
        operator: 'equals',
      },
    },
  },

  /* Bank Account Information */
  epaBankRoutingNumber: {
    id: 'epa-bank-routing-number',
    questionRef: 'epaBankRoutingNumber',
    title: 'Routing/Transit Number',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        operator: 'or',
        conditions: [
          {
            field: 'epaOneTimePremiumAuth',
            value: 'yes',
            operator: 'contains',
          },
          {
            field: 'recurringPremiumPayments',
            value: 'authorize-recurring-payment',
            operator: 'equals',
          },
        ],
      },
      required: {
        operator: 'or',
        conditions: [
          {
            field: 'epaOneTimePremiumAuth',
            value: 'yes',
            operator: 'contains',
          },
          {
            field: 'recurringPremiumPayments',
            value: 'authorize-recurring-payment',
            operator: 'equals',
          },
        ],
      },
    },
  },
  epaBankAccountNumber: {
    id: 'epa-bank-account-number',
    questionRef: 'epaBankAccountNumber',
    title: 'Account Number',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        operator: 'or',
        conditions: [
          {
            field: 'epaOneTimePremiumAuth',
            value: 'yes',
            operator: 'contains',
          },
          {
            field: 'recurringPremiumPayments',
            value: 'authorize-recurring-payment',
            operator: 'equals',
          },
        ],
      },
      required: {
        operator: 'or',
        conditions: [
          {
            field: 'epaOneTimePremiumAuth',
            value: 'yes',
            operator: 'contains',
          },
          {
            field: 'recurringPremiumPayments',
            value: 'authorize-recurring-payment',
            operator: 'equals',
          },
        ],
      },
    },
  },
  epaBankAccountType: {
    id: 'epa-bank-account-type',
    questionRef: 'epaBankAccountType',
    title: 'Account Type',
    type: 'radio',
    required: false,
    options: [
      { id: 'checking', value: 'Checking' },
      { id: 'savings', value: 'Savings' },
    ],
    conditional: {
      visible: {
        operator: 'or',
        conditions: [
          {
            field: 'epaOneTimePremiumAuth',
            value: 'yes',
            operator: 'contains',
          },
          {
            field: 'recurringPremiumPayments',
            value: 'authorize-recurring-payment',
            operator: 'equals',
          },
        ],
      },
      required: {
        operator: 'or',
        conditions: [
          {
            field: 'epaOneTimePremiumAuth',
            value: 'yes',
            operator: 'contains',
          },
          {
            field: 'recurringPremiumPayments',
            value: 'authorize-recurring-payment',
            operator: 'equals',
          },
        ],
      },
    },
  },
  epaBankAccountCategory: {
    id: 'epa-bank-account-category',
    questionRef: 'epaBankAccountCategory',
    title: 'Bank Account Category',
    type: 'radio',
    required: false,
    options: [
      { id: 'consumer', value: 'Consumer' },
      { id: 'business', value: 'Business' },
    ],
    conditional: {
      visible: {
        operator: 'or',
        conditions: [
          {
            field: 'epaOneTimePremiumAuth',
            value: 'yes',
            operator: 'contains',
          },
          {
            field: 'recurringPremiumPayments',
            value: 'authorize-recurring-payment',
            operator: 'equals',
          },
        ],
      },
      required: {
        operator: 'or',
        conditions: [
          {
            field: 'epaOneTimePremiumAuth',
            value: 'yes',
            operator: 'contains',
          },
          {
            field: 'recurringPremiumPayments',
            value: 'authorize-recurring-payment',
            operator: 'equals',
          },
        ],
      },
    },
  },
  epaAccountholderName: {
    id: 'epa-accountholder-name',
    questionRef: 'epaAccountholderName',
    title: 'Accountholder Name',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        operator: 'or',
        conditions: [
          {
            field: 'epaOneTimePremiumAuth',
            value: 'yes',
            operator: 'contains',
          },
          {
            field: 'recurringPremiumPayments',
            value: 'authorize-recurring-payment',
            operator: 'equals',
          },
        ],
      },
      required: {
        operator: 'or',
        conditions: [
          {
            field: 'epaOneTimePremiumAuth',
            value: 'yes',
            operator: 'contains',
          },
          {
            field: 'recurringPremiumPayments',
            value: 'authorize-recurring-payment',
            operator: 'equals',
          },
        ],
      },
    },
  },
  epaAccountholderPhone: {
    id: 'epa-accountholder-phone',
    questionRef: 'epaAccountholderPhone',
    title: 'Phone Number',
    type: 'text',
    required: false,
    validationType: 'phone',
    conditional: {
      visible: {
        operator: 'or',
        conditions: [
          {
            field: 'epaOneTimePremiumAuth',
            value: 'yes',
            operator: 'contains',
          },
          {
            field: 'recurringPremiumPayments',
            value: 'authorize-recurring-payment',
            operator: 'equals',
          },
        ],
      },
    },
  },
  epaAccountholderEmail: {
    id: 'epa-accountholder-email',
    questionRef: 'epaAccountholderEmail',
    title: 'Email',
    type: 'text',
    required: false,
    validationType: 'email',
    conditional: {
      visible: {
        operator: 'or',
        conditions: [
          {
            field: 'epaOneTimePremiumAuth',
            value: 'yes',
            operator: 'contains',
          },
          {
            field: 'recurringPremiumPayments',
            value: 'authorize-recurring-payment',
            operator: 'equals',
          },
        ],
      },
    },
  },
  epaAccountholderStreetAddress: {
    id: 'epa-accountholder-street-address',
    questionRef: 'epaAccountholderStreetAddress',
    title: 'Street Address',
    type: 'text',
    required: false,
    validationType: 'streetAddress',
    conditional: {
      visible: {
        operator: 'or',
        conditions: [
          {
            field: 'epaOneTimePremiumAuth',
            value: 'yes',
            operator: 'contains',
          },
          {
            field: 'recurringPremiumPayments',
            value: 'authorize-recurring-payment',
            operator: 'equals',
          },
        ],
      },
      required: {
        operator: 'or',
        conditions: [
          {
            field: 'epaOneTimePremiumAuth',
            value: 'yes',
            operator: 'contains',
          },
          {
            field: 'recurringPremiumPayments',
            value: 'authorize-recurring-payment',
            operator: 'equals',
          },
        ],
      },
    },
  },
  epaAccountholderCity: {
    id: 'epa-accountholder-city',
    questionRef: 'epaAccountholderCity',
    title: 'City',
    type: 'text',
    required: false,
    validationType: 'city',
    conditional: {
      visible: {
        operator: 'or',
        conditions: [
          {
            field: 'epaOneTimePremiumAuth',
            value: 'yes',
            operator: 'contains',
          },
          {
            field: 'recurringPremiumPayments',
            value: 'authorize-recurring-payment',
            operator: 'equals',
          },
        ],
      },
      required: {
        operator: 'or',
        conditions: [
          {
            field: 'epaOneTimePremiumAuth',
            value: 'yes',
            operator: 'contains',
          },
          {
            field: 'recurringPremiumPayments',
            value: 'authorize-recurring-payment',
            operator: 'equals',
          },
        ],
      },
    },
  },
  epaAccountholderState: {
    id: 'epa-accountholder-state',
    questionRef: 'epaAccountholderState',
    title: 'State',
    validationType: 'state',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        operator: 'or',
        conditions: [
          {
            field: 'epaOneTimePremiumAuth',
            value: 'yes',
            operator: 'contains',
          },
          {
            field: 'recurringPremiumPayments',
            value: 'authorize-recurring-payment',
            operator: 'equals',
          },
        ],
      },
      required: {
        operator: 'or',
        conditions: [
          {
            field: 'epaOneTimePremiumAuth',
            value: 'yes',
            operator: 'contains',
          },
          {
            field: 'recurringPremiumPayments',
            value: 'authorize-recurring-payment',
            operator: 'equals',
          },
        ],
      },
    },
  },
  epaAccountholderZipCode: {
    id: 'epa-accountholder-zip-code',
    questionRef: 'epaAccountholderZipCode',
    title: 'ZIP Code',
    validationType: 'zipcode',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        operator: 'or',
        conditions: [
          {
            field: 'epaOneTimePremiumAuth',
            value: 'yes',
            operator: 'contains',
          },
          {
            field: 'recurringPremiumPayments',
            value: 'authorize-recurring-payment',
            operator: 'equals',
          },
        ],
      },
      required: {
        operator: 'or',
        conditions: [
          {
            field: 'epaOneTimePremiumAuth',
            value: 'yes',
            operator: 'contains',
          },
          {
            field: 'recurringPremiumPayments',
            value: 'authorize-recurring-payment',
            operator: 'equals',
          },
        ],
      },
    },
  },

  /* Long-Term Care Insurance Personal Worksheet */

  /* Premium Information */
  ltcPremiumPerMonth: {
    id: 'ltc-premium-per-month',
    questionRef: 'ltcPremiumPerMonth',
    title: 'Premium Amount for the Coverage You Are Considering Will Per Month',
    type: 'text',
    required: false,
    placeholder: '$',
    validationType: 'currency',
    conditional: {
      visible: {
        operator: 'and',
        conditions: [
          {
            field: 'policyProductFundingSelection',
            operator: 'equals',
            value: 'recurring-premium',
          },
          {
            field: 'policyRecurringBillingMode',
            operator: 'equals',
            value: 'monthly-bank-draft',
          },
        ],
      },
    },
  },
  ltcPremiumPerYear: {
    id: 'ltc-premium-per-year',
    questionRef: 'ltcPremiumPerYear',
    title: 'Premium Amount for the Coverage You Are Considering Per Year',
    type: 'text',
    required: false,
    placeholder: '$',
    validationType: 'currency',
    conditional: {
      visible: {
        operator: 'and',
        conditions: [
          {
            field: 'policyProductFundingSelection',
            operator: 'equals',
            value: 'recurring-premium',
          },
          {
            field: 'policyRecurringBillingMode',
            operator: 'equals',
            value: 'annual',
          },
        ],
      },
    },
  },
  ltcPremiumOneTime: {
    id: 'ltc-premium-one-time',
    questionRef: 'ltcPremiumOneTime',
    title: 'Premium Amount for the Coverage You Are Considering One-Time',
    type: 'text',
    required: false,
    placeholder: '$',
    validationType: 'currency',
    conditional: {
      visible: {
        operator: 'or',
        conditions: [
          {
            field: 'policyProductFundingSelection',
            operator: 'equals',
            value: 'single-premium-no-return',
          },
          {
            field: 'policyProductFundingSelection',
            operator: 'equals',
            value: 'single-premium-with-return',
          },
        ],
      },
    },
  },

  /* Questions About Your Income */
  ltcHouseholdIncome: {
    id: 'ltc-household-income',
    questionRef: 'ltcHouseholdIncome',
    title: 'What is your household annual income from all sources? (check one)',
    type: 'select',
    required: false,
    options: [
      { id: 'less-10000', value: 'Less Than $10,000' },
      { id: '10000-19999', value: '$10,000-$19,999' },
      { id: '20000-29999', value: '$20,000-$29,999' },
      { id: '30000-50000', value: '$30,000-$50,000' },
      { id: 'more-50000', value: 'More Than $50,000' },
    ],
  },
  ltcIncomeChangeExpectation: {
    id: 'ltc-income-change-expectation',
    questionRef: 'ltcIncomeChangeExpectation',
    title: 'Do you expect your income to change over the next 10 years?',
    type: 'select',
    required: false,
    options: [
      { id: 'yes-increase', value: 'Yes, Expect Increase' },
      { id: 'yes-decrease', value: 'Yes, Expect Decrease' },
      { id: 'no', value: 'No' },
    ],
  },
  ltcBuyInflationProtection: {
    id: 'ltc-buy-inflation-protection',
    questionRef: 'ltcBuyInflationProtection',
    title: 'Will you buy inflation protection?',
    type: 'radio',
    required: false,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },

  /* Questions About Your Income (continued) */
  ltcPayDuringElimination: {
    id: 'ltc-pay-during-elimination',
    questionRef: 'ltcPayDuringElimination',
    title: 'How do you plan to pay for your care during the elimination period?',
    type: 'multiselect',
    required: false,
    options: [
      { id: 'from-income', value: 'From My Income' },
      { id: 'from-savings-investments', value: 'From My Savings/Investments' },
      { id: 'family-will-pay', value: 'My Family Will Pay' },
    ],
  },

  /* Questions About Your Savings and Investments */
  ltcAssetsWorth: {
    id: 'ltc-assets-worth',
    questionRef: 'ltcAssetsWorth',
    title:
      'Not counting your home, about how much are all of your assets (your savings and investments) worth? (check one)',
    type: 'select',
    required: false,
    options: [
      { id: 'less-20000', value: 'Less Than $20,000' },
      { id: '20000-29999', value: '$20,000-$29,999' },
      { id: '30000-49999', value: '$30,000-$49,999' },
      { id: 'more-50000', value: 'More Than $50,000' },
    ],
  },
  ltcAssetsChangeExpectation: {
    id: 'ltc-assets-change-expectation',
    questionRef: 'ltcAssetsChangeExpectation',
    title: 'Do you expect the value of your assets to changes over the next ten years? (check one)',
    type: 'select',
    required: false,
    options: [
      { id: 'yes-increase', value: 'Yes, Expect to Increase' },
      { id: 'yes-decrease', value: 'Yes, Expect to Decrease' },
      { id: 'no', value: 'No' },
    ],
  },

  /* Disclosure Statement */
  ltcDisclosureStatement: {
    id: 'ltc-disclosure-statement',
    questionRef: 'ltcDisclosureStatement',
    title: 'Check One',
    type: 'radio',
    required: true,
    options: [
      {
        id: 'answers-describe',
        value: 'The answers to the questions above describe my financial situation.',
      },
      { id: 'choose-not-complete', value: 'I choose not to complete this information.' },
    ],
  },
  ltcAgreeReviewed: {
    id: 'ltc-agree-reviewed',
    questionRef: 'ltcAgreeReviewed',
    title:
      'I agree that the company and/or its agent (below) has reviewed this worksheet with me including the premium and the premium increase history. I understand the information contained in this worksheet.',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  ltcAgentExplained: {
    id: 'ltc-agent-explained',
    questionRef: 'ltcAgentExplained',
    title: 'I explained to the applicant the importance of answering these questions.',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  ltcAgentPrintedName: {
    id: 'ltc-agent-printed-name',
    questionRef: 'ltcAgentPrintedName',
    title: 'Agent Printed Name',
    type: 'text',
    required: true,
  },
  personalWorksheetNotSuitable: {
    id: 'personal-worksheet-not-suitable',
    questionRef: 'personalWorksheetNotSuitable',
    title:
      'My Agent/Producer has advised me that this policy does not seem to be suitable for me. However, I still want the company to consider my application.',
    type: 'radio',
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },

  /* Temporary Insurance Agreement */

  /* Health Question Section */
  healthQuestion90Days: {
    id: 'health-question-90-days',
    questionRef: 'healthQuestion90Days',
    title:
      'Within the past 90 days, has any Proposed Insured been a patient in a hospital or other medical facility, had surgery, or been advised by a member of the medical profession to be hospitalized or have surgery?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  healthQuestionProposedInsuredName: {
    id: 'health-question-proposed-insured-name',
    questionRef: 'healthQuestionProposedInsuredName',
    title: 'Give name of Proposed Insured',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'healthQuestion90Days',
        value: 'yes',
        operator: 'equals',
      },
      required: {
        field: 'healthQuestion90Days',
        value: 'yes',
        operator: 'equals',
      },
    },
  },

  /* Premium Deposit Section */
  premiumDepositReceivedFrom: {
    id: 'premium-deposit-received-from',
    questionRef: 'premiumDepositReceivedFrom',
    title: 'Received from',
    type: 'text',
    required: false,
  },
  premiumDepositAmount: {
    id: 'premium-deposit-amount',
    questionRef: 'premiumDepositAmount',
    title: 'Sum',
    type: 'text',
    required: false,
    placeholder: '$',
    validationType: 'currency',
  },
  representativeOrAgent: {
    id: 'representative-or-agent',
    questionRef: 'representativeOrAgent',
    title: 'Representative/Agent',
    type: 'text',
    required: false,
  },
  tempInsuranceOwner: {
    id: 'temp-insurance-owner',
    questionRef: 'tempInsuranceOwner',
    title: 'Owner (If other than Proposed Insured)',
    type: 'text',
    required: false,
  },

  /* Supplement Shared Field */
  firstProposedInsuredFullName: {
    id: 'first-proposed-insured-full-name',
    questionRef: 'firstProposedInsuredFullName',
    title: 'First Proposed Insured Full Name',
    type: 'text',
    disabled: true,
    hidden: true,
    composite: {
      questionRefs: [
        'firstProposedInsuredFirstName',
        'firstProposedInsuredMiddleInitial',
        'firstProposedInsuredLastName',
      ],
      delimiter: ' ',
    },
  },
  secondProposedInsuredFullName: {
    id: 'second-proposed-insured-full-name',
    questionRef: 'secondProposedInsuredFullName',
    title: 'Second Proposed Insured Full Name',
    type: 'text',
    disabled: true,
    hidden: true,
    composite: {
      questionRefs: [
        'secondProposedInsuredFirstName',
        'secondProposedInsuredMiddleInitial',
        'secondProposedInsuredLastName',
      ],
      delimiter: ' ',
    },
  },
  signedAtCity: {
    id: 'signed-at-city',
    questionRef: 'signedAtCity',
    title: 'City',
    type: 'text',
    disabled: true,
    hidden: true,
    composite: {
      questionRefs: ['firstProposedInsuredCityStateZip'],
      delimiter: ' ',
    },
    parser: {
      type: 'parseCity',
    },
  },
  signedAtState: {
    id: 'signed-at-state',
    questionRef: 'signedAtState',
    title: 'State',
    type: 'text',
    disabled: true,
    hidden: true,
    composite: {
      questionRefs: ['firstProposedInsuredCityStateZip'],
      delimiter: ' ',
    },
    parser: {
      type: 'parseState',
    },
  },
  signedAtCityState: {
    id: 'signed-at-city-state',
    questionRef: 'signedAtCityState',
    title: 'City, State',
    type: 'text',
    disabled: true,
    hidden: true,
    composite: {
      questionRefs: ['firstProposedInsuredCityStateZip'],
      delimiter: ',',
    },
    parser: {
      type: 'parseCityState',
    },
  },

  /* Alcohol Use Supplement */

  /* Question 1 - Current Alcohol Use */
  alcoholUseBeer: {
    id: 'alcohol-use-beer',
    questionRef: 'alcoholUseBeer',
    title: 'Beer',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  alcoholUseBeerDaily: {
    id: 'alcohol-use-beer-daily',
    questionRef: 'alcoholUseBeerDaily',
    title: 'Beer - Daily',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'alcoholUseBeer',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  alcoholUseBeerWeekly: {
    id: 'alcohol-use-beer-weekly',
    questionRef: 'alcoholUseBeerWeekly',
    title: 'Beer - Weekly',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'alcoholUseBeer',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  alcoholUseBeerMonthly: {
    id: 'alcohol-use-beer-monthly',
    questionRef: 'alcoholUseBeerMonthly',
    title: 'Beer - Monthly',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'alcoholUseBeer',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  alcoholUseWine: {
    id: 'alcohol-use-wine',
    questionRef: 'alcoholUseWine',
    title: 'Wine',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  alcoholUseWineDaily: {
    id: 'alcohol-use-wine-daily',
    questionRef: 'alcoholUseWineDaily',
    title: 'Wine - Daily',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'alcoholUseWine',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  alcoholUseWineWeekly: {
    id: 'alcohol-use-wine-weekly',
    questionRef: 'alcoholUseWineWeekly',
    title: 'Wine - Weekly',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'alcoholUseWine',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  alcoholUseWineMonthly: {
    id: 'alcohol-use-wine-monthly',
    questionRef: 'alcoholUseWineMonthly',
    title: 'Wine - Monthly',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'alcoholUseWine',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  alcoholUseLiquor: {
    id: 'alcohol-use-liquor',
    questionRef: 'alcoholUseLiquor',
    title: 'Liquor',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  alcoholUseLiquorDaily: {
    id: 'alcohol-use-liquor-daily',
    questionRef: 'alcoholUseLiquorDaily',
    title: 'Liquor - Daily',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'alcoholUseLiquor',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  alcoholUseLiquorWeekly: {
    id: 'alcohol-use-liquor-weekly',
    questionRef: 'alcoholUseLiquorWeekly',
    title: 'Liquor - Weekly',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'alcoholUseLiquor',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  alcoholUseLiquorMonthly: {
    id: 'alcohol-use-liquor-monthly',
    questionRef: 'alcoholUseLiquorMonthly',
    title: 'Liquor - Monthly',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'alcoholUseLiquor',
        value: 'yes',
        operator: 'equals',
      },
    },
  },

  /* Question 2 - Last Drink */
  alcoholLastDrink: {
    id: 'alcohol-last-drink',
    questionRef: 'alcoholLastDrink',
    title: 'If you do not now use alcohol, when did you take your last drink?',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        operator: 'and',
        conditions: [
          {
            field: 'alcoholUseBeer',
            value: 'no',
            operator: 'equals',
          },
          {
            field: 'alcoholUseWine',
            value: 'no',
            operator: 'equals',
          },
          {
            field: 'alcoholUseLiquor',
            value: 'no',
            operator: 'equals',
          },
        ],
      },
    },
  },

  /* Question 3 - Treatment */
  alcoholTreatment: {
    id: 'alcohol-treatment',
    questionRef: 'alcoholTreatment',
    title: 'Are you presently taking any treatment drug to help reduce alcohol use?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },

  /* Question 4 - Drinking More Than Present */
  alcoholDrinkMore: {
    id: 'alcohol-drink-more',
    questionRef: 'alcoholDrinkMore',
    title: 'Did you ever drink substantially more than present?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },

  /* Past Drinking History Table */
  alcoholPastDrinkingHistory: {
    id: 'alcohol-past-drinking-history',
    questionRef: 'alcoholPastDrinkingHistory',
    title: 'Past Drinking History',
    type: 'table',
    required: false,
    minRows: 0,
    maxRows: 3,
    gridColumns: 3,
    columns: [
      {
        id: 'type',
        title: 'Type',
        type: 'select',
        required: false,
        options: [
          { id: 'beer', value: 'Beer' },
          { id: 'wine', value: 'Wine' },
          { id: 'liquor', value: 'Liquor' },
        ],
      },
      {
        id: 'dateFrom',
        title: 'Dates Used - From',
        type: 'date',
        required: false,
      },
      {
        id: 'dateTo',
        title: 'Dates Used - To',
        type: 'date',
        required: false,
      },
      {
        id: 'quantityDaily',
        title: 'Quantity - Daily',
        type: 'text',
        required: false,
      },
      {
        id: 'quantityWeekly',
        title: 'Quantity - Weekly',
        type: 'text',
        required: false,
      },
      {
        id: 'quantityMonthly',
        title: 'Quantity - Monthly',
        type: 'text',
        required: false,
      },
    ],
    conditional: {
      visible: {
        field: 'alcoholDrinkMore',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  alcoholChangeReason: {
    id: 'alcohol-change-reason',
    questionRef: 'alcoholChangeReason',
    title: 'Why did you change your drinking habits?',
    type: 'textarea',
    required: false,
    conditional: {
      visible: {
        field: 'alcoholDrinkMore',
        value: 'yes',
        operator: 'equals',
      },
    },
  },

  /* Question 5 - Treatment History */
  alcoholAdvisedReduce: {
    id: 'alcohol-advised-reduce',
    questionRef: 'alcoholAdvisedReduce',
    title:
      'Have you ever been advised to reduce your alcohol consumption, or consulted a doctor or received treatment because of your alcohol use?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  alcoholTreatmentDetails: {
    id: 'alcohol-treatment-details',
    questionRef: 'alcoholTreatmentDetails',
    title:
      'Indicate names and addresses of any doctor, hospital and/or treatment center, plus treatment dates',
    type: 'textarea',
    required: false,
    conditional: {
      visible: {
        field: 'alcoholAdvisedReduce',
        value: 'yes',
        operator: 'equals',
      },
      required: {
        field: 'alcoholAdvisedReduce',
        value: 'yes',
        operator: 'equals',
      },
    },
  },

  /* Question 6 - DUI/Accidents */
  alcoholDuiAccidents: {
    id: 'alcohol-dui-accidents',
    questionRef: 'alcoholDuiAccidents',
    title:
      'Have you ever been convicted of driving under the influence of alcohol or had any accident(s) where alcohol use was involved?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  alcoholDuiDetails: {
    id: 'alcohol-dui-details',
    questionRef: 'alcoholDuiDetails',
    title: 'Please provide details',
    type: 'textarea',
    required: false,
    conditional: {
      visible: {
        field: 'alcoholDuiAccidents',
        value: 'yes',
        operator: 'equals',
      },
      required: {
        field: 'alcoholDuiAccidents',
        value: 'yes',
        operator: 'equals',
      },
    },
  },

  /* Question 7 - Support Groups */
  alcoholSupportGroup: {
    id: 'alcohol-support-group',
    questionRef: 'alcoholSupportGroup',
    title:
      'Are you or have you ever been a member of Alcoholics Anonymous or any other support group to reduce alcohol use?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  alcoholSupportGroupDateAttended: {
    id: 'alcohol-support-group-date-attended',
    questionRef: 'alcoholSupportGroupDateAttended',
    title: 'Give date last attended',
    type: 'date',
    required: false,
    conditional: {
      visible: {
        field: 'alcoholSupportGroup',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  alcoholSupportGroupFrequency: {
    id: 'alcohol-support-group-frequency',
    questionRef: 'alcoholSupportGroupFrequency',
    title: 'Frequency of Attendance',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'alcoholSupportGroup',
        value: 'yes',
        operator: 'equals',
      },
    },
  },

  /* Question 8 - Relapse */
  alcoholRelapse: {
    id: 'alcohol-relapse',
    questionRef: 'alcoholRelapse',
    title: 'Have you ever had a relapse?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  alcoholRelapseDetails: {
    id: 'alcohol-relapse-details',
    questionRef: 'alcoholRelapseDetails',
    title: 'Please give details with dates',
    type: 'textarea',
    required: false,
    conditional: {
      visible: {
        field: 'alcoholRelapse',
        value: 'yes',
        operator: 'equals',
      },
      required: {
        field: 'alcoholRelapse',
        value: 'yes',
        operator: 'equals',
      },
    },
  },

  /* Question 9 - Additional Info */
  alcoholAdditionalInformation: {
    id: 'alcohol-additional-information',
    questionRef: 'alcoholAdditionalInformation',
    title: 'Please provide any additional information',
    type: 'textarea',
    required: false,
  },

  /* Certificate of Insurability and Application for Delivery */
  certDeliveryPolicyNumber: {
    id: 'cert-delivery-policy-number',
    questionRef: 'certDeliveryPolicyNumber',
    title: 'Policy Number',
    type: 'text',
    required: false,
  },
  certDeliveryName: {
    id: 'cert-delivery-name',
    questionRef: 'certDeliveryName',
    title: 'Name',
    type: 'text',
    required: true,
  },
  certDeliveryEffectiveDate: {
    id: 'cert-delivery-effective-date',
    questionRef: 'certDeliveryEffectiveDate',
    title: 'I hereby apply for Delivery of said policy which is being made effective on',
    type: 'date',
    required: true,
  },
  certDeliveryStatementsTrue: {
    id: 'cert-delivery-statements-true',
    questionRef: 'certDeliveryStatementsTrue',
    title: 'THE STATEMENTS MADE IN ITEMS 1 & 2 ABOVE ARE TRUE',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'YES' },
      { id: 'no', value: 'NO' },
    ],
  },
  certDeliveryNoResponseDetails: {
    id: 'cert-delivery-no-response-details',
    questionRef: 'certDeliveryNoResponseDetails',
    title:
      'If the answer is "NO," list below details to a "NO" response in 1. or 2. above, indicating name of insured and as applicable, the illness, injury, dates, results of treatment, names and addresses of each facility and medical care provider',
    type: 'textarea',
    required: false,
    conditional: {
      visible: {
        field: 'certDeliveryStatementsTrue',
        value: 'no',
        operator: 'equals',
      },
      required: {
        field: 'certDeliveryStatementsTrue',
        value: 'no',
        operator: 'equals',
      },
    },
  },

  /* Aviation Supplement */

  /* Question 1 - Pilot Status */
  aviationPilotStatus: {
    id: 'aviation-pilot-status',
    questionRef: 'aviationPilotStatus',
    title: 'Are you now or do you expect to be a pilot within the next two (2) years?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },

  /* Civilian Aircraft Hours Table */
  aviationCivilianHoursTable: {
    id: 'aviation-civilian-hours-table',
    questionRef: 'aviationCivilianHoursTable',
    title: 'Civilian Aircraft Hours Flown',
    type: 'table',
    required: false,
    minRows: 0,
    maxRows: 5,
    columns: [
      {
        id: 'category',
        title: 'In Civilian Aircraft',
        type: 'select',
        required: false,
        options: [
          { id: 'student', value: 'Student' },
          { id: 'instructor', value: 'Instructor' },
          { id: 'privately-owned-or-rented', value: 'Privately Owned or Rented' },
          { id: 'commercial-non-scheduled', value: 'Commercial Non-Scheduled' },
          { id: 'scheduled-airline', value: 'Scheduled Airline' },
        ],
      },
      {
        id: 'hours13to24MonthsAgo',
        title: '13-24 Months Ago',
        type: 'number',
        required: false,
      },
      {
        id: 'hoursLast12Months',
        title: 'Last 12 Months',
        type: 'number',
        required: false,
      },
      {
        id: 'hoursEstimatedNext12Months',
        title: 'Estimated Next 12 Months',
        type: 'number',
        required: false,
      },
    ],
    conditional: {
      visible: {
        field: 'aviationPilotStatus',
        value: 'yes',
        operator: 'equals',
      },
    },
  },

  hours13to24MonthsAgoTotal: {
    id: 'hours-13-24-months-ago-total',
    questionRef: 'hours13to24MonthsAgoTotal',
    title: 'Total Hours 13-24 Months Ago',
    type: 'number',
    required: false,
    conditional: {
      visible: {
        field: 'aviationPilotStatus',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  hoursLast12MonthsTotal: {
    id: 'hours-12-months-ago-total',
    questionRef: 'hoursLast12MonthsTotal',
    title: 'Total Hours Last 12 Months',
    type: 'number',
    required: false,
    conditional: {
      visible: {
        field: 'aviationPilotStatus',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  hoursEstimatedNext12MonthsTotal: {
    id: 'hours-12-months-ago-total',
    questionRef: 'hoursEstimatedNext12MonthsTotal',
    title: 'Total Hours Estimated Next 12 Months',
    type: 'number',
    required: false,
    conditional: {
      visible: {
        field: 'aviationPilotStatus',
        value: 'yes',
        operator: 'equals',
      },
    },
  },

  /* Type of Activity */
  aviationActivityTypes: {
    id: 'aviation-activity-types',
    questionRef: 'aviationActivityTypes',
    title: 'Type of Activity',
    type: 'multiselect',
    required: false,
    options: [
      { id: 'crop-dusting', value: 'Crop-Dusting' },
      { id: 'seeding', value: 'Seeding' },
      { id: 'spraying', value: 'Spraying' },
      { id: 'mapping', value: 'Mapping' },
      { id: 'civil-air-patrol', value: 'Civil Air Patrol' },
      { id: 'photography', value: 'Photography' },
      { id: 'taxi', value: 'Taxi' },
      { id: 'news-coverage', value: 'News Coverage' },
      { id: 'inspection', value: 'Inspection' },
      { id: 'hunting', value: 'Hunting' },
      { id: 'traffic-control', value: 'Traffic Control' },
      { id: 'aerobatics', value: 'Aerobatics' },
    ],
    conditional: {
      visible: {
        field: 'aviationPilotStatus',
        value: 'yes',
        operator: 'equals',
      },
    },
  },

  aviationActivityHours13to24MonthsAgo: {
    id: 'aviation-activity-hours-13-24-months-ago',
    questionRef: 'aviationActivityHours13to24MonthsAgo',
    title: 'Activity Hours 13-24 Months Ago',
    type: 'number',
    required: false,
    conditional: {
      visible: {
        field: 'aviationPilotStatus',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  aviationActivityHoursLast12Months: {
    id: 'aviation-activity-hours-12-months-ago',
    questionRef: 'aviationActivityHoursLast12Months',
    title: 'Activity Hours Last 12 Months',
    type: 'number',
    required: false,
    conditional: {
      visible: {
        field: 'aviationPilotStatus',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  aviationActivityHoursEstimatedNext12Months: {
    id: 'aviation-activity-hours-12-months-ago',
    questionRef: 'aviationActivityHoursEstimatedNext12Months',
    title: 'Activity Hours Estimated Next 12 Months',
    type: 'number',
    required: false,
    conditional: {
      visible: {
        field: 'aviationPilotStatus',
        value: 'yes',
        operator: 'equals',
      },
    },
  },

  /* Military Aircraft Hours Table */
  aviationMilitaryHoursTable: {
    id: 'aviation-military-hours-table',
    questionRef: 'aviationMilitaryHoursTable',
    title: 'Military Aircraft Hours',
    type: 'table',
    required: false,
    minRows: 1,
    maxRows: 4,
    columns: [
      {
        id: 'category',
        title: 'In Military Aircraft',
        type: 'select',
        required: false,
        options: [
          { id: 'student', value: 'Student' },
          { id: 'instructor', value: 'Instructor' },
          {
            id: 'fighter-observation-reconnaissance',
            value: 'Fighter, Observation, Reconnaissance',
          },
          { id: 'commercial-non-scheduled', value: 'Commercial Non-Scheduled' },
        ],
      },
      {
        id: 'hours13to24MonthsAgo',
        title: '13-24 Months Ago',
        type: 'number',
        required: false,
      },
      {
        id: 'hoursLast12Months',
        title: 'Last 12 Months',
        type: 'number',
        required: false,
      },
      {
        id: 'hoursEstimatedNext12Months',
        title: 'Estimated Next 12 Months',
        type: 'number',
        required: false,
      },
    ],
    conditional: {
      visible: {
        field: 'aviationPilotStatus',
        value: 'yes',
        operator: 'equals',
      },
    },
  },

  aviationMilitaryHours13to24MonthsAgo: {
    id: 'aviation-military-hours-13-24-months-ago',
    questionRef: 'aviationMilitaryHours13to24MonthsAgo',
    title: 'Activity Hours 13-24 Months Ago',
    type: 'number',
    required: false,
    conditional: {
      visible: {
        field: 'aviationPilotStatus',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  aviationMilitaryHoursLast12Months: {
    id: 'aviation-military-hours-12-months-ago',
    questionRef: 'aviationMilitaryHoursLast12Months',
    title: 'Activity Hours Last 12 Months',
    type: 'number',
    required: false,
    conditional: {
      visible: {
        field: 'aviationPilotStatus',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  aviationMilitaryHoursEstimatedNext12Months: {
    id: 'aviation-military-hours-12-months-ago',
    questionRef: 'aviationMilitaryHoursEstimatedNext12Months',
    title: 'Activity Hours Estimated Next 12 Months',
    type: 'number',
    required: false,
    conditional: {
      visible: {
        field: 'aviationPilotStatus',
        value: 'yes',
        operator: 'equals',
      },
    },
  },

  hours13to24MonthsAgoTotalAviationMilitary: {
    id: 'hours-13-24-months-ago-total-aviation-military',
    questionRef: 'hours13to24MonthsAgoTotalAviationMilitary',
    title: 'Total Hours 13-24 Months Ago',
    type: 'number',
    required: false,
    conditional: {
      visible: {
        field: 'aviationPilotStatus',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  hoursLast12MonthsTotalAviationMilitary: {
    id: 'hours-12-months-ago-total-aviation-military',
    questionRef: 'hoursLast12MonthsTotalAviationMilitary',
    title: 'Total Hours Last 12 Months',
    type: 'number',
    required: false,
    conditional: {
      visible: {
        field: 'aviationPilotStatus',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  hoursEstimatedNext12MonthsTotalAviationMilitary: {
    id: 'hours-12-months-ago-total-aviation-military',
    questionRef: 'hoursEstimatedNext12MonthsTotalAviationMilitary',
    title: 'Total Hours Estimated Next 12 Months',
    type: 'number',
    required: false,
    conditional: {
      visible: {
        field: 'aviationPilotStatus',
        value: 'yes',
        operator: 'equals',
      },
    },
  },

  /* Type of Military Aircraft */
  aviationMilitaryAircraftTypes: {
    id: 'aviation-military-aircraft-types',
    questionRef: 'aviationMilitaryAircraftTypes',
    title: 'Type of Aircraft',
    type: 'multiselect',
    required: false,
    options: [
      { id: 'bomber', value: 'Bomber' },
      { id: 'cargo', value: 'Cargo' },
      { id: 'helicopter', value: 'Helicopter' },
      { id: 'tanker', value: 'Tanker' },
      { id: 'liaison', value: 'Liaison' },
      { id: 'search', value: 'Search' },
      { id: 'rescue', value: 'Rescue' },
      { id: 'trainer', value: 'Trainer' },
      { id: 'utility', value: 'Utility' },
    ],
    conditional: {
      visible: {
        field: 'aviationPilotStatus',
        value: 'yes',
        operator: 'equals',
      },
    },
  },

  /* Question 2 - Total Hours */
  aviationTotalHoursFlown: {
    id: 'aviation-total-hours-flown',
    questionRef: 'aviationTotalHoursFlown',
    title: 'Total hours flown as a pilot',
    type: 'number',
    required: false,
    conditional: {
      visible: {
        field: 'aviationPilotStatus',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  aviationDateLastFlight: {
    id: 'aviation-date-last-flight',
    questionRef: 'aviationDateLastFlight',
    title: 'Date of last flight',
    type: 'date',
    required: false,
    conditional: {
      visible: {
        field: 'aviationPilotStatus',
        value: 'yes',
        operator: 'equals',
      },
    },
  },

  /* Question 3 - Instrument Rating */
  aviationInstrumentRating: {
    id: 'aviation-instrument-rating',
    questionRef: 'aviationInstrumentRating',
    title: 'Do you have an instrument flight rating?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
    conditional: {
      visible: {
        field: 'aviationPilotStatus',
        value: 'yes',
        operator: 'equals',
      },
    },
  },

  /* Question 4 - License/Accidents */
  aviationLicenseRevoked: {
    id: 'aviation-license-revoked',
    questionRef: 'aviationLicenseRevoked',
    title: 'Have you ever been grounded, had your license revoked or had an aviation accident?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
    conditional: {
      visible: {
        field: 'aviationPilotStatus',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  aviationLicenseRevokedExplain: {
    id: 'aviation-license-revoked-explain',
    questionRef: 'aviationLicenseRevokedExplain',
    title: 'Explain',
    type: 'textarea',
    required: false,
    conditional: {
      visible: {
        field: 'aviationLicenseRevoked',
        value: 'yes',
        operator: 'equals',
      },
      required: {
        field: 'aviationLicenseRevoked',
        value: 'yes',
        operator: 'equals',
      },
    },
  },

  /* Question 5 - Past Aircraft Types */
  aviationPastThreeYears: {
    id: 'aviation-past-three-years',
    questionRef: 'aviationPastThreeYears',
    title: 'Describe type of aircraft flown in past three years in civilian aviation',
    type: 'textarea',
    required: false,
    conditional: {
      visible: {
        field: 'aviationPilotStatus',
        value: 'yes',
        operator: 'equals',
      },
    },
  },

  /* Question 6 - Crop Dusting */
  aviationCropDustingType: {
    id: 'aviation-crop-dusting-type',
    questionRef: 'aviationCropDustingType',
    title: 'If involved with crop dusting, what type of spray plane do you fly?',
    type: 'radio',
    required: false,
    options: [
      { id: 'ag-plane', value: 'Ag Plane' },
      { id: 'conversion-aircraft', value: 'Conversion Aircraft' },
    ],
    conditional: {
      visible: {
        field: 'aviationPilotStatus',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  aviationCropDustingBase: {
    id: 'aviation-crop-dusting-base',
    questionRef: 'aviationCropDustingBase',
    title: 'Are you fixed based or not fixed based?',
    type: 'radio',
    required: false,
    options: [
      { id: 'fixed-base', value: 'Fixed Base' },
      { id: 'not-fixed-base', value: 'Not Fixed Base' },
    ],
    conditional: {
      visible: {
        field: 'aviationCropDustingType',
        operator: 'notEmpty',
      },
    },
  },
  aviationNighttimeSpraying: {
    id: 'aviation-nighttime-spraying',
    questionRef: 'aviationNighttimeSpraying',
    title: 'Do you do any nighttime spraying?',
    type: 'radio',
    required: false,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
    conditional: {
      visible: {
        field: 'aviationCropDustingType',
        operator: 'notEmpty',
      },
    },
  },
  aviationCropSprayingHours: {
    id: 'aviation-crop-spraying-hours',
    questionRef: 'aviationCropSprayingHours',
    title: 'How many hours of crop spraying experience do you have?',
    type: 'number',
    required: false,
    placeholder: 'hours',
    conditional: {
      visible: {
        field: 'aviationCropDustingType',
        operator: 'notEmpty',
      },
    },
  },

  /* Question 7 - Crew Member */
  aviationCrewMember: {
    id: 'aviation-crew-member',
    questionRef: 'aviationCrewMember',
    title: 'Are you now or do you expect to be a crew member within the next two (2) years?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
    conditional: {
      visible: {
        field: 'aviationPilotStatus',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  aviationCrewMemberDetails: {
    id: 'aviation-crew-member-details',
    questionRef: 'aviationCrewMemberDetails',
    title:
      'Explain whether civilian or military, types of planes flown in the last three years, and your duties',
    type: 'textarea',
    required: false,
    conditional: {
      visible: {
        field: 'aviationCrewMember',
        value: 'yes',
        operator: 'equals',
      },
      required: {
        field: 'aviationCrewMember',
        value: 'yes',
        operator: 'equals',
      },
    },
  },

  /* Question 8 - Coverage Preference */
  aviationCoveragePreference: {
    id: 'aviation-coverage-preference',
    questionRef: 'aviationCoveragePreference',
    title:
      'If it is necessary to modify the proposed policy because of my aviation activity, I prefer',
    type: 'radio',
    required: false,
    format: 'vertical',
    options: [
      {
        id: 'aviation-coverage',
        value: 'Aviation coverage with payment of appropriate extra premium (if any).',
      },
      {
        id: 'restricted-coverage',
        value:
          'Restricted coverage limiting amount payable if death results therefrom. (if applicable, this option not offered in all states)',
      },
    ],
    conditional: {
      visible: {
        field: 'aviationPilotStatus',
        value: 'yes',
        operator: 'equals',
      },
    },
  },

  /* Drug Use Questionnaire */
  /* Question 1 - Drug Use History */
  drugUseOpiates: {
    id: 'drug-use-opiates',
    questionRef: 'drugUseOpiates',
    title:
      'Have you ever used except as prescribed by a physician: Opiates (codeine, heroin, methadone, oxycontin, fentanyl, etc.)?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  drugUseBarbiturates: {
    id: 'drug-use-barbiturates',
    questionRef: 'drugUseBarbiturates',
    title:
      'Have you ever used except as prescribed by a physician: Barbiturates (amytal, phenobarbital, tuinal, etc.)?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  drugUseNonBarbiturates: {
    id: 'drug-use-non-barbiturates',
    questionRef: 'drugUseNonBarbiturates',
    title:
      'Have you ever used except as prescribed by a physician: Non-Barbiturates (placidyl, doriden, pareset, etc.)?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  drugUseAmphetamines: {
    id: 'drug-use-amphetamines',
    questionRef: 'drugUseAmphetamines',
    title:
      'Have you ever used except as prescribed by a physician:Amphetamines (benzedrine, dexedrine, preludin, methamphetamine, etc.)?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  drugUseAnticholinergics: {
    id: 'drug-use-anticholinergics',
    questionRef: 'drugUseAnticholinergics',
    title:
      'Have you ever used except as prescribed by a physician: Anticholinergics (belladonna), Bromines or Cocaine (including Crack)?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  drugUseHallucinogens: {
    id: 'drug-use-hallucinogens',
    questionRef: 'drugUseHallucinogens',
    title:
      'Have you ever used except as prescribed by a physician: Hallucinogens (LSD-25, peyote, psilocin, etc.)?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  drugUseCannabis: {
    id: 'drug-use-cannabis',
    questionRef: 'drugUseCannabis',
    title:
      'Have you ever used except as prescribed by a physician: Cannabis (marijuana, hashish, THC-Delta 9)?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  drugUseOtherHabitForming: {
    id: 'drug-use-other-habit-forming',
    questionRef: 'drugUseOtherHabitForming',
    title:
      'Have you ever used except as prescribed by a physician: Other habit forming drugs? (please list)',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },

  /* Drug Use Details Table */
  drugUseDetailsTable: {
    id: 'drug-use-details-table',
    questionRef: 'drugUseDetailsTable',
    title: 'Drug Use Details',
    type: 'table',
    required: false,
    minRows: 0,
    maxRows: 5,
    gridColumns: 3,
    columns: [
      {
        id: 'type',
        title: 'Type',
        type: 'text',
        required: false,
      },
      {
        id: 'frequencyUsed',
        title: 'Frequency Used',
        type: 'text',
        required: false,
      },
      {
        id: 'dosageAmountUsed',
        title: 'Dosage/Amount Used',
        type: 'text',
        required: false,
      },
      {
        id: 'datesUsedFrom',
        title: 'Dates Used - From',
        type: 'date',
        required: false,
      },
      {
        id: 'datesUsedTo',
        title: 'Dates Used - To',
        type: 'date',
        required: false,
      },
    ],
    conditional: {
      visible: {
        operator: 'or',
        conditions: [
          {
            field: 'drugUseOpiates',
            value: 'yes',
            operator: 'equals',
          },
          {
            field: 'drugUseBarbiturates',
            value: 'yes',
            operator: 'equals',
          },
          {
            field: 'drugUseNonBarbiturates',
            value: 'yes',
            operator: 'equals',
          },
          {
            field: 'drugUseAmphetamines',
            value: 'yes',
            operator: 'equals',
          },
          {
            field: 'drugUseAnticholinergics',
            value: 'yes',
            operator: 'equals',
          },
          {
            field: 'drugUseHallucinogens',
            value: 'yes',
            operator: 'equals',
          },
          {
            field: 'drugUseCannabis',
            value: 'yes',
            operator: 'equals',
          },
          {
            field: 'drugUseOtherHabitForming',
            value: 'yes',
            operator: 'equals',
          },
        ],
      },
    },
  },

  /* Question 2 - Arrests */
  drugUseArrested: {
    id: 'drug-use-arrested',
    questionRef: 'drugUseArrested',
    title: 'Were you ever arrested in connection with the use, possession, or sale of drugs?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  drugUseArrestedDetails: {
    id: 'drug-use-arrested-details',
    questionRef: 'drugUseArrestedDetails',
    title: 'Give details including date(s)',
    type: 'textarea',
    required: false,
    conditional: {
      visible: {
        field: 'drugUseArrested',
        value: 'yes',
        operator: 'equals',
      },
      required: {
        field: 'drugUseArrested',
        value: 'yes',
        operator: 'equals',
      },
    },
  },

  /* Question 3 - Assistance in Discontinuing */
  drugUseSeekAssistance: {
    id: 'drug-use-seek-assistance',
    questionRef: 'drugUseSeekAssistance',
    title: 'Did you ever seek assistance in discontinuing the use of drugs?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  drugUseAssistanceDetails: {
    id: 'drug-use-assistance-details',
    questionRef: 'drugUseAssistanceDetails',
    title: 'Please give dates, name and address of doctor or facility',
    type: 'textarea',
    required: false,
    conditional: {
      visible: {
        field: 'drugUseSeekAssistance',
        value: 'yes',
        operator: 'equals',
      },
      required: {
        field: 'drugUseSeekAssistance',
        value: 'yes',
        operator: 'equals',
      },
    },
  },

  /* Question 4 - Relapse */
  drugUseRelapse: {
    id: 'drug-use-relapse',
    questionRef: 'drugUseRelapse',
    title: 'Have you ever had a relapse?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  drugUseRelapseDetails: {
    id: 'drug-use-relapse-details',
    questionRef: 'drugUseRelapseDetails',
    title: 'Explain details including date',
    type: 'textarea',
    required: false,
    conditional: {
      visible: {
        field: 'drugUseRelapse',
        value: 'yes',
        operator: 'equals',
      },
      required: {
        field: 'drugUseRelapse',
        value: 'yes',
        operator: 'equals',
      },
    },
  },

  /* Question 5 - Support Groups */
  drugUseSupportGroups: {
    id: 'drug-use-support-groups',
    questionRef: 'drugUseSupportGroups',
    title:
      'Do you now or did you ever attend Narcotics Anonymous meetings or other support groups?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  drugUseSupportGroupsLastAttended: {
    id: 'drug-use-support-groups-last-attended',
    questionRef: 'drugUseSupportGroupsLastAttended',
    title: 'Date last attended',
    type: 'date',
    required: false,
    conditional: {
      visible: {
        field: 'drugUseSupportGroups',
        value: 'yes',
        operator: 'equals',
      },
    },
  },

  /* Question 6 - Additional Remarks */
  drugUseAdditionalRemarks: {
    id: 'drug-use-additional-remarks',
    questionRef: 'drugUseAdditionalRemarks',
    title: 'Additional Remarks',
    type: 'textarea',
    required: false,
  },

  /* Armed Forces Supplement */

  /* Section A - Military Status */

  /* Question 1 - Active Duty */
  militaryActiveDuty: {
    id: 'military-active-duty',
    questionRef: 'militaryActiveDuty',
    title:
      'Are you currently an active duty member of the military, military reserve, or National Guard or have you entered into a written agreement to become a member at a future date?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  militaryActiveBranch: {
    id: 'military-active-branch',
    questionRef: 'militaryActiveBranch',
    title: 'Branch of Service',
    type: 'multiselect',
    required: false,
    options: [
      { id: 'army', value: 'Army' },
      { id: 'navy', value: 'Navy' },
      { id: 'air-force', value: 'Air Force' },
      { id: 'marine-corps', value: 'Marine Corps' },
      { id: 'coast-guard', value: 'Coast Guard' },
    ],
    conditional: {
      visible: {
        field: 'militaryActiveDuty',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  militaryActiveBranchDetails: {
    id: 'military-active-branch-details',
    questionRef: 'militaryActiveBranchDetails',
    title: 'Branch (e.g., infantry, artillery, etc.)',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'militaryActiveDuty',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  militaryActivePayGrade: {
    id: 'military-active-pay-grade',
    questionRef: 'militaryActivePayGrade',
    title: 'Pay Grade',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'militaryActiveDuty',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  militaryActiveJobTitle: {
    id: 'military-active-job-title',
    questionRef: 'militaryActiveJobTitle',
    title: 'Job Title',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'militaryActiveDuty',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  militaryActiveJobDuties: {
    id: 'military-active-job-duties',
    questionRef: 'militaryActiveJobDuties',
    title: 'Job Duties',
    type: 'textarea',
    required: false,
    conditional: {
      visible: {
        field: 'militaryActiveDuty',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  militaryActiveLengthFrom: {
    id: 'military-active-length-from',
    questionRef: 'militaryActiveLengthFrom',
    title: 'Length of Service (Mo/Yr) from',
    type: 'text',
    required: false,
    placeholder: 'MM/YYYY',
    conditional: {
      visible: {
        field: 'militaryActiveDuty',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  militaryActiveLengthTo: {
    id: 'military-active-length-to',
    questionRef: 'militaryActiveLengthTo',
    title: 'Length of Service (Mo/Yr) to',
    type: 'text',
    required: false,
    placeholder: 'MM/YYYY',
    conditional: {
      visible: {
        field: 'militaryActiveDuty',
        value: 'yes',
        operator: 'equals',
      },
    },
  },

  /* Question 2 - Reserve/Guard Status */
  militaryReserveGuard: {
    id: 'military-reserve-guard',
    questionRef: 'militaryReserveGuard',
    title:
      'If you are not now on active duty, are you a member of the military, military reserve, or National Guard?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  militaryReserveStatus: {
    id: 'military-reserve-status',
    questionRef: 'militaryReserveStatus',
    title: 'Status',
    type: 'multiselect',
    required: false,
    options: [
      { id: 'inactive', value: 'Inactive' },
      { id: 'standby', value: 'Standby' },
    ],
    conditional: {
      visible: {
        field: 'militaryReserveGuard',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  militaryReserveBranch: {
    id: 'military-reserve-branch',
    questionRef: 'militaryReserveBranch',
    title: 'Branch',
    type: 'multiselect',
    required: false,
    options: [
      { id: 'army', value: 'Army' },
      { id: 'navy', value: 'Navy' },
      { id: 'air-force', value: 'Air Force' },
      { id: 'marine-corps', value: 'Marine Corps' },
      { id: 'coast-guard', value: 'Coast Guard' },
    ],
    conditional: {
      visible: {
        field: 'militaryReserveGuard',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  militaryReserveBranchDetails: {
    id: 'military-reserve-branch-details',
    questionRef: 'militaryReserveBranchDetails',
    title: 'Branch (e.g., infantry, artillery, etc.)',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'militaryReserveGuard',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  militaryReservePayGrade: {
    id: 'military-reserve-pay-grade',
    questionRef: 'militaryReservePayGrade',
    title: 'Pay Grade',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'militaryReserveGuard',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  militaryReserveJobTitle: {
    id: 'military-reserve-job-title',
    questionRef: 'militaryReserveJobTitle',
    title: 'Job Title',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'militaryReserveGuard',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  militaryReserveTraining: {
    id: 'military-reserve-training',
    questionRef: 'militaryReserveTraining',
    title: 'Training Program',
    type: 'multiselect',
    required: false,
    options: [
      { id: 'advanced-rotc', value: 'Advanced ROTC' },
      { id: 'service-academy', value: 'Service Academy' },
    ],
    conditional: {
      visible: {
        field: 'militaryReserveGuard',
        value: 'yes',
        operator: 'equals',
      },
    },
  },

  /* Questions 3-7 */
  militaryAlertedActive: {
    id: 'military-alerted-active',
    questionRef: 'militaryAlertedActive',
    title: 'Have you been alerted for or requested active duty within the next two (2) years?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  militaryHazardousPay: {
    id: 'military-hazardous-pay',
    questionRef: 'militaryHazardousPay',
    title:
      'Are you now receiving or do you expect to receive hazardous duty pay within the next two (2) years?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  militarySpecialTraining: {
    id: 'military-special-training',
    questionRef: 'militarySpecialTraining',
    title: 'Have you received or requested any special training or schooling?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  militaryDutyOutside: {
    id: 'military-duty-outside',
    questionRef: 'militaryDutyOutside',
    title:
      'Have you been alerted or volunteered for, have any foreknowledge of, or expect duty outside the continental United States within the next two (2) years?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  militaryAviationIntent: {
    id: 'military-aviation-intent',
    questionRef: 'militaryAviationIntent',
    title:
      'Are you now, have you ever been, or do you intend to become a pilot or crew member of an aircraft within the next two (2) years?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  militaryRemarks: {
    id: 'military-remarks',
    questionRef: 'militaryRemarks',
    title: 'Remarks',
    type: 'textarea',
    required: false,
  },

  /* Section B - Military Aviation (Conditional on Question A7) */
  militaryAviationService: {
    id: 'military-aviation-service',
    questionRef: 'militaryAviationService',
    title: 'Please check the appropriate box designating your service',
    type: 'multiselect',
    required: false,
    options: [
      { id: 'air-force', value: 'A. United States Air Force' },
      { id: 'army', value: 'B. United States Army' },
      { id: 'navy', value: 'C. United States Navy' },
      { id: 'coast-guard', value: 'D. United States Coast Guard' },
      { id: 'marine-corps', value: 'E. United States Marine Corps' },
      { id: 'national-guard', value: 'F. National Guard' },
      { id: 'reserves', value: 'G. Reserves' },
      { id: 'rotc', value: 'H. ROTC or Service Academy' },
      { id: 'civil-air-patrol', value: 'I. Civil Air Patrol' },
      { id: 'other', value: 'J. Other (Specify)' },
    ],
    conditional: {
      visible: {
        field: 'militaryAviationIntent',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  militaryAviationOtherSpecify: {
    id: 'military-aviation-other-specify',
    questionRef: 'militaryAviationOtherSpecify',
    title: 'Other (Specify)',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'militaryAviationService',
        value: 'other',
        operator: 'contains',
      },
    },
  },
  militaryAviationActivity: {
    id: 'military-aviation-activity',
    questionRef: 'militaryAviationActivity',
    title: 'Specify aviation activity and duties. If crew member, give job title.',
    type: 'textarea',
    required: false,
    conditional: {
      visible: {
        field: 'militaryAviationIntent',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  militaryAviationDutyAssignment: {
    id: 'military-aviation-duty-assignment',
    questionRef: 'militaryAviationDutyAssignment',
    title: 'Specify duty assignment (MAC, SAC, TAC, etc.)',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'militaryAviationIntent',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  militaryAviationAircraft: {
    id: 'military-aviation-aircraft',
    questionRef: 'militaryAviationAircraft',
    title: 'Specify aircraft in which duties performed (F4, B52, T28, HU-1, etc.)',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'militaryAviationIntent',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  militaryAviationActivityChange: {
    id: 'military-aviation-activity-change',
    questionRef: 'militaryAviationActivityChange',
    title:
      'Do you have any knowledge of any change in activities or anticipate such a change within the next two (2) years?',
    type: 'radio',
    required: false,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
    conditional: {
      visible: {
        field: 'militaryAviationIntent',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  militaryAviationAerobatic: {
    id: 'military-aviation-aerobatic',
    questionRef: 'militaryAviationAerobatic',
    title: 'Are you a member of an aerobatic or precision flying team?',
    type: 'radio',
    required: false,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
    conditional: {
      visible: {
        field: 'militaryAviationIntent',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  militaryAviationProficiency: {
    id: 'military-aviation-proficiency',
    questionRef: 'militaryAviationProficiency',
    title: 'Do you fly for proficiency only? Specify hours flown.',
    type: 'radio',
    required: false,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
    conditional: {
      visible: {
        field: 'militaryAviationIntent',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  militaryAviationRemarks: {
    id: 'military-aviation-remarks',
    questionRef: 'militaryAviationRemarks',
    title: 'Remarks',
    type: 'textarea',
    required: false,
    conditional: {
      visible: {
        field: 'militaryAviationIntent',
        value: 'yes',
        operator: 'equals',
      },
    },
  },

  /* Section C - Aviation Supplement Flight Hours Tables */
  militaryAviationCivilianTable: {
    id: 'military-aviation-civilian-table',
    questionRef: 'militaryAviationCivilianTable',
    title: 'CIVILIAN Flight Hours',
    type: 'table',
    required: false,
    minRows: 0,
    maxRows: 5,
    columns: [
      {
        id: 'category',
        title: 'Category',
        type: 'select',
        required: false,
        options: [
          {
            id: 'commercialAirlines',
            value: 'Commercial Airlines',
          },
          {
            id: 'companyOwnedPlanes',
            value: 'Company-owned planes',
          },
          {
            id: 'otherCommercialFlying',
            value: 'Other commercial flying',
          },
          {
            id: 'pleasureFlying',
            value: 'Pleasure flying',
          },
          {
            id: 'anyOtherFlying',
            value: 'Any other flying',
          },
        ],
      },
      {
        id: 'pilotLast12',
        title: 'Pilot - Last 12 Months',
        type: 'number',
        required: false,
      },
      {
        id: 'pilotPreceding12',
        title: 'Pilot - Preceding 12 Months',
        type: 'number',
        required: false,
      },
      {
        id: 'pilotTotalFlown',
        title: 'Pilot - Total of All Hours Flown',
        type: 'number',
        required: false,
      },
      {
        id: 'pilotEstimateNext12',
        title: 'Pilot - Estimate Next 12 Months',
        type: 'number',
        required: false,
      },
      {
        id: 'passengerLast12',
        title: 'Passenger - Last 12 Months',
        type: 'number',
        required: false,
      },
      {
        id: 'passengerPreceding12',
        title: 'Passenger - Preceding 12 Months',
        type: 'number',
        required: false,
      },
      {
        id: 'passengerTotalFlown',
        title: 'Passenger - Total of All Hours Flown',
        type: 'number',
        required: false,
      },
      {
        id: 'passengerEstimateNext12',
        title: 'Passenger - Estimate Next 12 Months',
        type: 'number',
        required: false,
      },
    ],
    conditional: {
      visible: {
        field: 'militaryAviationIntent',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  militaryAviationMilitaryTable: {
    id: 'military-aviation-military-table',
    questionRef: 'militaryAviationMilitaryTable',
    title: 'MILITARY Flight Hours',
    type: 'table',
    required: false,
    minRows: 0,
    maxRows: 8,
    columns: [
      {
        id: 'category',
        title: 'Category',
        type: 'select',
        required: false,
        options: [
          {
            id: 'singleEnginePlanes',
            value: 'Single engine planes',
          },
          {
            id: 'multiEnginePlanes',
            value: 'Multi-engine planes',
          },
          {
            id: 'transportsMATS',
            value: 'Transports (MATS)',
          },
          {
            id: 'fleetLogisticAirWing',
            value: 'Fleet Logistic Air Wing (FLOGS)',
          },
          {
            id: 'proficiency',
            value: 'Proficiency',
          },
          {
            id: 'nationalGuard',
            value: 'National Guard',
          },
          {
            id: 'reserveCorps',
            value: 'Reserve Corps',
          },
          {
            id: 'anyOtherFlying',
            value: 'Any other flying',
          },
        ],
      },
      {
        id: 'pilotLast12',
        title: 'Pilot - Last 12 Months',
        type: 'number',
        required: false,
      },
      {
        id: 'pilotPreceding12',
        title: 'Pilot - Preceding 12 Months',
        type: 'number',
        required: false,
      },
      {
        id: 'pilotTotalFlown',
        title: 'Pilot - Total of All Hours Flown',
        type: 'number',
        required: false,
      },
      {
        id: 'pilotEstimateNext12',
        title: 'Pilot - Estimate Next 12 Months',
        type: 'number',
        required: false,
      },
      {
        id: 'passengerLast12',
        title: 'Passenger - Last 12 Months',
        type: 'number',
        required: false,
      },
      {
        id: 'passengerPreceding12',
        title: 'Passenger - Preceding 12 Months',
        type: 'number',
        required: false,
      },
      {
        id: 'passengerTotalFlown',
        title: 'Passenger - Total of All Hours Flown',
        type: 'number',
        required: false,
      },
      {
        id: 'passengerEstimateNext12',
        title: 'Passenger - Estimate Next 12 Months',
        type: 'number',
        required: false,
      },
    ],
    conditional: {
      visible: {
        field: 'militaryAviationIntent',
        value: 'yes',
        operator: 'equals',
      },
    },
  },

  /* Aviation Supplement Continued */
  militaryAviationOtherFlying: {
    id: 'military-aviation-other-flying',
    questionRef: 'militaryAviationOtherFlying',
    title:
      'Details of any other flying not specifically classified above (crop dusting, mapping, surveying, testing, etc.) and type of aircraft flown (glider, helicopter, lighter-than-air, etc.)',
    type: 'textarea',
    required: false,
    conditional: {
      visible: {
        field: 'militaryAviationIntent',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  militaryAviationBranchService: {
    id: 'military-aviation-branch-service',
    questionRef: 'militaryAviationBranchService',
    title: 'If a pilot or crew member of military planes, what is your branch of service?',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'militaryAviationIntent',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  militaryAviationRank: {
    id: 'military-aviation-rank',
    questionRef: 'militaryAviationRank',
    title: 'Your rank?',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'militaryAviationIntent',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  militaryAviationOtherDuties: {
    id: 'military-aviation-other-duties',
    questionRef: 'militaryAviationOtherDuties',
    title: 'If not a full-time pilot, what other duties do you have?',
    type: 'textarea',
    required: false,
    conditional: {
      visible: {
        field: 'militaryAviationIntent',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  militaryAviationOwnPlane: {
    id: 'military-aviation-own-plane',
    questionRef: 'militaryAviationOwnPlane',
    title:
      'Do you own or have plans to own a plane or an interest therein within the next two (2) years?',
    type: 'radio',
    required: false,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
    conditional: {
      visible: {
        field: 'militaryAviationIntent',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  militaryAviationFlyPlans: {
    id: 'military-aviation-fly-plans',
    questionRef: 'militaryAviationFlyPlans',
    title: 'Do you have plans to fly within the next two (2) years?',
    type: 'radio',
    required: false,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
    conditional: {
      visible: {
        field: 'militaryAviationIntent',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  militaryAviationFlyPlansDetails: {
    id: 'military-aviation-fly-plans-details',
    questionRef: 'militaryAviationFlyPlansDetails',
    title: 'If the type flying contemplated differs from any past flying, please explain',
    type: 'textarea',
    required: false,
    conditional: {
      visible: {
        field: 'militaryAviationFlyPlans',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  militaryAviationPilotLicense: {
    id: 'military-aviation-pilot-license',
    questionRef: 'militaryAviationPilotLicense',
    title:
      "Have you within the last two (2) years held a pilot's license or certificate or student's permit for aircraft?",
    type: 'radio',
    required: false,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
    conditional: {
      visible: {
        field: 'militaryAviationIntent',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  militaryAviationLicenseType: {
    id: 'military-aviation-license-type',
    questionRef: 'militaryAviationLicenseType',
    title: 'Type of License held?',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'militaryAviationPilotLicense',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  militaryAviationLicenseIssueDate: {
    id: 'military-aviation-license-issue-date',
    questionRef: 'militaryAviationLicenseIssueDate',
    title: 'Date of Issue?',
    type: 'date',
    required: false,
    conditional: {
      visible: {
        field: 'militaryAviationPilotLicense',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  militaryAviationLicenseRenewalDate: {
    id: 'military-aviation-license-renewal-date',
    questionRef: 'militaryAviationLicenseRenewalDate',
    title: 'Date of last renewal?',
    type: 'date',
    required: false,
    conditional: {
      visible: {
        field: 'militaryAviationPilotLicense',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  militaryAviationLastFlightDate: {
    id: 'military-aviation-last-flight-date',
    questionRef: 'militaryAviationLastFlightDate',
    title: 'Date of last flight as a pilot?',
    type: 'date',
    required: false,
    conditional: {
      visible: {
        field: 'militaryAviationPilotLicense',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  militaryAviationCoveragePreference: {
    id: 'military-aviation-coverage-preference',
    questionRef: 'militaryAviationCoveragePreference',
    title: ' Should you not qualify for full coverage at Standard Rates, do you desire:',
    type: 'radio',
    format: 'vertical',
    required: false,
    options: [
      {
        id: 'a-full-coverage',
        value: '(a) a full aviation coverage with extra premium, if available?',
      },
      {
        id: 'b-restricted-coverage',
        value: '(b) restricted aviation coverage without aviation extra premium?',
      },
    ],
    conditional: {
      visible: {
        field: 'militaryAviationIntent',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  militaryAviationCoveragePreferenceA: {
    id: 'military-aviation-coverage-preference-a',
    questionRef: 'militaryAviationCoveragePreferenceA',
    title: '(a) a full aviation coverage with extra premium, if available?',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        operator: 'and',
        conditions: [
          {
            field: 'militaryAviationIntent',
            value: 'yes',
            operator: 'equals',
          },
          {
            field: 'militaryAviationCoveragePreference',
            value: 'a-full-coverage',
            operator: 'equals',
          },
        ],
      },
    },
  },
  militaryAviationCoveragePreferenceB: {
    id: 'military-aviation-coverage-preference-b',
    questionRef: 'militaryAviationCoveragePreferenceB',
    title: '(b) restricted aviation coverage without aviation extra premium?',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        operator: 'and',
        conditions: [
          {
            field: 'militaryAviationIntent',
            value: 'yes',
            operator: 'equals',
          },
          {
            field: 'militaryAviationCoveragePreference',
            value: 'b-restricted-coverage',
            operator: 'equals',
          },
        ],
      },
    },
  },
  militaryAviationAdditionalRemarks: {
    id: 'military-aviation-additional-remarks',
    questionRef: 'militaryAviationAdditionalRemarks',
    title: 'Additional remarks',
    type: 'textarea',
    required: false,
    conditional: {
      visible: {
        field: 'militaryAviationIntent',
        value: 'yes',
        operator: 'equals',
      },
    },
  },

  /* Avocation Supplement */

  /* Section 1 - Racing */
  racingEngagement: {
    id: 'racing-engagement',
    questionRef: 'racingEngagement',
    title:
      'Do you, have you ever or do you expect to engage in automobile, motorcycle, motorboat, snowmobile, or dune buggy racing within the next two (2) years?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },

  /* Racing Details Table */
  racingDetailsTable: {
    id: 'racing-details-table',
    questionRef: 'racingDetailsTable',
    title: 'Racing Details',
    type: 'table',
    required: false,
    minRows: 0,
    maxRows: 4,
    columns: [
      {
        id: 'typesOfRaces',
        title: 'Types of Races*',
        type: 'text',
        required: false,
      },
      {
        id: 'last12MonthsRaces',
        title: 'Last 12 Months - Races',
        type: 'number',
        required: false,
      },
      {
        id: 'last12MonthsMiles',
        title: 'Last 12 Months - Miles',
        type: 'number',
        required: false,
      },
      {
        id: 'oneToTwoYearsAgoRaces',
        title: '1-2 Years Ago - Races',
        type: 'number',
        required: false,
      },
      {
        id: 'oneToTwoYearsAgoMiles',
        title: '1-2 Years Ago - Miles',
        type: 'number',
        required: false,
      },
      {
        id: 'priorToTwoYearsRaces',
        title: 'Prior to 2 Years Ago - Races',
        type: 'number',
        required: false,
      },
      {
        id: 'priorToTwoYearsMiles',
        title: 'Prior to 2 Years Ago - Miles',
        type: 'number',
        required: false,
      },
      {
        id: 'next12MonthsRaces',
        title: 'Next 12 Months - Races',
        type: 'number',
        required: false,
      },
      {
        id: 'next12MonthsMiles',
        title: 'Next 12 Months - Miles',
        type: 'number',
        required: false,
      },
      {
        id: 'topSpeeds',
        title: 'Top Speeds',
        type: 'text',
        required: false,
      },
    ],
    conditional: {
      visible: {
        field: 'racingEngagement',
        value: 'yes',
        operator: 'equals',
      },
    },
  },

  /* Racing Questions b-l */
  racingOwnVehicle: {
    id: 'racing-own-vehicle',
    questionRef: 'racingOwnVehicle',
    title: 'What is your racing vehicle ownership status?',
    type: 'radio',
    required: false,
    options: [
      { id: 'own', value: 'Own' },
      { id: 'have-access', value: 'Have Access' },
    ],
    conditional: {
      visible: {
        field: 'racingEngagement',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  racingOwnVehicleConfirm: {
    id: 'racing-own-vehicle-confirm',
    questionRef: 'racingOwnVehicleConfirm',
    title: 'Do you own or have access to a competition vehicle?',
    type: 'radio',
    required: false,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
    conditional: {
      visible: {
        field: 'racingOwnVehicle',
        value: ['own', 'have-access'],
        operator: 'in',
      },
    },
  },
  racingVehicleClass: {
    id: 'racing-vehicle-class',
    questionRef: 'racingVehicleClass',
    title: 'Class',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'racingOwnVehicle',
        value: ['own', 'have-access'],
        operator: 'in',
      },
    },
  },
  racingVehicleEngine: {
    id: 'racing-vehicle-engine',
    questionRef: 'racingVehicleEngine',
    title: 'Engine Make and Model',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'racingOwnVehicle',
        value: ['own', 'have-access'],
        operator: 'in',
      },
    },
  },
  racingVehicleHorsepower: {
    id: 'racing-vehicle-horsepower',
    questionRef: 'racingVehicleHorsepower',
    title: 'HP',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'racingOwnVehicle',
        value: ['own', 'have-access'],
        operator: 'in',
      },
    },
  },

  racingDriverSchool: {
    id: 'racing-driver-school',
    questionRef: 'racingDriverSchool',
    title: "Have you ever attended any type of driver's school?",
    type: 'radio',
    required: false,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
    conditional: {
      visible: {
        field: 'racingEngagement',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  racingCompetitionLicense: {
    id: 'racing-competition-license',
    questionRef: 'racingCompetitionLicense',
    title: "Do you hold a competition driver's license from any organization?",
    type: 'radio',
    required: false,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
    conditional: {
      visible: {
        field: 'racingEngagement',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  racingParticipationLength: {
    id: 'racing-participation-length',
    questionRef: 'racingParticipationLength',
    title: 'How long have you participated in racing?',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'racingEngagement',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  racingSanctionedEvents: {
    id: 'racing-sanctioned-events',
    questionRef: 'racingSanctionedEvents',
    title: 'Do you participate in other than sanctioned events?',
    type: 'radio',
    required: false,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
    conditional: {
      visible: {
        field: 'racingEngagement',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  racingOtherPurpose: {
    id: 'racing-other-purpose',
    questionRef: 'racingOtherPurpose',
    title: 'Do you drive a competition vehicle for any other purpose?',
    type: 'radio',
    required: false,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
    conditional: {
      visible: {
        field: 'racingEngagement',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  racingStuntDriving: {
    id: 'racing-stunt-driving',
    questionRef: 'racingStuntDriving',
    title:
      'Have you ever done any stunt driving or do you have plans to do so within the next two (2) years?',
    type: 'radio',
    required: false,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
    conditional: {
      visible: {
        field: 'racingEngagement',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  racingOutsideUS: {
    id: 'racing-outside-us',
    questionRef: 'racingOutsideUS',
    title:
      'Have you ever competed, or do you have plans to compete, outside the U.S. within the next two (2) years?',
    type: 'radio',
    required: false,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
    conditional: {
      visible: {
        field: 'racingEngagement',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  racingLastRaceDate: {
    id: 'racing-last-race-date',
    questionRef: 'racingLastRaceDate',
    title: 'Date of your last race',
    type: 'date',
    required: false,
    conditional: {
      visible: {
        field: 'racingEngagement',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  racingLastRaceLocation: {
    id: 'racing-last-race-location',
    questionRef: 'racingLastRaceLocation',
    title: 'Where?',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'racingEngagement',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  racingProfessionalCash: {
    id: 'racing-professional-cash',
    questionRef: 'racingProfessionalCash',
    title:
      'Have you ever raced, or do you have plans to race, professionally or for cash prizes within the next two (2) years?',
    type: 'radio',
    required: false,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
    conditional: {
      visible: {
        field: 'racingEngagement',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  racingTrackDetails: {
    id: 'racing-track-details',
    questionRef: 'racingTrackDetails',
    title:
      'What size and type of track do you have plans to race, professionally or for cash prizes within the next two (2) years?',
    type: 'textarea',
    required: false,
    conditional: {
      visible: {
        field: 'racingProfessionalCash',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  racingYesAnswerDetails: {
    id: 'racing-yes-answer-details',
    questionRef: 'racingYesAnswerDetails',
    title: 'Give details of any "Yes" answers',
    type: 'textarea',
    required: false,
    conditional: {
      visible: {
        field: 'racingEngagement',
        value: 'yes',
        operator: 'equals',
      },
    },
  },

  /* Section 2 - Skydiving */
  skydivingEngagement: {
    id: 'skydiving-engagement',
    questionRef: 'skydivingEngagement',
    title:
      'Do you, have you ever or do you have plans to engage in skydiving within the next two (2) years?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  skydivingClubMember: {
    id: 'skydiving-club-member',
    questionRef: 'skydivingClubMember',
    title: 'Do you belong to any skydiving club or association?',
    type: 'radio',
    required: false,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
    conditional: {
      visible: {
        field: 'skydivingEngagement',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  skydivingClubName: {
    id: 'skydiving-club-name',
    questionRef: 'skydivingClubName',
    title: 'Name',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'skydivingClubMember',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  skydivingMemberPCA: {
    id: 'skydiving-member-pca',
    questionRef: 'skydivingMemberPCA',
    title: 'Member PCA?',
    type: 'radio',
    required: false,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
    conditional: {
      visible: {
        field: 'skydivingClubMember',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  skydivingTimeEngaged: {
    id: 'skydiving-time-engaged',
    questionRef: 'skydivingTimeEngaged',
    title: 'Length of time engaged in skydiving',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'skydivingEngagement',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  skydivingFormalTraining: {
    id: 'skydiving-formal-training',
    questionRef: 'skydivingFormalTraining',
    title: 'Did you receive formal training?',
    type: 'radio',
    required: false,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
    conditional: {
      visible: {
        field: 'skydivingEngagement',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  skydivingTotalJumps: {
    id: 'skydiving-total-jumps',
    questionRef: 'skydivingTotalJumps',
    title: 'Total number of jumps made',
    type: 'number',
    required: false,
    conditional: {
      visible: {
        field: 'skydivingEngagement',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  skydivingJumpsLast12Months: {
    id: 'skydiving-jumps-last-12-months',
    questionRef: 'skydivingJumpsLast12Months',
    title: 'Number of jumps in last 12 months',
    type: 'number',
    required: false,
    conditional: {
      visible: {
        field: 'skydivingEngagement',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  skydivingLastJumpDate: {
    id: 'skydiving-last-jump-date',
    questionRef: 'skydivingLastJumpDate',
    title: 'Date of last jump',
    type: 'date',
    required: false,
    conditional: {
      visible: {
        field: 'skydivingEngagement',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  skydivingJumpsNext12Months: {
    id: 'skydiving-jumps-next-12-months',
    questionRef: 'skydivingJumpsNext12Months',
    title: 'Number of jumps contemplated in next 12 months',
    type: 'number',
    required: false,
    conditional: {
      visible: {
        field: 'skydivingEngagement',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  skydivingClubSanction: {
    id: 'skydiving-club-sanction',
    questionRef: 'skydivingClubSanction',
    title: 'Are all jumps made under sanction of your club or association?',
    type: 'radio',
    required: false,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
    conditional: {
      visible: {
        field: 'skydivingEngagement',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  skydivingProfessionalJumping: {
    id: 'skydiving-professional-jumping',
    questionRef: 'skydivingProfessionalJumping',
    title:
      'Have you done professional, exhibitional or competitive jumping or do you contemplate doing so within the next two (2) years?',
    type: 'radio',
    required: false,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
    conditional: {
      visible: {
        field: 'skydivingEngagement',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  skydivingDetails: {
    id: 'skydiving-details',
    questionRef: 'skydivingDetails',
    title: 'Details',
    type: 'textarea',
    required: false,
    conditional: {
      visible: {
        operator: 'or',
        conditions: [
          {
            field: 'skydivingClubSanction',
            value: 'no',
            operator: 'equals',
          },
          {
            field: 'skydivingProfessionalJumping',
            value: 'yes',
            operator: 'equals',
          },
        ],
      },
    },
  },

  /* Section 3 - Skin or Scuba Diving */
  skinDivingEngagement: {
    id: 'skin-diving-engagement',
    questionRef: 'skinDivingEngagement',
    title:
      'Do you, have you ever or do you have plans to engage in skin diving within the next two (2) years?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  skinDivingDuration: {
    id: 'skin-diving-duration',
    questionRef: 'skinDivingDuration',
    title: 'How long have you been skin diving?',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'skinDivingEngagement',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  skinDivingLastDive: {
    id: 'skin-diving-last-dive',
    questionRef: 'skinDivingLastDive',
    title: 'Date of last dive?',
    type: 'date',
    required: false,
    conditional: {
      visible: {
        field: 'skinDivingEngagement',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  skinDivingInstructions: {
    id: 'skin-diving-instructions',
    questionRef: 'skinDivingInstructions',
    title: 'Under whose auspices did you receive your original instructions?',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'skinDivingEngagement',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  skinDivingTotalHours: {
    id: 'skin-diving-total-hours',
    questionRef: 'skinDivingTotalHours',
    title: 'Total Hours',
    type: 'number',
    required: false,
    conditional: {
      visible: {
        field: 'skinDivingEngagement',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  skinDivingFormalCourse: {
    id: 'skin-diving-formal-course',
    questionRef: 'skinDivingFormalCourse',
    title: 'Was this a formal course?',
    type: 'radio',
    required: false,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
    conditional: {
      visible: {
        field: 'skinDivingEngagement',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  skinDivingEquipment: {
    id: 'skin-diving-equipment',
    questionRef: 'skinDivingEquipment',
    title: 'Describe type of equipment used',
    type: 'textarea',
    required: false,
    conditional: {
      visible: {
        field: 'skinDivingEngagement',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  skinDivingScubaClub: {
    id: 'skin-diving-scuba-club',
    questionRef: 'skinDivingScubaClub',
    title: 'Do you belong to a scuba diving club?',
    type: 'radio',
    required: false,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
    conditional: {
      visible: {
        field: 'skinDivingEngagement',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  skinDivingClubName: {
    id: 'skin-diving-club-name',
    questionRef: 'skinDivingClubName',
    title: 'Name',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'skinDivingScubaClub',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  skinDivingWithoutBuddy: {
    id: 'skin-diving-without-buddy',
    questionRef: 'skinDivingWithoutBuddy',
    title: 'Do you ever dive without a "buddy" accompanying you?',
    type: 'radio',
    required: false,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
    conditional: {
      visible: {
        field: 'skinDivingEngagement',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  skinDivingArea: {
    id: 'skin-diving-area',
    questionRef: 'skinDivingArea',
    title: 'In what area do you do most of your diving?',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'skinDivingEngagement',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  skinDivingPurpose: {
    id: 'skin-diving-purpose',
    questionRef: 'skinDivingPurpose',
    title: 'What is the usual purpose of your diving?',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'skinDivingEngagement',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  skinDivingForPay: {
    id: 'skin-diving-for-pay',
    questionRef: 'skinDivingForPay',
    title: 'Have you done any type of diving in the past two (2) years for pay?',
    type: 'radio',
    required: false,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
    conditional: {
      visible: {
        field: 'skinDivingEngagement',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  skinDivingForPayExplain: {
    id: 'skin-diving-for-pay-explain',
    questionRef: 'skinDivingForPayExplain',
    title: 'Explain',
    type: 'textarea',
    required: false,
    conditional: {
      visible: {
        field: 'skinDivingForPay',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  skinDivingTreasureHunting: {
    id: 'skin-diving-treasure-hunting',
    questionRef: 'skinDivingTreasureHunting',
    title:
      'Have you ever hunted sunken treasure, or done salvage or rescue work, or have plans to do same within the next two (2) years?',
    type: 'radio',
    required: false,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
    conditional: {
      visible: {
        field: 'skinDivingEngagement',
        value: 'yes',
        operator: 'equals',
      },
    },
  },

  /* Diving Depths Table */
  skinDivingDepthsTable: {
    id: 'skin-diving-depths-table',
    questionRef: 'skinDivingDepthsTable',
    title: '',
    type: 'table',
    required: false,
    minRows: 0,
    maxRows: 3,
    columns: [
      {
        id: 'depthRange',
        title: 'Diving Depths',
        type: 'select',
        required: false,
        options: [
          { id: 'lessThan50', value: 'Less than 50 ft.' },
          { id: '51to80', value: '51 to 80 ft.' },
          { id: 'over85', value: 'Over 85 ft.' },
        ],
      },
      {
        id: 'oneToTwoYearsNumber',
        title: '1-2 Years Ago - Number',
        type: 'number',
        required: false,
      },
      {
        id: 'oneToTwoYearsTotalHours',
        title: '1-2 Years Ago - Total Hrs.',
        type: 'number',
        required: false,
      },
      {
        id: 'last12MonthsNumber',
        title: 'Last 12 Months - Number of Dives*',
        type: 'number',
        required: false,
      },
      {
        id: 'last12MonthsTotalHours',
        title: 'Last 12 Months - Total Hours',
        type: 'number',
        required: false,
      },
      {
        id: 'next12MonthsNumber',
        title: 'Next 12 Months (estimate) - Number of Dives',
        type: 'number',
        required: false,
      },
      {
        id: 'next12MonthsTotalHours',
        title: 'Next 12 Months (estimate) - Total Hours',
        type: 'number',
        required: false,
      },
    ],
    conditional: {
      visible: {
        field: 'skinDivingEngagement',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  skinDivingTanksUsed: {
    id: 'skin-diving-tanks-used',
    questionRef: 'skinDivingTanksUsed',
    title: 'Number of tanks used',
    type: 'number',
    required: false,
    conditional: {
      visible: {
        field: 'skinDivingEngagement',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  skinDivingCompressedAir: {
    id: 'skin-diving-compressed-air',
    questionRef: 'skinDivingCompressedAir',
    title: 'Do you use anything other than compressed air?',
    type: 'radio',
    required: false,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
    conditional: {
      visible: {
        field: 'skinDivingEngagement',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  skinDivingGreatestDepth: {
    id: 'skin-diving-greatest-depth',
    questionRef: 'skinDivingGreatestDepth',
    title: 'What is the greatest depth to which you have ever dived?',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'skinDivingEngagement',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  skinDivingAdvisedBeyond50: {
    id: 'skin-diving-advised-beyond-50',
    questionRef: 'skinDivingAdvisedBeyond50',
    title:
      'Have you, for any reason, ever been advised by either physician or instructor against diving to: Depths beyond 50 ft.?',
    type: 'radio',
    required: false,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
    conditional: {
      visible: {
        field: 'skinDivingEngagement',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  skinDivingAdvisedBeyond40: {
    id: 'skin-diving-advised-beyond-40',
    questionRef: 'skinDivingAdvisedBeyond40',
    title: 'Depths beyond 40 ft.?',
    type: 'radio',
    required: false,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
    conditional: {
      visible: {
        field: 'skinDivingEngagement',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  skinDivingAdvisedExplain: {
    id: 'skin-diving-advised-explain',
    questionRef: 'skinDivingAdvisedExplain',
    title: 'Explain',
    type: 'textarea',
    required: false,
    conditional: {
      visible: {
        operator: 'or',
        conditions: [
          {
            field: 'skinDivingAdvisedBeyond50',
            value: 'yes',
            operator: 'equals',
          },
          {
            field: 'skinDivingAdvisedBeyond40',
            value: 'yes',
            operator: 'equals',
          },
        ],
      },
    },
  },
  skinDivingRemarks: {
    id: 'skin-diving-remarks',
    questionRef: 'skinDivingRemarks',
    title: 'Remarks',
    type: 'textarea',
    required: false,
    conditional: {
      visible: {
        field: 'skinDivingEngagement',
        value: 'yes',
        operator: 'equals',
      },
    },
  },

  /* Section 4 - Rodeo Riding */
  rodeoAssociation: {
    id: 'rodeo-association',
    questionRef: 'rodeoAssociation',
    title: 'Are you associated with any rodeo circuit, club or organization?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  rodeoOrganizationName: {
    id: 'rodeo-organization-name',
    questionRef: 'rodeoOrganizationName',
    title: 'Give organization name(s)',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'rodeoAssociation',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  rodeoEventTypes: {
    id: 'rodeo-event-types',
    questionRef: 'rodeoEventTypes',
    title: 'In what type of event(s) do you participate?',
    type: 'multiselect',
    required: false,
    options: [
      { id: 'bronco-riding', value: 'Bronco Riding' },
      { id: 'steer-riding', value: 'Steer Riding' },
      { id: 'roping', value: 'Roping' },
      { id: 'bull-dogging', value: 'Bull Dogging' },
      { id: 'other', value: 'Other' },
    ],
    conditional: {
      visible: {
        field: 'rodeoAssociation',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  rodeoEventOtherSpecify: {
    id: 'rodeo-event-other-specify',
    questionRef: 'rodeoEventOtherSpecify',
    title: 'Other (specify)',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'rodeoEventTypes',
        value: 'other',
        operator: 'contains',
      },
    },
  },
  rodeoParticipationFrequency: {
    id: 'rodeo-participation-frequency',
    questionRef: 'rodeoParticipationFrequency',
    title: 'How frequently do you participate?',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'rodeoAssociation',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  rodeoLocationsLastYear: {
    id: 'rodeo-locations-last-year',
    questionRef: 'rodeoLocationsLastYear',
    title: 'Give locations where you have participated in the last year',
    type: 'textarea',
    required: false,
    conditional: {
      visible: {
        field: 'rodeoAssociation',
        value: 'yes',
        operator: 'equals',
      },
    },
  },

  /* Section 5 - Other Avocations */
  otherAvocationsEngagement: {
    id: 'other-avocations-engagement',
    questionRef: 'otherAvocationsEngagement',
    title:
      'Do you, have you ever, or do you have plans to engage in any of the following activities: spelunking, rock or mountain climbing, martial arts, combat sports, extreme sports, parachuting, ultralight, soaring, or ballooning within the next two (2) years?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  otherAvocationsType: {
    id: 'other-avocations-type',
    questionRef: 'otherAvocationsType',
    title: 'Type of activity',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'otherAvocationsEngagement',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  otherAvocationsFrequency: {
    id: 'other-avocations-frequency',
    questionRef: 'otherAvocationsFrequency',
    title: 'How often?',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'otherAvocationsEngagement',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  otherAvocationsLastTime: {
    id: 'other-avocations-last-time',
    questionRef: 'otherAvocationsLastTime',
    title: 'When was last time?',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'otherAvocationsEngagement',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  otherAvocationsRemarks: {
    id: 'other-avocations-remarks',
    questionRef: 'otherAvocationsRemarks',
    title: 'Remarks',
    type: 'textarea',
    required: false,
  },

  /* Foreign Nationals or Foreign Travel Supplement */

  /* Question 1 - U.S. Citizenship */
  foreignUSCitizen: {
    id: 'foreign-us-citizen',
    questionRef: 'foreignUSCitizen',
    title: 'Are you a U.S. citizen?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },

  /* If NOT a U.S. Citizen */
  foreignCountryCitizenship: {
    id: 'foreign-country-citizenship',
    questionRef: 'foreignCountryCitizenship',
    title: 'What is your country of citizenship?',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'foreignUSCitizen',
        value: 'no',
        operator: 'equals',
      },
      required: {
        field: 'foreignUSCitizen',
        value: 'no',
        operator: 'equals',
      },
    },
  },
  foreignAlienRegistrationNumber: {
    id: 'foreign-alien-registration-number',
    questionRef: 'foreignAlienRegistrationNumber',
    title: 'What is your Alien Registration Receipt Card (green card) number?',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'foreignUSCitizen',
        value: 'no',
        operator: 'equals',
      },
    },
  },
  foreignVisaType: {
    id: 'foreign-visa-type',
    questionRef: 'foreignVisaType',
    title: 'If no green card, what type of visa do you have?',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        operator: 'and',
        conditions: [
          {
            field: 'foreignUSCitizen',
            value: 'no',
            operator: 'equals',
          },
          {
            field: 'foreignAlienRegistrationNumber',
            operator: 'empty',
          },
        ],
      },
    },
  },
  foreignPermanentResident: {
    id: 'foreign-permanent-resident',
    questionRef: 'foreignPermanentResident',
    title: 'Are you a permanent resident of the United States?',
    type: 'radio',
    required: false,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
    conditional: {
      visible: {
        field: 'foreignUSCitizen',
        value: 'no',
        operator: 'equals',
      },
    },
  },
  foreignPermanentResidentDuration: {
    id: 'foreign-permanent-resident-duration',
    questionRef: 'foreignPermanentResidentDuration',
    title: 'For how long?',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'foreignPermanentResident',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  foreignOwnHomeUS: {
    id: 'foreign-own-home-us',
    questionRef: 'foreignOwnHomeUS',
    title: 'Do you own a home in the U.S.?',
    type: 'radio',
    required: false,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
    conditional: {
      visible: {
        field: 'foreignUSCitizen',
        value: 'no',
        operator: 'equals',
      },
    },
  },
  foreignOwnHomeUSAddress: {
    id: 'foreign-own-home-us-address',
    questionRef: 'foreignOwnHomeUSAddress',
    title: 'Show address',
    type: 'text',
    required: false,
    validationType: 'address',
    conditional: {
      visible: {
        field: 'foreignOwnHomeUS',
        value: 'yes',
        operator: 'equals',
      },
    },
  },

  foreignOwnHomeForeignCountry: {
    id: 'foreign-own-home-foreign-country',
    questionRef: 'foreignOwnHomeForeignCountry',
    title: 'Do you own or maintain a home in a foreign country?',
    type: 'radio',
    required: false,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
    conditional: {
      visible: {
        field: 'foreignUSCitizen',
        value: 'no',
        operator: 'equals',
      },
    },
  },
  foreignFamilyLiveWithYou: {
    id: 'foreign-family-live-with-you',
    questionRef: 'foreignFamilyLiveWithYou',
    title: 'If married, does your family live with you?',
    type: 'radio',
    required: false,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
    conditional: {
      visible: {
        field: 'foreignUSCitizen',
        value: 'no',
        operator: 'equals',
      },
    },
  },

  /* Question 2 - Travel Plans */
  foreignTravelPlans: {
    id: 'foreign-travel-plans',
    questionRef: 'foreignTravelPlans',
    title: 'Do you plan to travel outside of the United states within the next two (2) years?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  foreignTravelWhere: {
    id: 'foreign-travel-where',
    questionRef: 'foreignTravelWhere',
    title: 'Where?',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'foreignTravelPlans',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  foreignTravelPurpose: {
    id: 'foreign-travel-purpose',
    questionRef: 'foreignTravelPurpose',
    title: 'Purpose of travel?',
    type: 'radio',
    required: false,
    options: [
      { id: 'business', value: 'Business' },
      { id: 'pleasure', value: 'Pleasure' },
    ],
    conditional: {
      visible: {
        field: 'foreignTravelPlans',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  foreignTravelFrequency: {
    id: 'foreign-travel-frequency',
    questionRef: 'foreignTravelFrequency',
    title: 'How often?',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'foreignTravelPlans',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  foreignTravelAveragePeriod: {
    id: 'foreign-travel-average-period',
    questionRef: 'foreignTravelAveragePeriod',
    title: 'Average period of time for each trip?',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'foreignTravelPlans',
        value: 'yes',
        operator: 'equals',
      },
    },
  },

  /* Question 3 - Past Travel */
  foreignPastTravelList: {
    id: 'foreign-past-travel-list',
    questionRef: 'foreignPastTravelList',
    title:
      'List all trips outside of the United states in the past two years. Include name of countries visited and length of stay in each',
    type: 'textarea',
    required: false,
  },

  /* Question 4 - Occupational Duties Outside US */
  foreignOccupationalDuties: {
    id: 'foreign-occupational-duties',
    questionRef: 'foreignOccupationalDuties',
    title: 'List occupational duties performed outside the United States',
    type: 'textarea',
    required: false,
  },

  /* Financial Institution Client Disclosure and Acknowledgment */

  /* Client Information */
  fiDisclosureClientName: {
    id: 'fi-disclosure-client-name',
    questionRef: 'fiDisclosureClientName',
    title: 'Client(s) Name',
    type: 'text',
    required: true,
  },

  /* Company Selection */
  fiDisclosureCompanySelection: {
    id: 'fi-disclosure-company-selection',
    questionRef: 'fiDisclosureCompanySelection',
    title: 'Check all that apply',
    type: 'multiselect',
    required: true,
    options: [
      { id: 'aul', value: 'American United Life Insurance Company® (AUL)' },
      { id: 'state-life', value: 'The State Life Insurance Company' },
      { id: 'pml', value: 'Pioneer Mutual Life Insurance Company' },
      {
        id: 'golden-rule',
        value: 'Golden Rule Insurance Company (Administered by The State Life Insurance Company)',
      },
    ],
  },

  /* Bank/Credit Union Name (fill-in) */
  fiDisclosureBankCreditUnionName: {
    id: 'fi-disclosure-bank-credit-union-name',
    questionRef: 'fiDisclosureBankCreditUnionName',
    title: 'Bank/Credit Union Name',
    type: 'text',
    required: false,
    placeholder: 'Enter bank/credit union name',
  },

  /* Assignment for 1035 Exchange of Life Insurance or Annuity */

  /* Current Insurer Information */
  exchangeCurrentInsurer: {
    id: 'exchange-current-insurer',
    questionRef: 'exchangeCurrentInsurer',
    title: 'Current Insurer',
    type: 'text',
    required: true,
  },
  exchangeCurrentInsurerAddress: {
    id: 'exchange-current-insurer-address',
    questionRef: 'exchangeCurrentInsurerAddress',
    title: 'Current Insurer Address',
    type: 'text',
    required: true,
    validationType: 'address',
  },
  exchangeCurrentInsurerPhone: {
    id: 'exchange-current-insurer-phone',
    questionRef: 'exchangeCurrentInsurerPhone',
    title: 'Phone Number',
    type: 'text',
    required: true,
    validationType: 'phone',
  },
  exchangeExistingPolicyNumbers: {
    id: 'exchange-existing-policy-numbers',
    questionRef: 'exchangeExistingPolicyNumbers',
    title: 'Existing Policy Numbers',
    type: 'text',
    required: false,
  },
  exchangeInsuredAnnuitantName: {
    id: 'exchange-insured-annuitant-name',
    questionRef: 'exchangeInsuredAnnuitantName',
    title: 'Insured/Annuitant Name',
    type: 'text',
    required: true,
  },
  exchangeOwnerName: {
    id: 'exchange-owner-name',
    questionRef: 'exchangeOwnerName',
    title: 'Owner Name if other than Insured/Annuitant',
    type: 'text',
    required: false,
  },
  exchangeOwnerSSN: {
    id: 'exchange-owner-ssn',
    questionRef: 'exchangeOwnerSSN',
    title: 'Owner Social Security Number',
    type: 'text',
    required: false,
    validationType: 'ssn',
  },

  /* Company Selection */
  exchangeCompanySelection: {
    id: 'exchange-company-selection',
    questionRef: 'exchangeCompanySelection',
    title: 'Company',
    type: 'radio',
    format: 'vertical',
    required: true,
    options: [
      { id: 'aul', value: 'American United Life Insurance Company® (AUL)' },
      { id: 'pml', value: 'Pioneer Mutual Life Insurance Company (PML)' },
      { id: 'state-life', value: 'The State Life Insurance Company (State Life)' },
    ],
  },

  /* Exchange Type */
  exchangeType: {
    id: 'exchange-type',
    questionRef: 'exchangeType',
    title: 'Choose one',
    type: 'radio',
    required: true,
    format: 'vertical',
    options: [
      {
        id: 'full-exchange',
        value:
          'The sole purpose of this assignment is to effect an exchange of insurance/annuity policies under Internal Revenue Code Section 1035. The Company agrees that any surrender value received by it will be applied to a Company Policy to be owned by me on the life of the Insured/Annuitant. I understand and agree that if the Company issues, and I accept such Company Policy, The Company will surrender the Existing Policy(ies) to complete and effect a 1035 exchange.',
      },
      {
        id: 'partial-exchange',
        value:
          'The sole purpose of this assignment is to effect a partial exchange of insurance/annuity policies under Internal Revenue Code Section 1035. The Company agrees that any partial surrender values received by it will be applied to a Company Policy to be owned by me on the life of the Insured/Annuitant. I understand and agree that if the Company issues, and I accept such Company Policy, the Company will request the following surrender of the Existing Policy(ies) to complete and effect a 1035 exchange.',
      },
    ],
    disabled: false,
    composite: {
      questionRefs: ['paymentMethod'],
      delimiter: ' ',
      mapping: {
        full_1035: 'full-exchange',
        partial_1035: 'partial-exchange',
      },
    },
  },
  exchangePartialAmount: {
    id: 'exchange-partial-amount',
    questionRef: 'exchangePartialAmount',
    title: 'Please liquidate $ of the account value of the above Policy(ies).',
    type: 'text',
    required: true,
    placeholder: '$',
    validationType: 'currency',
    conditional: {
      visible: {
        field: 'exchangeType',
        value: 'partial-exchange',
        operator: 'equals',
      },
      required: {
        field: 'exchangePartialPercentage',
        operator: 'empty',
      },
    },
  },
  exchangePartialPercentage: {
    id: 'exchange-partial-percentage',
    questionRef: 'exchangePartialPercentage',
    title: 'Please liquidate % of the account value of the above Policy(ies).',
    type: 'number',
    required: true,
    placeholder: '%',
    conditional: {
      visible: {
        field: 'exchangeType',
        value: 'partial-exchange',
        operator: 'equals',
      },
      required: {
        field: 'exchangePartialAmount',
        operator: 'empty',
      },
    },
  },

  /* Policy Requirements for Full Surrenders */
  exchangeSurrenderReason: {
    id: 'exchange-surrender-reason',
    questionRef: 'exchangeSurrenderReason',
    title: 'POLICY REQUIRED FOR FULL SURRENDERS - Please check one of the following statements',
    type: 'radio',
    required: true,
    format: 'vertical',
    options: [
      {
        id: 'returning-policy',
        value:
          'I am returning the Existing Policy(ies) referenced above with this surrender request.',
      },
      {
        id: 'policy-lost',
        value: 'I certify that the Existing Policy has either been lost or destroyed.',
      },
      { id: 'partial-exchange-only', value: 'This request is for a partial exchange.' },
    ],
    composite: {
      questionRefs: ['paymentMethod'],
      delimiter: ' ',
      mapping: {
        partial_1035: 'partial-exchange-only',
      },
      conditional: {
        field: 'paymentMethod',
        operator: 'equals',
        value: 'partial_1035',
      },
    },
  },

  /* Payment Details */
  exchangePaymentCompany: {
    id: 'exchange-payment-company',
    questionRef: 'exchangePaymentCompany',
    title: 'Payment should be made to',
    type: 'radio',
    required: true,
    format: 'vertical',
    options: [
      { id: 'aul', value: 'American United Life Insurance Company® (AUL)' },
      { id: 'pml', value: 'Pioneer Mutual Life Insurance Company (PML)' },
      { id: 'state-life', value: 'The State Life Insurance Company (State Life)' },
    ],
  },

  exchangeIssuedDifferentThanApplied: {
    id: 'exchange-issued-different-than-applied',
    questionRef: 'exchangeIssuedDifferentThanApplied',
    title: 'Have you previously submitted a payment for another application that was not approved?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  authorizeTransferFundsIssuedDifferentThanApplied: {
    id: 'authorize-transfer-funds-issued-different-than-applied',
    questionRef: 'authorizeTransferFundsIssuedDifferentThanApplied',
    title:
      'Would you like to authorize us to use those funds to pay for this new policy application?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
    conditional: {
      visible: {
        field: 'exchangeIssuedDifferentThanApplied',
        value: 'yes',
        operator: 'equals',
      },
    },
  },

  /* Acceptance of §1035 Exchange - Issued different than applied */
  exchange1035ProposedPolicyholder: {
    id: 'exchange-1035-proposed-policyholder',
    questionRef: 'exchange1035ProposedPolicyholder',
    title: 'Proposed Policyholder(s)',
    type: 'text',
    required: true,
  },
  exchange1035ProposedPolicyNumber: {
    id: 'exchange-1035-proposed-policy-number',
    questionRef: 'exchange1035ProposedPolicyNumber',
    title: 'Proposed Policy Number',
    type: 'text',
    required: false,
  },
  exchange1035Company: {
    id: 'exchange-1035-company',
    questionRef: 'exchange1035Company',
    title: 'Company',
    type: 'radio',
    format: 'vertical',
    required: true,
    options: [
      { id: 'aul', value: 'American United Life Insurance Company® (AUL)' },
      { id: 'pml', value: 'Pioneer Mutual Life Insurance Company (PML)' },
      { id: 'state-life', value: 'The State Life Insurance Company (State Life)' },
    ],
  },

  /* Section 1035 Partial Transfer Request */

  /* Section 1 - Owner Information */
  partial1035OwnerName: {
    id: 'partial-1035-owner-name',
    questionRef: 'partial1035OwnerName',
    title: 'Name',
    type: 'text',
    required: true,
  },
  partial1035OwnerSSN: {
    id: 'partial-1035-owner-ssn',
    questionRef: 'partial1035OwnerSSN',
    title: 'Social Security Number',
    type: 'text',
    validationType: 'ssn',
    required: true,
  },
  partial1035OwnerAddress: {
    id: 'partial-1035-owner-address',
    questionRef: 'partial1035OwnerAddress',
    title: 'Address',
    type: 'text',
    required: true,
    validationType: 'streetAddress',
  },
  partial1035OwnerCity: {
    id: 'partial-1035-owner-city',
    questionRef: 'partial1035OwnerCity',
    title: 'City',
    type: 'text',
    required: true,
    validationType: 'city',
  },
  partial1035OwnerState: {
    id: 'partial-1035-owner-state',
    questionRef: 'partial1035OwnerState',
    title: 'State',
    type: 'text',
    required: true,
    validationType: 'state',
  },
  partial1035OwnerZipCode: {
    id: 'partial-1035-owner-zip-code',
    questionRef: 'partial1035OwnerZipCode',
    title: 'Zip Code',
    type: 'text',
    required: true,
    validationType: 'zipcode',
  },
  partial1035DaytimePhone: {
    id: 'partial-1035-daytime-phone',
    questionRef: 'partial1035DaytimePhone',
    title: 'Daytime Phone Number',
    type: 'text',
    required: true,
    validationType: 'phone',
  },
  partial1035EveningPhone: {
    id: 'partial-1035-evening-phone',
    questionRef: 'partial1035EveningPhone',
    title: 'Evening Phone Number',
    type: 'text',
    required: false,
    validationType: 'phone',
  },

  /* Section 2 - Amounts to Be Liquidated and Transferred */
  partial1035PresentInsuranceCompany: {
    id: 'partial-1035-present-insurance-company',
    questionRef: 'partial1035PresentInsuranceCompany',
    title: 'Name of Present Insurance Company',
    type: 'text',
    required: true,
  },
  partial1035TransferDetails: {
    id: 'partial-1035-transfer-details',
    questionRef: 'partial1035TransferDetails',
    title: 'Transfer Details',
    type: 'table',
    required: true,
    minRows: 1,
    maxRows: 2,
    columns: [
      {
        id: 'policy-number-1',
        title: 'Policy Number',
        type: 'text',
        required: false,
      },
      {
        id: 'amount-to-be-transferred-1',
        title: 'Amount to Be Transferred',
        type: 'number',
        required: true,
        placeholder: '$',
      },
    ],
  },
  partial1035CompanyAddress: {
    id: 'partial-1035-company-address',
    questionRef: 'partial1035CompanyAddress',
    title: 'Address',
    type: 'text',
    required: true,
    validationType: 'streetAddress',
  },
  partial1035CompanyCity: {
    id: 'partial-1035-company-city',
    questionRef: 'partial1035CompanyCity',
    title: 'City',
    type: 'text',
    required: true,
    validationType: 'city',
  },
  partial1035CompanyState: {
    id: 'partial-1035-company-state',
    questionRef: 'partial1035CompanyState',
    title: 'State',
    type: 'text',
    required: true,
    validationType: 'city',
  },
  partial1035CompanyZipCode: {
    id: 'partial-1035-company-zip-code',
    questionRef: 'partial1035CompanyZipCode',
    title: 'Zip Code',
    type: 'text',
    required: true,
    validationType: 'zipcode',
  },
  partial1035ProcessRequest: {
    id: 'partial-1035-process-request',
    questionRef: 'partial1035ProcessRequest',
    title: 'Please process this request',
    type: 'radio',
    required: true,
    options: [
      { id: 'immediately', value: 'Immediately' },
      { id: 'on-date', value: 'On the following date' },
    ],
  },
  partial1035ProcessDate: {
    id: 'partial-1035-process-date',
    questionRef: 'partial1035ProcessDate',
    title: 'Process Date',
    type: 'date',
    required: false,
    conditional: {
      visible: {
        field: 'partial1035ProcessRequest',
        value: 'on-date',
        operator: 'equals',
      },
      required: {
        field: 'partial1035ProcessRequest',
        value: 'on-date',
        operator: 'equals',
      },
    },
  },

  /* Power of Attorney Affidavit */
  poaAffidavitInitialConfirmation: {
    id: 'poa-affidavit-initial-confirmation',
    questionRef: 'poaAffidavitInitialConfirmation',
    title: 'Is there any power of attorney that is two years old or older?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  poaAffidavitCompanySelection: {
    id: 'poa-affidavit-company-selection',
    questionRef: 'poaAffidavitCompanySelection',
    title: 'Check all that apply',
    type: 'multiselect',
    required: true,
    options: [
      { id: 'aul', value: 'American United Life Insurance Company® (AUL)' },
      { id: 'state-life', value: 'The State Life Insurance Company' },
      { id: 'one-america', value: 'OneAmerica Securities, Inc.' },
      { id: 'pml', value: 'Pioneer Mutual Life Insurance Company' },
      { id: 'golden-rule', value: 'Golden Rule Insurance Company' },
    ],
    conditional: {
      visible: {
        field: 'poaAffidavitInitialConfirmation',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  poaAffidavitPolicyAccountNumber: {
    id: 'poa-affidavit-policy-account-number',
    questionRef: 'poaAffidavitPolicyAccountNumber',
    title: 'Policy/Account Number(s)',
    type: 'text',
    required: true,
    conditional: {
      visible: {
        field: 'poaAffidavitInitialConfirmation',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  poaAffidavitInsuredAccountholderName: {
    id: 'poa-affidavit-insured-accountholder-name',
    questionRef: 'poaAffidavitInsuredAccountholderName',
    title: 'Insured/Accountholder(s) Name',
    type: 'text',
    required: true,
    conditional: {
      visible: {
        field: 'poaAffidavitInitialConfirmation',
        value: 'yes',
        operator: 'equals',
      },
    },
  },

  /* Affiant Information Section */
  poaAffidavitState: {
    id: 'poa-affidavit-state',
    questionRef: 'poaAffidavitState',
    title: 'STATE OF',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'poaAffidavitInitialConfirmation',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  poaAffidavitCounty: {
    id: 'poa-affidavit-county',
    questionRef: 'poaAffidavitCounty',
    title: 'COUNTY OF',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'poaAffidavitInitialConfirmation',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  poaAffidavitAffiantName: {
    id: 'poa-affidavit-affiant-name',
    questionRef: 'poaAffidavitAffiantName',
    title: 'Affiant Name',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'poaAffidavitInitialConfirmation',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  poaAffidavitPrincipalName: {
    id: 'poa-affidavit-principal-name',
    questionRef: 'poaAffidavitPrincipalName',
    title: 'Principal Name',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'poaAffidavitInitialConfirmation',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  poaAffidavitPowerOfAttorneyDate: {
    id: 'poa-affidavit-power-of-attorney-date',
    questionRef: 'poaAffidavitPowerOfAttorneyDate',
    title: 'Power of Attorney Date',
    type: 'date',
    required: false,
    conditional: {
      visible: {
        field: 'poaAffidavitInitialConfirmation',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  poaAffidavitAffiantPrintedName: {
    id: 'poa-affidavit-affiant-printed-name',
    questionRef: 'poaAffidavitAffiantPrintedName',
    title: 'Printed Name',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'poaAffidavitInitialConfirmation',
        value: 'yes',
        operator: 'equals',
      },
    },
  },

  /* Notary Section */
  poaAffidavitNotaryInitialConfirmation: {
    id: 'poa-affidavit-notary-initial-confirmation',
    questionRef: 'poaAffidavitNotaryInitialConfirmation',
    title:
      "Do you confirm that you'll personally appear before a Notary Public and swear/affirm under oath that the above information is true?",
    type: 'radio',
    required: false,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
    conditional: {
      visible: {
        field: 'poaAffidavitInitialConfirmation',
        value: 'yes',
        operator: 'equals',
      },
    },
  },
  poaAffidavitNotaryState: {
    id: 'poa-affidavit-notary-state',
    questionRef: 'poaAffidavitNotaryState',
    title: 'STATE OF',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        operator: 'and',
        conditions: [
          {
            operator: 'and',
            conditions: [
              { field: 'poaAffidavitInitialConfirmation', operator: 'equals', value: 'yes' },
              { field: 'poaAffidavitNotaryInitialConfirmation', operator: 'equals', value: 'yes' },
            ],
          },
        ],
      },
    },
  },
  poaAffidavitNotaryCounty: {
    id: 'poa-affidavit-notary-county',
    questionRef: 'poaAffidavitNotaryCounty',
    title: 'COUNTY OF',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        operator: 'and',
        conditions: [
          {
            operator: 'and',
            conditions: [
              { field: 'poaAffidavitInitialConfirmation', operator: 'equals', value: 'yes' },
              { field: 'poaAffidavitNotaryInitialConfirmation', operator: 'equals', value: 'yes' },
            ],
          },
        ],
      },
    },
  },
  poaAffidavitNotaryAppearedName: {
    id: 'poa-affidavit-notary-appeared-name',
    questionRef: 'poaAffidavitNotaryAppearedName',
    title: 'Name of Person Appearing',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        operator: 'and',
        conditions: [
          {
            operator: 'and',
            conditions: [
              { field: 'poaAffidavitInitialConfirmation', operator: 'equals', value: 'yes' },
              { field: 'poaAffidavitNotaryInitialConfirmation', operator: 'equals', value: 'yes' },
            ],
          },
        ],
      },
    },
  },
  poaAffidavitNotaryCommissionExpires: {
    id: 'poa-affidavit-notary-commission-expires',
    questionRef: 'poaAffidavitNotaryCommissionExpires',
    title: 'My Commission Expires',
    type: 'date',
    required: false,
    conditional: {
      visible: {
        operator: 'and',
        conditions: [
          {
            operator: 'and',
            conditions: [
              { field: 'poaAffidavitInitialConfirmation', operator: 'equals', value: 'yes' },
              { field: 'poaAffidavitNotaryInitialConfirmation', operator: 'equals', value: 'yes' },
            ],
          },
        ],
      },
    },
  },
  poaAffidavitNotaryCountyOfResidence: {
    id: 'poa-affidavit-notary-county-of-residence',
    questionRef: 'poaAffidavitNotaryCountyOfResidence',
    title: 'My County of Residence',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        operator: 'and',
        conditions: [
          {
            operator: 'and',
            conditions: [
              { field: 'poaAffidavitInitialConfirmation', operator: 'equals', value: 'yes' },
              { field: 'poaAffidavitNotaryInitialConfirmation', operator: 'equals', value: 'yes' },
            ],
          },
        ],
      },
    },
  },

  /* Signature Authority of Entity */
  signatureAuthorityPolicyNumber: {
    id: 'signature-authority-policy-number',
    questionRef: 'signatureAuthorityPolicyNumber',
    title: 'Policy Number (if existing contract/policy)',
    type: 'text',
    required: false,
  },
  signatureAuthorityType: {
    id: 'signature-authority-type',
    questionRef: 'signatureAuthorityType',
    title: 'Authorization Type',
    type: 'radio',
    required: false,
    options: [
      { id: 'new', value: 'New Authorization' },
      { id: 'update', value: 'Update Existing Authorization' },
    ],
  },
  signatureAuthorityEntityName: {
    id: 'signature-authority-entity-name',
    questionRef: 'signatureAuthorityEntityName',
    title: 'Full Name of the Entity',
    type: 'text',
    required: false,
  },
  signatureAuthorityEntityType: {
    id: 'signature-authority-entity-type',
    questionRef: 'signatureAuthorityEntityType',
    title: 'Type of Entity',
    type: 'radio',
    required: false,
    options: [
      { id: 'corporation', value: 'Corporation' },
      { id: 'partnership', value: 'Partnership' },
      { id: 'llc', value: 'Limited Liability Company' },
    ],
  },
  signatureAuthorityTaxId: {
    id: 'signature-authority-tax-id',
    questionRef: 'signatureAuthorityTaxId',
    title: 'Tax Identification Number for the Entity',
    type: 'text',
    required: false,
  },

  /* Authorizing Resolution Section */
  signatureAuthoritySecretaryName: {
    id: 'signature-authority-secretary-name',
    questionRef: 'signatureAuthoritySecretaryName',
    title: 'Secretary/Managing Member Name',
    type: 'text',
    required: false,
    placeholder: "(secretary/managing member's name)",
  },
  signatureAuthorityCompanyName: {
    id: 'signature-authority-company-name',
    questionRef: 'signatureAuthorityCompanyName',
    title: 'Company Name',
    type: 'text',
    required: false,
    placeholder: '(company name)',
  },
  signatureAuthorityState: {
    id: 'signature-authority-state',
    questionRef: 'signatureAuthorityState',
    title: 'State',
    type: 'text',
    required: false,
    placeholder: '(state)',
  },
  signatureAuthorityMeetingDate: {
    id: 'signature-authority-meeting-date',
    questionRef: 'signatureAuthorityMeetingDate',
    title: 'Meeting Date',
    type: 'date',
    required: false,
    placeholder: '(date)',
  },

  /* Authorized Individuals */
  signatureAuthorityAuthorizedIndividuals: {
    id: 'signature-authority-authorized-individuals',
    questionRef: 'signatureAuthorityAuthorizedIndividuals',
    title: 'Authorized Individuals',
    type: 'table',
    required: false,
    minRows: 1,
    maxRows: 3,
    gridColumns: 1,
    columns: [
      {
        id: 'printed-name',
        title: 'Authorized Individual Printed Name',
        type: 'text',
        required: false,
      },
    ],
  },

  // W-4P Withholding Certificate for Periodic Pension or Annuity Payments
  // step 1
  w4pPersonalInfoFirstName: {
    id: 'w4p-personal-info-first-name',
    questionRef: 'w4pPersonalInfoFirstName',
    title: 'First name and middle initial',
    type: 'text',
    required: true,
  },

  w4pPersonalInfoLastName: {
    id: 'w4p-personal-info-last-name',
    questionRef: 'w4pPersonalInfoLastName',
    title: 'Last name',
    type: 'text',
    required: true,
  },

  w4pPersonalInfoSSN: {
    id: 'w4p-personal-info-ssn',
    questionRef: 'w4pPersonalInfoSSN',
    title: 'Social Security Number',
    type: 'text',
    required: true,
    validationType: 'ssn',
  },

  w4pPersonalInfoAddress: {
    id: 'w4p-personal-info-address',
    questionRef: 'w4pPersonalInfoAddress',
    title: 'Address',
    type: 'text',
    required: true,
    validationType: 'address',
  },

  w4pPersonalInfoCityStateZip: {
    id: 'w4p-personal-info-city-state-zip',
    questionRef: 'w4pPersonalInfoCityStateZip',
    title: 'City or town, state, and ZIP code',
    type: 'text',
    required: true,
    validationType: 'address',
  },

  w4pPersonalInfoFilingStatus: {
    id: 'w4p-personal-info-filing-status',
    questionRef: 'w4pPersonalInfoFilingStatus',
    title: 'Filing Status',
    type: 'radio',
    format: 'vertical',
    required: true,
    options: [
      { id: 'single-or-married-separately', value: 'Single or Married filing separately' },
      {
        id: 'married-jointly-or-surviving-spouse',
        value: 'Married filing jointly or Qualifying surviving spouse',
      },
      {
        id: 'head-of-household',
        value:
          'Head of household (unmarried and paying more than half the costs of keeping up a home for yourself and a qualifying individual)',
      },
    ],
  },

  // step 2
  w4pAnnualIncomeJobs: {
    id: 'w4p-annual-income-jobs',
    questionRef: 'w4pAnnualIncomeJobs',
    title:
      'If you (and/or your spouse) have one or more jobs, then enter the total taxable annual pay from all jobs, plus any income entered on Form W-4, Step 4(a), for the jobs less the deductions entered on Form W-4, Step 4(b), for the jobs. Otherwise, enter “-0-”',
    type: 'text',
    required: false,
    validationType: 'currency',
  },

  w4pAnnualIncomeOtherPensions: {
    id: 'w4p-annual-income-other-pensions',
    questionRef: 'w4pAnnualIncomeOtherPensions',
    title:
      'If you (and/or your spouse) have any other pensions/annuities that pay less annually than this pension/annuity, then enter the total annual taxable payments from all lower-paying pensions/annuities. Otherwise, enter “-0-”',
    type: 'text',
    required: false,
    validationType: 'currency',
  },

  w4pStep3DependentsUnder17: {
    id: 'w4p-step3-dependents-under17',
    questionRef: 'w4pStep3DependentsUnder17',
    title: 'Add the amounts from items (i) and (ii) and enter the total here',
    type: 'text',
    required: false,
    validationType: 'currency',
  },

  // step 3
  w4pStep3OtherChildren: {
    id: 'w4p-step3-other-children',
    questionRef: 'w4pStep3OtherChildren',
    title: 'Multiply the number of qualifying children under age 17 by $2,000',
    type: 'text',
    required: false,
    validationType: 'currency',
  },

  w4pStep3OtherDependents: {
    id: 'w4p-step3-other-dependents',
    questionRef: 'w4pStep3OtherDependents',
    title: 'Multiply the number of other dependents by $500',
    type: 'text',
    required: false,
    validationType: 'currency',
  },

  w4pStep3OtherCredits: {
    id: 'w4p-step3-other-credits',
    questionRef: 'w4pStep3OtherCredits',
    title: 'Add other credits, such as foreign tax credit and education tax credits',
    type: 'text',
    required: false,
    validationType: 'currency',
  },

  w4pStep3TotalCredits: {
    id: 'w4p-step3-total-credits',
    questionRef: 'w4pStep3TotalCredits',
    title:
      'Add the amounts for qualifying children, other dependents, and other credits and enter the total here',
    type: 'text',
    required: false,
    validationType: 'currency',
  },

  // step 4
  w4pStep4OtherIncome: {
    id: 'w4p-step4-other-income',
    questionRef: 'w4pStep4OtherIncome',
    title:
      "Other income (not from jobs or pension/annuity payments). If you want tax withheld on other income you expect this year that won't have withholding, enter the amount of other income here. This may include interest, taxable social security, and dividends",
    type: 'text',
    required: false,
    validationType: 'currency',
  },

  w4pStep4Deductions: {
    id: 'w4p-step4-deductions',
    questionRef: 'w4pStep4Deductions',
    title:
      'Deductions. If you expect to claim deductions other than the basic standard deduction and want to reduce your withholding, use the Deductions Worksheet on page 3 and enter the result here',
    type: 'text',
    required: false,
    validationType: 'currency',
  },

  w4pStep4ExtraWithholding: {
    id: 'w4p-step4-extra-withholding',
    questionRef: 'w4pStep4ExtraWithholding',
    title: 'Extra withholding. Enter any additional tax you want withheld from each payment',
    type: 'text',
    required: false,
    validationType: 'currency',
  },

  // step 4(b)
  deductionsWorksheetLine1Estimate: {
    id: 'deductions-worksheet-line1-estimate',
    questionRef: 'deductionsWorksheetLine1Estimate',
    title:
      'Enter an estimate of your 2025 itemized deductions (from Schedule A (Form 1040)). Such deductions may include qualifying home mortgage interest, charitable contributions, state and local taxes (up to $10,000), and medical expenses in excess of 7.5% of your income',
    type: 'text',
    required: false,
    validationType: 'currency',
  },

  deductionsWorksheetLine2StandardDeduction: {
    id: 'deductions-worksheet-line2-standard-deduction',
    questionRef: 'deductionsWorksheetLine2StandardDeduction',
    title: `Enter:
- $30,000 if you're married filing jointly or a qualifying surviving spouse
- $22,500 if you're head of household
- $15,000 if you're single or married filing separately`,
    type: 'text',
    required: false,
    validationType: 'currency',
  },

  deductionsWorksheetLine3Difference: {
    id: 'deductions-worksheet-line3-difference',
    questionRef: 'deductionsWorksheetLine3Difference',
    title:
      'If line 1 is greater than line 2, subtract line 2 from line 1 and enter the result here. If line 2 is greater than line 1, enter “-0-”',
    type: 'text',
    required: false,
    validationType: 'currency',
  },

  deductionsWorksheetLine4AgeAdjustment: {
    id: 'deductions-worksheet-line4-age-adjustment',
    questionRef: 'deductionsWorksheetLine4AgeAdjustment',
    title: `If line 3 equals zero, and you (or your spouse) are 65 or older, enter:
- $2,000 if you're single or head of household.
- $1,600 if you're married filing separately.
- $1,600 if you're a qualifying surviving spouse or you're married filing jointly and one of you is under age 65.
- $3,200 if you're married filing jointly and both of you are age 65 or older.
Otherwise, enter “-0-”. See Pub. 505 for more information`,
    type: 'text',
    required: false,
    validationType: 'currency',
  },

  deductionsWorksheetLine5Adjustments: {
    id: 'deductions-worksheet-line5-adjustments',
    questionRef: 'deductionsWorksheetLine5Adjustments',
    title:
      'Enter an estimate of your student loan interest, deductible IRA contributions, and certain other adjustments (from Part II of Schedule 1 (Form 1040)). See Pub. 505 for more information',
    type: 'text',
    required: false,
    validationType: 'currency',
  },

  deductionsWorksheetLine6Total: {
    id: 'deductions-worksheet-line6-total',
    questionRef: 'deductionsWorksheetLine6Total',
    title: 'Add lines 3 through 5. Enter the result here and in Step 4(b) on Form W-4P',
    type: 'text',
    required: false,
    validationType: 'currency',
  },

  // Pre-interview Worksheet
  doctorsVisited: {
    id: 'doctors-visited',
    questionRef: 'doctorsVisited',
    title: 'Doctors you have visited in the last five years, including primary care physician',
    type: 'table',
    required: true,
    minRows: 1,
    maxRows: 2,
    columns: [
      {
        id: 'doctor-name',
        title: 'Name',
        type: 'text',
        required: true,
      },
      {
        id: 'doctor-specialization',
        title: 'Specialization',
        type: 'text',
        required: true,
      },
      {
        id: 'doctor-phone-number',
        title: 'Phone Number',
        type: 'text',
        required: true,
      },
      {
        id: 'doctor-address',
        title: 'Address',
        type: 'text',
        required: true,
      },
      {
        id: 'doctor-date-of-visit',
        title: 'Date of Visit',
        type: 'date',
        required: true,
      },
      {
        id: 'doctor-reason-for-visit',
        title: 'Reason for Visit',
        type: 'text',
        required: true,
      },
      {
        id: 'doctor-treatment-or-test',
        title: 'Treatment or Test Received',
        type: 'text',
        required: true,
      },
    ],
  },

  prescriptionMedications: {
    id: 'prescription-medications',
    questionRef: 'prescriptionMedications',
    title: 'Prescription and over-the-counter medications',
    type: 'table',
    required: true,
    minRows: 0,
    maxRows: 4,
    columns: [
      {
        id: 'medication-name',
        title: 'Name',
        type: 'text',
        required: true,
      },
      {
        id: 'medication-dosage',
        title: 'Dosage',
        type: 'text',
        required: true,
      },
      {
        id: 'medication-reason',
        title: 'Reason for Taking',
        type: 'text',
        required: true,
      },
      {
        id: 'medication-length-taken',
        title: 'Length Taken',
        type: 'text',
        required: true,
      },
      {
        id: 'medication-date-last-taken',
        title: 'Date Last Taken',
        type: 'date',
        required: true,
      },
    ],
  },

  medicalHistory: {
    id: 'medical-history',
    questionRef: 'medicalHistory',
    title: 'Medical History',
    helperText:
      'All diagnoses including but not limited to coronary conditions, cancers, diabetes, heart disease, retinopathy, hypertension, neuropathy, kidney disease, insulin reaction, urine protein/microalbumin, cerebrovascular/peripheral vascular disease.',
    type: 'table',
    required: true,
    minRows: 0,
    maxRows: 4,
    columns: [
      {
        id: 'medical-condition',
        title: 'Condition',
        type: 'text',
        required: true,
      },
      {
        id: 'medical-date-of-onset',
        title: 'Date of Onset',
        type: 'date',
        required: true,
      },
      {
        id: 'medical-last-date-of-treatment',
        title: 'Last Date of Treatment',
        type: 'date',
        required: true,
      },
      {
        id: 'medical-exam-results',
        title: 'Exam or Test Results',
        type: 'text',
        required: true,
      },
      {
        id: 'medical-md-treating',
        title: 'MD Treating Condition',
        type: 'text',
        required: true,
      },
    ],
  },
};
